export type Audio = {
  /**
   * Type of media (always 'audio') Default value: `"audio"`
   */
  media_type?: string;
  /**
   * URL where the media file can be accessed
   */
  url: string;
  /**
   * MIME type of the media file
   */
  content_type: string;
  /**
   * Original filename of the media
   */
  file_name: string;
  /**
   * Size of the file in bytes
   */
  file_size: number;
  /**
   * Duration of the media in seconds
   */
  duration: number;
  /**
   * Overall bitrate of the media in bits per second
   */
  bitrate: number;
  /**
   * Codec used to encode the media
   */
  codec: string;
  /**
   * Container format of the media file (e.g., 'mp4', 'mov')
   */
  container: string;
  /**
   * Number of audio channels
   */
  channels: number;
  /**
   * Audio sample rate in Hz
   */
  sample_rate: number;
};
export type AudioFile = {
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The duration of the audio file in seconds.
   */
  duration: number;
};
export type AudioSetting = {
  /**
   * Sample rate of generated audio Default value: `"32000"`
   */
  sample_rate?: "8000" | "16000" | "22050" | "24000" | "32000" | "44100";
  /**
   * Bitrate of generated audio Default value: `"128000"`
   */
  bitrate?: "32000" | "64000" | "128000" | "256000";
  /**
   * Audio format Default value: `"mp3"`
   */
  format?: "mp3" | "pcm" | "flac";
  /**
   * Number of audio channels (1=mono, 2=stereo) Default value: `"1"`
   */
  channel?: "1" | "2";
};
export type AudioTrack = {
  /**
   * Audio codec used (e.g., 'aac', 'mp3')
   */
  codec: string;
  /**
   * Number of audio channels
   */
  channels: number;
  /**
   * Audio sample rate in Hz
   */
  sample_rate: number;
  /**
   * Audio bitrate in bits per second
   */
  bitrate: number;
};
export type BBoxPromptBase = {
  /**
   * X Min Coordinate of the box (0-1)
   */
  x_min?: number;
  /**
   * Y Min Coordinate of the box (0-1)
   */
  y_min?: number;
  /**
   * X Max Coordinate of the prompt (0-1)
   */
  x_max?: number;
  /**
   * Y Max Coordinate of the prompt (0-1)
   */
  y_max?: number;
};
export type BoxPrompt = {
  /**
   * X Min Coordinate of the box
   */
  x_min?: number;
  /**
   * Y Min Coordinate of the box
   */
  y_min?: number;
  /**
   * X Max Coordinate of the prompt
   */
  x_max?: number;
  /**
   * Y Max Coordinate of the prompt
   */
  y_max?: number;
  /**
   * The frame index to interact with.
   */
  frame_index?: number;
};
export type BoxPromptBase = {
  /**
   * X Min Coordinate of the box
   */
  x_min?: number;
  /**
   * Y Min Coordinate of the box
   */
  y_min?: number;
  /**
   * X Max Coordinate of the prompt
   */
  x_max?: number;
  /**
   * Y Max Coordinate of the prompt
   */
  y_max?: number;
};
export type CameraControl = {
  /**
   * The type of camera movement
   */
  movement_type: "horizontal" | "vertical" | "pan" | "tilt" | "roll" | "zoom";
  /**
   * The value of the camera movement
   */
  movement_value: number;
};
export type ColorPalette = {
  /**
   * A list of color palette members that define the color palette
   */
  members?: Array<ColorPaletteMember>;
  /**
   * A color palette preset value
   */
  name?:
    | "EMBER"
    | "FRESH"
    | "JUNGLE"
    | "MAGIC"
    | "MELON"
    | "MOSAIC"
    | "PASTEL"
    | "ULTRAMARINE";
};
export type ColorPaletteMember = {
  /**
   * RGB color value for the palette member
   */
  rgb: RGBColor;
  /**
   * The weight of the color in the color palette Default value: `0.5`
   */
  color_weight?: number;
};
export type ControlLoraWeight = {
  /**
   * URL or the path to the LoRA weights.
   */
  path: string;
  /**
   * The scale of the LoRA weight. This is used to scale the LoRA weight
   * before merging it with the base model. Providing a dictionary as {"layer_name":layer_scale} allows per-layer lora scale settings. Layers with no scale provided will have scale 1.0. Default value: `1`
   */
  scale?: any | number;
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * Type of preprocessing to apply to the input image. Default value: `"None"`
   */
  preprocess?: "canny" | "depth" | "None";
};
export type ControlNet = {
  /**
   * URL or the path to the control net weights.
   */
  path: string;
  /**
   * optional URL to the controlnet config.json file.
   */
  config_url?: string | Blob | File;
  /**
   * The optional variant if a Hugging Face repo key is used.
   */
  variant?: string;
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * The scale of the control net weight. This is used to scale the control net weight
   * before merging it with the base model. Default value: `1`
   */
  conditioning_scale?: number;
  /**
   * The percentage of the image to start applying the controlnet in terms of the total timesteps.
   */
  start_percentage?: number;
  /**
   * The percentage of the image to end applying the controlnet in terms of the total timesteps. Default value: `1`
   */
  end_percentage?: number;
};
export type ControlNetUnion = {
  /**
   * URL or the path to the control net weights.
   */
  path: string;
  /**
   * optional URL to the controlnet config.json file.
   */
  config_url?: string | Blob | File;
  /**
   * The optional variant if a Hugging Face repo key is used.
   */
  variant?: string;
  /**
   * The control images and modes to use for the control net.
   */
  controls: Array<ControlNetUnionInput>;
};
export type DynamicMask = {
  /**
   * URL of the image for Dynamic Brush Application Area (Mask image created by users using the motion brush)
   */
  mask_url: string | Blob | File;
  /**
   * List of trajectories
   */
  trajectories?: Array<Trajectory>;
};
export type EasyControlWeight = {
  /**
   * URL to safetensor weights of control method to be applied. Can also be one of `canny`, `depth`, `hedsketch`, `inpainting`, `pose`, `seg`, `subject`, `ghibli`
   */
  control_method_url: string | Blob | File;
  /**
   * Scale for the control method. Default value: `1`
   */
  scale?: number;
  /**
   * URL of an image to use as a control
   */
  image_url: string | Blob | File;
  /**
   * Control type of the image. Must be one of `spatial` or `subject`.
   */
  image_control_type: "subject" | "spatial";
};
export type fal__toolkit__image__image__Image = {
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
  /**
   * File data
   */
  file_data?: string;
  /**
   * The width of the image in pixels.
   */
  width?: number;
  /**
   * The height of the image in pixels.
   */
  height?: number;
};
export type File = {
  /**
   * The URL where the file can be downloaded from.
   */
  url: string;
  /**
   * The mime type of the file.
   */
  content_type?: string;
  /**
   * The name of the file. It will be auto-generated if not provided.
   */
  file_name?: string;
  /**
   * The size of the file in bytes.
   */
  file_size?: number;
};
export type Image = {
  /**
   *
   */
  url: string;
  /**
   *
   */
  width: number;
  /**
   *
   */
  height: number;
  /**
   *  Default value: `"image/jpeg"`
   */
  content_type?: string;
};
export type ImageCondition = {
  /**
   * The URL of the image to use as input.
   */
  image_url: string | Blob | File;
  /**
   * The frame number to start the condition on.
   */
  start_frame_number?: number;
  /**
   * The strength of the condition. Default value: `1`
   */
  strength?: number;
};
export type ImageSize = {
  /**
   * The width of the generated image. Default value: `512`
   */
  width?: number;
  /**
   * The height of the generated image. Default value: `512`
   */
  height?: number;
};
export type IPAdapter = {
  /**
   * Hugging Face path to the IP-Adapter
   */
  path: string;
  /**
   * Subfolder in which the ip_adapter weights exist
   */
  subfolder?: string;
  /**
   * Name of the safetensors file containing the ip-adapter weights
   */
  weight_name?: string;
  /**
   * Path to the Image Encoder for the IP-Adapter, for example 'openai/clip-vit-large-patch14'
   */
  image_encoder_path: string;
  /**
   * Subfolder in which the image encoder weights exist.
   */
  image_encoder_subfolder?: string;
  /**
   * Name of the image encoder.
   */
  image_encoder_weight_name?: string;
  /**
   * URL of Image for IP-Adapter conditioning.
   */
  image_url: string | Blob | File;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * Scale for ip adapter.
   */
  scale: number;
};
export type Keyframe = {
  /**
   * The timestamp in milliseconds where this keyframe starts
   */
  timestamp: number;
  /**
   * The duration in milliseconds of this keyframe
   */
  duration: number;
  /**
   * The URL where this keyframe's media file can be accessed
   */
  url: string;
};
export type LoraWeight = {
  /**
   * URL or the path to the LoRA weights.
   */
  path: string;
  /**
   * The scale of the LoRA weight. This is used to scale the LoRA weight
   * before merging it with the base model. Default value: `1`
   */
  scale?: number;
};
export type LoRAWeight = {
  /**
   * URL or path to the LoRA weights.
   */
  path: string;
  /**
   * Name of the LoRA weight. Only used if `path` is a HuggingFace repository, and is only required when the repository contains multiple LoRA weights.
   */
  weight_name?: string;
  /**
   * Scale of the LoRA weight. This is a multiplier applied to the LoRA weight when loading it. Default value: `1`
   */
  scale?: number;
};
export type LoudnessNormalizationSetting = {
  /**
   * Enable loudness normalization for the audio Default value: `true`
   */
  enabled?: boolean;
  /**
   * Target loudness in LUFS (default -18.0) Default value: `-18`
   */
  target_loudness?: number;
  /**
   * Target loudness range in LU (default 8.0) Default value: `8`
   */
  target_range?: number;
  /**
   * Target peak level in dBTP (default -0.5). Default value: `-0.5`
   */
  target_peak?: number;
};
export type LoudnormSummary = {
  /**
   * Input integrated loudness in LUFS
   */
  input_integrated?: number;
  /**
   * Input true peak in dBTP
   */
  input_true_peak?: number;
  /**
   * Input loudness range in LU
   */
  input_lra?: number;
  /**
   * Input threshold in LUFS
   */
  input_threshold?: number;
  /**
   * Output integrated loudness in LUFS
   */
  output_integrated?: number;
  /**
   * Output true peak in dBTP
   */
  output_true_peak?: number;
  /**
   * Output loudness range in LU
   */
  output_lra?: number;
  /**
   * Output threshold in LUFS
   */
  output_threshold?: number;
  /**
   * Type of normalization applied (Dynamic/Linear)
   */
  normalization_type?: string;
  /**
   * Target offset in LU
   */
  target_offset?: number;
};
export type PikaImage = {
  /**
   *
   */
  image_url: string | Blob | File;
};
export type PointPrompt = {
  /**
   * X Coordinate of the prompt Default value: `305`
   */
  x?: number;
  /**
   * Y Coordinate of the prompt Default value: `350`
   */
  y?: number;
  /**
   * Label of the prompt. 1 for foreground, 0 for background Default value: `"1"`
   */
  label?: "0" | "1";
  /**
   * The frame index to interact with.
   */
  frame_index?: number;
};
export type PronunciationDict = {
  /**
   * List of pronunciation replacements in format ['text/(pronunciation)', ...]. For Chinese, tones are 1-5. Example: ['燕少飞/(yan4)(shao3)(fei1)']
   */
  tone_list?: Array<string>;
};
export type ReferenceImage = {
  /**
   * URL to the reference image file (PNG format recommended)
   */
  image_url: string | Blob | File;
};
export type registry__image__fast_sdxl__models__Image = {
  /**
   *
   */
  url: string;
  /**
   *
   */
  width: number;
  /**
   *
   */
  height: number;
  /**
   *  Default value: `"image/jpeg"`
   */
  content_type?: string;
};
export type Resolution = {
  /**
   * Display aspect ratio (e.g., '16:9')
   */
  aspect_ratio: string;
  /**
   * Width of the video in pixels
   */
  width: number;
  /**
   * Height of the video in pixels
   */
  height: number;
};
export type RGBColor = {
  /**
   * Red color value
   */
  r?: number;
  /**
   * Green color value
   */
  g?: number;
  /**
   * Blue color value
   */
  b?: number;
};
export type Speaker = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  speaker_id: number;
  /**
   *
   */
  audio_url: string | Blob | File;
};
export type Track = {
  /**
   * Unique identifier for the track
   */
  id: string;
  /**
   * Type of track ('video' or 'audio')
   */
  type: string;
  /**
   * List of keyframes that make up this track
   */
  keyframes: Array<Keyframe>;
};
export type Trajectory = {
  /**
   * X coordinate of the motion trajectory
   */
  x: number;
  /**
   * Y coordinate of the motion trajectory
   */
  y: number;
};
export type TranscriptionWord = {
  /**
   * The transcribed word or audio event
   */
  text: string;
  /**
   * Start time in seconds
   */
  start: number;
  /**
   * End time in seconds
   */
  end: number;
  /**
   * Type of element (word, spacing, or audio_event)
   */
  type: string;
  /**
   * Speaker identifier if diarization was enabled
   */
  speaker_id?: string;
};
export type Turn = {
  /**
   *
   */
  speaker_id: number;
  /**
   *
   */
  text: string;
};
export type Validation = {
  /**
   * The prompt to use for validation.
   */
  prompt: string;
  /**
   * An image to use for image-to-video validation. If provided for one validation, _all_ validation inputs must have an image.
   */
  image_url?: string | Blob | File;
};
export type Video = {
  /**
   * Type of media (always 'video') Default value: `"video"`
   */
  media_type?: string;
  /**
   * URL where the media file can be accessed
   */
  url: string;
  /**
   * MIME type of the media file
   */
  content_type: string;
  /**
   * Original filename of the media
   */
  file_name: string;
  /**
   * Size of the file in bytes
   */
  file_size: number;
  /**
   * Duration of the media in seconds
   */
  duration: number;
  /**
   * Overall bitrate of the media in bits per second
   */
  bitrate: number;
  /**
   * Codec used to encode the media
   */
  codec: string;
  /**
   * Container format of the media file (e.g., 'mp4', 'mov')
   */
  container: string;
  /**
   * Frames per second
   */
  fps: number;
  /**
   * Total number of frames in the video
   */
  frame_count: number;
  /**
   * Time base used for frame timestamps
   */
  timebase: string;
  /**
   * Video resolution information
   */
  resolution: Resolution;
  /**
   * Detailed video format information
   */
  format: VideoFormat;
  /**
   * Audio track information if video has audio
   */
  audio?: AudioTrack;
  /**
   * URL of the extracted first frame
   */
  start_frame_url?: string | Blob | File;
  /**
   * URL of the extracted last frame
   */
  end_frame_url?: string | Blob | File;
};
export type VideoCondition = {
  /**
   * The URL of the video to use as input.
   */
  video_url: string | Blob | File;
  /**
   * The frame number to start the condition on.
   */
  start_frame_number?: number;
  /**
   * The strength of the condition. Default value: `1`
   */
  strength?: number;
};
export type VideoFormat = {
  /**
   * Container format of the video
   */
  container: string;
  /**
   * Video codec used (e.g., 'h264')
   */
  video_codec: string;
  /**
   * Codec profile (e.g., 'main', 'high')
   */
  profile: string;
  /**
   * Codec level (e.g., 4.1)
   */
  level: number;
  /**
   * Pixel format used (e.g., 'yuv420p')
   */
  pixel_format: string;
  /**
   * Video bitrate in bits per second
   */
  bitrate: number;
};
export type VoiceSetting = {
  /**
   * Predefined voice ID to use for synthesis Default value: `"Wise_Woman"`
   */
  voice_id?: string;
  /**
   * Speech speed (0.5-2.0) Default value: `1`
   */
  speed?: number;
  /**
   * Volume (0-10) Default value: `1`
   */
  vol?: number;
  /**
   * Voice pitch (-12 to 12)
   */
  pitch?: number;
  /**
   * Emotion of the generated speech
   */
  emotion?:
    | "happy"
    | "sad"
    | "angry"
    | "fearful"
    | "disgusted"
    | "surprised"
    | "neutral";
  /**
   * Enables English text normalization to improve number reading performance, with a slight increase in latency
   */
  english_normalization?: boolean;
};
export type WordTime = {
  /**
   * The word to inpaint.
   */
  text: string;
  /**
   * The start and end timestamp of the word.
   */
  timestamp: Array<void>;
};
export type AceStepAudioInpaintInput = {
  /**
   * URL of the audio file to be inpainted.
   */
  audio_url: string | Blob | File;
  /**
   * Whether the start time is relative to the start or end of the audio. Default value: `"start"`
   */
  start_time_relative_to?: "start" | "end";
  /**
   * start time in seconds for the inpainting process.
   */
  start_time?: number;
  /**
   * Whether the end time is relative to the start or end of the audio. Default value: `"start"`
   */
  end_time_relative_to?: "start" | "end";
  /**
   * end time in seconds for the inpainting process. Default value: `30`
   */
  end_time?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Variance for the inpainting process. Higher values can lead to more diverse results. Default value: `0.5`
   */
  variance?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
};
export type AceStepAudioInpaintOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
};
export type AceStepAudioOutpaintInput = {
  /**
   * URL of the audio file to be outpainted.
   */
  audio_url: string | Blob | File;
  /**
   * Duration in seconds to extend the audio from the start.
   */
  extend_before_duration?: number;
  /**
   * Duration in seconds to extend the audio from the end. Default value: `30`
   */
  extend_after_duration?: number;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
};
export type AceStepAudioOutpaintOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
};
export type AceStepAudioToAudioInput = {
  /**
   * URL of the audio file to be outpainted.
   */
  audio_url: string | Blob | File;
  /**
   * Whether to edit the lyrics only or remix the audio. Default value: `"remix"`
   */
  edit_mode?: "lyrics" | "remix";
  /**
   * Original tags of the audio file.
   */
  original_tags: string;
  /**
   * Original lyrics of the audio file. Default value: `""`
   */
  original_lyrics?: string;
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
  /**
   * Original seed of the audio file.
   */
  original_seed?: number;
};
export type AceStepAudioToAudioOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
};
export type AceStepInput = {
  /**
   * Comma-separated list of genre tags to control the style of the generated audio.
   */
  tags: string;
  /**
   * Lyrics to be sung in the audio. If not provided or if [inst] or [instrumental] is the content of this field, no lyrics will be sung. Use control structures like [verse], [chorus] and [bridge] to control the structure of the song. Default value: `""`
   */
  lyrics?: string;
  /**
   * The duration of the generated audio in seconds. Default value: `60`
   */
  duration?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
};
export type AceStepOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
};
export type AceStepPromptToAudioInput = {
  /**
   * Prompt to control the style of the generated audio. This will be used to generate tags and lyrics.
   */
  prompt: string;
  /**
   * Whether to generate an instrumental version of the audio.
   */
  instrumental?: boolean;
  /**
   * The duration of the generated audio in seconds. Default value: `60`
   */
  duration?: number;
  /**
   * Number of steps to generate the audio. Default value: `27`
   */
  number_of_steps?: number;
  /**
   * Random seed for reproducibility. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Scheduler to use for the generation process. Default value: `"euler"`
   */
  scheduler?: "euler" | "heun";
  /**
   * Type of CFG to use for the generation process. Default value: `"apg"`
   */
  guidance_type?: "cfg" | "apg" | "cfg_star";
  /**
   * Granularity scale for the generation process. Higher values can reduce artifacts. Default value: `10`
   */
  granularity_scale?: number;
  /**
   * Guidance interval for the generation. 0.5 means only apply guidance in the middle steps (0.25 * infer_steps to 0.75 * infer_steps) Default value: `0.5`
   */
  guidance_interval?: number;
  /**
   * Guidance interval decay for the generation. Guidance scale will decay from guidance_scale to min_guidance_scale in the interval. 0.0 means no decay.
   */
  guidance_interval_decay?: number;
  /**
   * Guidance scale for the generation. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * Minimum guidance scale for the generation after the decay. Default value: `3`
   */
  minimum_guidance_scale?: number;
  /**
   * Tag guidance scale for the generation. Default value: `5`
   */
  tag_guidance_scale?: number;
  /**
   * Lyric guidance scale for the generation. Default value: `1.5`
   */
  lyric_guidance_scale?: number;
};
export type AceStepPromptToAudioOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The random seed used for the generation process.
   */
  seed: number;
  /**
   * The genre tags used in the generation process.
   */
  tags: string;
  /**
   * The lyrics used in the generation process.
   */
  lyrics: string;
};
export type AdvancedFaceSwapInput = {
  /**
   * User's face image to face swap FROM
   */
  face_image_0: Image;
  /**
   * The gender of the person in the face image.
   */
  gender_0: "male" | "female" | "non-binary";
  /**
   * (Optional) The Second face image to face swap FROM
   */
  face_image_1?: Image;
  /**
   * The gender of the person in the second face image.
   */
  gender_1?: "male" | "female" | "non-binary";
  /**
   * The target image to face swap TO
   */
  target_image?: Image;
  /**
   * The type of face swap workflow. target_hair = preserve target's hair. user_hair = preserve user's hair.
   */
  workflow_type: "user_hair" | "target_hair";
  /**
   * Apply 2x upscale and boost quality. Upscaling will refine the image and make the subjects brighter. Default value: `true`
   */
  upscale?: boolean;
  /**
   * (Beta) Apply detailer to the image. Detailer will improve certain image details, but slightly increase generation time.
   */
  detailer?: boolean;
};
export type AdvancedFaceSwapOutput = {
  /**
   * The mirrored image.
   */
  image: Image;
};
export type AgeProgressionInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The age change to apply. Default value: `"20 years older"`
   */
  prompt?: string;
};
export type AgeProgressionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type AiAvatarInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `145`
   */
  num_frames?: number;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `42`
   */
  seed?: number;
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type AiAvatarMultiInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the Person 1 audio file.
   */
  first_audio_url: string | Blob | File;
  /**
   * The URL of the Person 2 audio file.
   */
  second_audio_url?: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `181`
   */
  num_frames?: number;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `81`
   */
  seed?: number;
  /**
   * Whether to use only the first audio file.
   */
  use_only_first_audio?: boolean;
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type AiAvatarMultiOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type AiAvatarMultiTextInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text input to guide video generation.
   */
  first_text_input: string;
  /**
   * The text input to guide video generation.
   */
  second_text_input: string;
  /**
   * The first person's voice to use for speech generation Default value: `"Sarah"`
   */
  voice1?:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
  /**
   * The second person's voice to use for speech generation Default value: `"Roger"`
   */
  voice2?:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `191`
   */
  num_frames?: number;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `81`
   */
  seed?: number;
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type AiAvatarMultiTextOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type AiAvatarOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type AiAvatarSingleTextInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text input to guide video generation.
   */
  text_input: string;
  /**
   * The voice to use for speech generation
   */
  voice:
    | "Aria"
    | "Roger"
    | "Sarah"
    | "Laura"
    | "Charlie"
    | "George"
    | "Callum"
    | "River"
    | "Liam"
    | "Charlotte"
    | "Alice"
    | "Matilda"
    | "Will"
    | "Jessica"
    | "Eric"
    | "Chris"
    | "Brian"
    | "Daniel"
    | "Lily"
    | "Bill";
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 129 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `136`
   */
  num_frames?: number;
  /**
   * Resolution of the video to generate. Must be either 480p or 720p. Default value: `"480p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen. Default value: `42`
   */
  seed?: number;
  /**
   * The acceleration level to use for generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type AiAvatarSingleTextOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type AmEngOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type AnyLlmEnterpriseInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * Name of the model to use. Premium models are charged at 10x the rate of standard models, they include: anthropic/claude-3.5-sonnet, meta-llama/llama-3.2-90b-vision-instruct, anthropic/claude-3.7-sonnet, openai/o3, openai/gpt-5-chat, anthropic/claude-3-5-haiku, google/gemini-pro-1.5, openai/gpt-4o. Default value: `"google/gemini-flash-1.5"`
   */
  model?:
    | "anthropic/claude-3.7-sonnet"
    | "anthropic/claude-3.5-sonnet"
    | "anthropic/claude-3-5-haiku"
    | "anthropic/claude-3-haiku"
    | "google/gemini-pro-1.5"
    | "google/gemini-flash-1.5"
    | "google/gemini-flash-1.5-8b"
    | "google/gemini-2.0-flash-001"
    | "meta-llama/llama-3.2-1b-instruct"
    | "meta-llama/llama-3.2-3b-instruct"
    | "meta-llama/llama-3.1-8b-instruct"
    | "meta-llama/llama-3.1-70b-instruct"
    | "openai/gpt-oss-120b"
    | "openai/gpt-4o-mini"
    | "openai/gpt-4o"
    | "openai/o3"
    | "openai/gpt-5-chat"
    | "openai/gpt-5-mini"
    | "openai/gpt-5-nano"
    | "meta-llama/llama-4-maverick"
    | "meta-llama/llama-4-scout";
};
export type AnyLlmEnterpriseOutput = {
  /**
   * Generated output
   */
  output: string;
  /**
   * Generated reasoning for the final answer
   */
  reasoning?: string;
  /**
   * Whether the output is partial
   */
  partial?: boolean;
  /**
   * Error message if an error occurred
   */
  error?: string;
};
export type Audio2VideoInput = {
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
  /**
   *
   */
  audio_url: string | Blob | File;
};
export type AudioInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
};
export type AudioOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The prompt used to generate the audio.
   */
  prompt: string;
};
export type AuraFlowInput = {
  /**
   * The prompt to generate images from
   */
  prompt: string;
  /**
   * The number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * The seed to use for generating images
   */
  seed?: number;
  /**
   * Classifier free guidance scale Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to take Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Whether to perform prompt expansion (recommended) Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type AuraFlowOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * The seed used to generate the images
   */
  seed: number;
  /**
   * The expanded prompt
   */
  prompt: string;
};
export type AuraSrInput = {
  /**
   * URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * Upscaling factor. More coming soon. Default value: `"4"`
   */
  upscaling_factor?: "4";
  /**
   * Whether to use overlapping tiles for upscaling. Setting this to true helps remove seams but doubles the inference time.
   */
  overlapping_tiles?: boolean;
  /**
   * Checkpoint to use for upscaling. More coming soon. Default value: `"v1"`
   */
  checkpoint?: "v1" | "v2";
};
export type AuraSrOutput = {
  /**
   * Upscaled image
   */
  image: Image;
  /**
   * Timings for each step in the pipeline.
   */
  timings: any;
};
export type AutoCaptionInput = {
  /**
   * URL to the .mp4 video with audio. Only videos of size <100MB are allowed.
   */
  video_url: string | Blob | File;
  /**
   * Colour of the text. Can be a RGB tuple, a color name, or an hexadecimal notation. Default value: `"white"`
   */
  txt_color?: string;
  /**
   * Font for generated captions. Choose one in 'Arial','Standard','Garamond', 'Times New Roman','Georgia', or pass a url to a .ttf file Default value: `"Standard"`
   */
  txt_font?: string;
  /**
   * Size of text in generated captions. Default value: `24`
   */
  font_size?: number;
  /**
   * Width of the text strokes in pixels Default value: `1`
   */
  stroke_width?: number;
  /**
   * Left-to-right alignment of the text. Can be a string ('left', 'center', 'right') or a float (0.0-1.0) Default value: `center`
   */
  left_align?: string | number;
  /**
   * Top-to-bottom alignment of the text. Can be a string ('top', 'center', 'bottom') or a float (0.0-1.0) Default value: `center`
   */
  top_align?: string | number;
  /**
   * Number of seconds the captions should stay on screen. A higher number will also result in more text being displayed at once. Default value: `1.5`
   */
  refresh_interval?: number;
};
export type AutoCaptionOutput = {
  /**
   * URL to the caption .mp4 video.
   */
  video_url: string | Blob | File;
};
export type AvatarsAudioToVideoInput = {
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
  /**
   *
   */
  audio_url: string | Blob | File;
};
export type AvatarsAudioToVideoOutput = {
  /**
   *
   */
  video: File;
};
export type AvatarsTextToVideoInput = {
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
  /**
   *
   */
  text: string;
};
export type AvatarsTextToVideoOutput = {
  /**
   *
   */
  video: File;
};
export type BabyVersionInput = {
  /**
   * URL of the image to transform into a baby version.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BabyVersionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type BackgroundChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired background to apply. Default value: `"beach sunset with palm trees"`
   */
  prompt?: string;
};
export type BackgroundChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type BagelEditInput = {
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
};
export type BagelEditOutput = {
  /**
   * The edited images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type BagelInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type BagelOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type BagelUnderstandInput = {
  /**
   * The image for the query.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to query the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
};
export type BagelUnderstandOutput = {
  /**
   * The answer to the query.
   */
  text: string;
  /**
   * The seed used for the generation.
   */
  seed: number;
  /**
   * The query used for the generation.
   */
  prompt: string;
  /**
   * The timings of the generation.
   */
  timings: any;
};
export type BaseFlux1ImageToInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseFlux1Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseFlux1ReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseImageToInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKontextEditInput = {
  /**
   * The URL of the image to edit.
   *
   * Max width: 14142px, Max height: 14142px, Timeout: 20s
   */
  image_url: string | Blob | File;
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
};
export type BaseKontextImg2ImgInput = {
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The URL of the image for image-to-image.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKontextInpaintInput = {
  /**
   * The URL of the image to be inpainted.
   */
  image_url: string | Blob | File;
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the reference image for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The URL of the mask for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
};
export type BaseKontextInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaFlux1ImageToInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaFlux1Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaFlux1ReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaImageToInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseKreaReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type BaseTextToImageInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BatchMoonDreamOutput = {
  /**
   * URL to the generated captions JSON file containing filename-caption pairs.
   */
  captions_file: File;
  /**
   * List of generated captions
   */
  outputs: Array<string>;
};
export type BatchQueryInput = {
  /**
   * List of image URLs to be processed (maximum 32 images)
   */
  images_data_url: string | Blob | File;
  /**
   * Single prompt to apply to all images
   */
  prompt: string;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
};
export type BboxInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * List of bounding box coordinates to erase (only one box prompt is supported)
   */
  box_prompts?: Array<BBoxPromptBase>;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
};
export type BenV2ImageInput = {
  /**
   * URL of image to be used for background removal
   */
  image_url: string | Blob | File;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type BenV2ImageOutput = {
  /**
   * The output image after background removal.
   */
  image: Image;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type BenV2VideoInput = {
  /**
   * URL of video to be used for background removal.
   */
  video_url: string | Blob | File;
  /**
   * Optional RGB values (0-255) for the background color. If not provided, the background will be transparent. For ex: [0, 0, 0]
   */
  background_color?: Array<void>;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type BenV2VideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type BGRemoveBatchedInput = {
  /**
   * List of image URLs to be processed (maximum 32 images)
   */
  images_data_url: string | Blob | File;
};
export type BGRemoveBatchedOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
};
export type BGRemoveInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BGRemoveOutput = {
  /**
   * The generated image
   */
  image: Image;
};
export type BGReplaceInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the reference image to be used for generating the new background. Use "" to leave empty. Either ref_image_url or bg_prompt has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt?: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Whether to refine prompt Default value: `true`
   */
  refine_prompt?: boolean;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BGReplaceOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BlurInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
};
export type BlurOutput = {
  /**
   * The processed images with blur effect
   */
  images: Array<Image>;
};
export type BrEngOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type BriaBackgroundRemoveInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaBackgroundRemoveOutput = {
  /**
   * The generated image
   */
  image: Image;
};
export type BriaBackgroundReplaceInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the reference image to be used for generating the new background. Use "" to leave empty. Either ref_image_url or bg_prompt has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt?: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Whether to refine prompt Default value: `true`
   */
  refine_prompt?: boolean;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaBackgroundReplaceOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BriaEraserInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * You can use this parameter to specify the type of the input mask from the list. 'manual' opttion should be used in cases in which the mask had been generated by a user (e.g. with a brush tool), and 'automatic' mask type should be used when mask had been generated by an algorithm like 'SAM'. Default value: `"manual"`
   */
  mask_type?: "manual" | "automatic";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, attempts to preserve the alpha channel of the input image.
   */
  preserve_alpha?: boolean;
};
export type BriaEraserOutput = {
  /**
   * The generated image
   */
  image: Image;
};
export type BriaExpandInput = {
  /**
   * The URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The desired size of the final image, after the expansion. should have an area of less than 5000x5000 pixels.
   */
  canvas_size: Array<number>;
  /**
   * The desired size of the original image, inside the full canvas. Ensure that the ratio of input image foreground or main subject to the canvas area is greater than 15% to achieve optimal results.
   */
  original_image_size: Array<number>;
  /**
   * The desired location of the original image, inside the full canvas. Provide the location of the upper left corner of the original image. The location can also be outside the canvas (the original image will be cropped).
   */
  original_image_location: Array<number>;
  /**
   * Text on which you wish to base the image expansion. This parameter is optional. Bria currently supports prompts in English only, excluding special characters. Default value: `""`
   */
  prompt?: string;
  /**
   * You can choose whether you want your generated expension to be random or predictable. You can recreate the same result in the future by using the seed value of a result from the response. You can exclude this parameter if you are not interested in recreating your results. This parameter is optional.
   */
  seed?: number;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaExpandOutput = {
  /**
   * The generated image
   */
  image: Image;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BriaGenfillInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaGenfillOutput = {
  /**
   * Generated Images
   */
  images: Array<Image>;
};
export type BriaProductShotInput = {
  /**
   * The URL of the product shot to be placed in a lifestyle shot. If both image_url and image_file are provided, image_url will be used. Accepted formats are jpeg, jpg, png, webp. Maximum file size 12MB.
   */
  image_url: string | Blob | File;
  /**
   * Text description of the new scene or background for the provided product shot. Bria currently supports prompts in English only, excluding special characters.
   */
  scene_description?: string;
  /**
   * The URL of the reference image to be used for generating the new scene or background for the product shot. Use "" to leave empty.Either ref_image_url or scene_description has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * Whether to optimize the scene description Default value: `true`
   */
  optimize_description?: boolean;
  /**
   * The number of lifestyle product shots you would like to generate. You will get num_results x 10 results when placement_type=automatic and according to the number of required placements x num_results if placement_type=manual_placement. Default value: `1`
   */
  num_results?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * This parameter allows you to control the positioning of the product in the image. Choosing 'original' will preserve the original position of the product in the image. Choosing 'automatic' will generate results with the 10 recommended positions for the product. Choosing 'manual_placement' will allow you to select predefined positions (using the parameter 'manual_placement_selection'). Selecting 'manual_padding' will allow you to control the position and size of the image by defining the desired padding in pixels around the product. Default value: `"manual_placement"`
   */
  placement_type?:
    | "original"
    | "automatic"
    | "manual_placement"
    | "manual_padding";
  /**
   * This flag is only relevant when placement_type=original. If true, the output image retains the original input image's size; otherwise, the image is scaled to 1 megapixel (1MP) while preserving its aspect ratio.
   */
  original_quality?: boolean;
  /**
   * The desired size of the final product shot. For optimal results, the total number of pixels should be around 1,000,000. This parameter is only relevant when placement_type=automatic or placement_type=manual_placement.
   */
  shot_size?: Array<number>;
  /**
   * If you've selected placement_type=manual_placement, you should use this parameter to specify which placements/positions you would like to use from the list. You can select more than one placement in one request. Default value: `"bottom_center"`
   */
  manual_placement_selection?:
    | "upper_left"
    | "upper_right"
    | "bottom_left"
    | "bottom_right"
    | "right_center"
    | "left_center"
    | "upper_center"
    | "bottom_center"
    | "center_vertical"
    | "center_horizontal";
  /**
   * The desired padding in pixels around the product, when using placement_type=manual_padding. The order of the values is [left, right, top, bottom]. For optimal results, the total number of pixels, including padding, should be around 1,000,000. It is recommended to first use the product cutout API, get the cutout and understand the size of the result, and then define the required padding and use the cutout as an input for this API.
   */
  padding_values?: Array<number>;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaProductShotOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
};
export type BriaReimagineInput = {
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The URL of the structure reference image. Use "" to leave empty. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  structure_image_url?: string | Blob | File;
  /**
   * The influence of the structure reference on the generated image. Default value: `0.75`
   */
  structure_ref_influence?: number;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `1`
   */
  num_results?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaReimagineOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BriaTextToImageBaseInput = {
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `4`
   */
  num_images?: number;
  /**
   * The aspect ratio of the image. When a guidance method is being used, the aspect ratio is defined by the guidance image and this parameter is ignored. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * When set to true, enhances the provided prompt by generating additional, more descriptive variations, resulting in more diverse and creative output images.
   */
  prompt_enhancement?: boolean;
  /**
   * Which medium should be included in your generated images. This parameter is optional.
   */
  medium?: "photography" | "art";
  /**
   * Guidance images to use for the generation. Up to 4 guidance methods can be combined during a single inference.
   */
  guidance?: Array<GuidanceInput>;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaTextToImageBaseOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BriaTextToImageFastInput = {
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `4`
   */
  num_images?: number;
  /**
   * The aspect ratio of the image. When a guidance method is being used, the aspect ratio is defined by the guidance image and this parameter is ignored. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `8`
   */
  num_inference_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * When set to true, enhances the provided prompt by generating additional, more descriptive variations, resulting in more diverse and creative output images.
   */
  prompt_enhancement?: boolean;
  /**
   * Which medium should be included in your generated images. This parameter is optional.
   */
  medium?: "photography" | "art";
  /**
   * Guidance images to use for the generation. Up to 4 guidance methods can be combined during a single inference.
   */
  guidance?: Array<GuidanceInput>;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaTextToImageFastOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BriaTextToImageHdInput = {
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `4`
   */
  num_images?: number;
  /**
   * The aspect ratio of the image. When a guidance method is being used, the aspect ratio is defined by the guidance image and this parameter is ignored. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * When set to true, enhances the provided prompt by generating additional, more descriptive variations, resulting in more diverse and creative output images.
   */
  prompt_enhancement?: boolean;
  /**
   * Which medium should be included in your generated images. This parameter is optional.
   */
  medium?: "photography" | "art";
  /**
   * Guidance images to use for the generation. Up to 4 guidance methods can be combined during a single inference.
   */
  guidance?: Array<GuidanceInput>;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BriaTextToImageHdOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type BroccoliHaircutInput = {
  /**
   * URL of the image to apply broccoli haircut style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type BroccoliHaircutOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type BrPortugeseOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type BytedanceDreaminaV31TextToImageInput = {
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * The size of the generated image. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Whether to use an LLM to enhance the prompt
   */
  enhance_prompt?: boolean;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type BytedanceDreaminaV31TextToImageOutput = {
  /**
   * Generated images
   */
  images: Array<Image>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceOmnihumanInput = {
  /**
   * The URL of the image used to generate the video
   */
  image_url: string | Blob | File;
  /**
   * The URL of the audio file to generate the video. Audio must be under 30s long.
   */
  audio_url: string | Blob | File;
};
export type BytedanceOmnihumanOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Duration of audio input/video output as used for billing.
   */
  duration: number;
};
export type BytedanceSeedanceV1LiteImageToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
  /**
   * The URL of the image the video ends with. Defaults to None.
   */
  end_image_url?: string | Blob | File;
};
export type BytedanceSeedanceV1LiteImageToVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceSeedanceV1LiteTextToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "9:21";
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type BytedanceSeedanceV1LiteTextToVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceSeedanceV1ProImageToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
};
export type BytedanceSeedanceV1ProImageToVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceSeedanceV1ProTextToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type BytedanceSeedanceV1ProTextToVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceSeededitV3EditImageInput = {
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * URL of the image to be edited.
   */
  image_url: string | Blob | File;
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `0.5`
   */
  guidance_scale?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type BytedanceSeededitV3EditImageOutput = {
  /**
   * Generated image
   */
  image: Image;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type BytedanceSeedreamV3TextToImageInput = {
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Use for finer control over the output image size. Will be used over aspect_ratio, if both are provided. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type BytedanceSeedreamV3TextToImageOutput = {
  /**
   * Generated images
   */
  images: Array<Image>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type CalligrapherInput = {
  /**
   * Base64-encoded source image with drawn mask layers
   */
  source_image_url: string | Blob | File;
  /**
   * Base64-encoded mask image (optional if using auto_mask_generation)
   */
  mask_image_url?: string | Blob | File;
  /**
   * Optional base64 reference image for style
   */
  reference_image_url?: string | Blob | File;
  /**
   * Text prompt to inpaint or customize
   */
  prompt: string;
  /**
   * How many images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Target image size for generation
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Guidance or strength scale for the model Default value: `1`
   */
  cfg_scale?: number;
  /**
   * Number of inference steps (1-100) Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Whether to prepend context reference to the input Default value: `true`
   */
  use_context?: boolean;
  /**
   * Whether to automatically generate mask from detected text
   */
  auto_mask_generation?: boolean;
  /**
   * Source text to replace (if empty, masks all detected text) Default value: `""`
   */
  source_text?: string;
};
export type CalligrapherOutput = {
  /**
   *
   */
  images: Array<Image>;
};
export type CartoonifyInput = {
  /**
   * URL of the image to apply Pixar style to
   */
  image_url: string | Blob | File;
  /**
   * Scale factor for the Pixar effect Default value: `1`
   */
  scale?: number;
  /**
   * Guidance scale for the generation Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use CFG zero
   */
  use_cfg_zero?: boolean;
  /**
   * The seed for image generation. Same seed with same parameters will generate same image.
   */
  seed?: number;
};
export type CartoonifyOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type CatVtonInput = {
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * Type of the Cloth to be tried on.
   *
   * Options:
   * upper: Upper body cloth
   * lower: Lower body cloth
   * overall: Full body cloth
   * inner: Inner cloth, like T-shirt inside a jacket
   * outer: Outer cloth, like a jacket over a T-shirt
   */
  cloth_type: "upper" | "lower" | "overall" | "inner" | "outer";
  /**
   * The size of the generated image. Default value: `portrait_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type CatVtonOutput = {
  /**
   * The output image.
   */
  image: Image;
};
export type ChainOfZoomInput = {
  /**
   * Input image to zoom into
   */
  image_url: string | Blob | File;
  /**
   * Zoom scale in powers of 2 Default value: `5`
   */
  scale?: number;
  /**
   * X coordinate of zoom center (0-1) Default value: `0.5`
   */
  center_x?: number;
  /**
   * Y coordinate of zoom center (0-1) Default value: `0.5`
   */
  center_y?: number;
  /**
   * Additional prompt text to guide the zoom enhancement Default value: `""`
   */
  user_prompt?: string;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ChainOfZoomOutput = {
  /**
   * List of intermediate images
   */
  images: Array<Image>;
  /**
   * Actual linear zoom scale applied
   */
  scale: number;
  /**
   * Center coordinates used for zoom
   */
  zoom_center: Array<number>;
};
export type ChatInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * Name of the model to use. Premium models are charged at 10x the rate of standard models, they include: anthropic/claude-3.5-sonnet, google/gemini-pro-1.5, meta-llama/llama-3.2-90b-vision-instruct, deepseek/deepseek-r1, openai/o3, openai/gpt-5-chat, anthropic/claude-3-5-haiku, anthropic/claude-3.7-sonnet, openai/gpt-4o. Default value: `"google/gemini-flash-1.5"`
   */
  model?:
    | "deepseek/deepseek-r1"
    | "anthropic/claude-3.7-sonnet"
    | "anthropic/claude-3.5-sonnet"
    | "anthropic/claude-3-5-haiku"
    | "anthropic/claude-3-haiku"
    | "google/gemini-pro-1.5"
    | "google/gemini-flash-1.5"
    | "google/gemini-flash-1.5-8b"
    | "google/gemini-2.0-flash-001"
    | "meta-llama/llama-3.2-1b-instruct"
    | "meta-llama/llama-3.2-3b-instruct"
    | "meta-llama/llama-3.1-8b-instruct"
    | "meta-llama/llama-3.1-70b-instruct"
    | "openai/gpt-oss-120b"
    | "openai/gpt-4o-mini"
    | "openai/gpt-4o"
    | "openai/o3"
    | "openai/gpt-5-chat"
    | "openai/gpt-5-mini"
    | "openai/gpt-5-nano"
    | "meta-llama/llama-4-maverick"
    | "meta-llama/llama-4-scout";
};
export type ChatterboxhdSpeechToSpeechInput = {
  /**
   * URL to the source audio file to be voice-converted.
   */
  source_audio_url: string | Blob | File;
  /**
   * The voice to use for the speech-to-speech request. If neither target_voice nor target_voice_audio_url are provided, a random target voice will be used.
   */
  target_voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
  /**
   * URL to the audio file which represents the voice of the output audio. If provided, this will override the target_voice setting. If neither target_voice nor target_voice_audio_url are provided, the default target voice will be used.
   */
  target_voice_audio_url?: string | Blob | File;
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
};
export type ChatterboxhdSpeechToSpeechOutput = {
  /**
   * The generated voice-converted audio file.
   */
  audio: Audio;
};
export type ChatterboxhdTextToSpeechInput = {
  /**
   * Text to synthesize into speech. Default value: `"My name is Maximus Decimus Meridius, commander of the Armies of the North, General of the Felix Legions and loyal servant to the true emperor, Marcus Aurelius. Father to a murdered son, husband to a murdered wife. And I will have my vengeance, in this life or the next."`
   */
  text?: string;
  /**
   * The voice to use for the TTS request. If neither voice nor audio are provided, a random voice will be used.
   */
  voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
  /**
   * URL to the audio sample to use as a voice prompt for zero-shot TTS voice cloning. Providing a audio sample will override the voice setting. If neither voice nor audio_url are provided, a random voice will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * Controls emotion exaggeration. Range typically 0.25 to 2.0. Default value: `0.5`
   */
  exaggeration?: number;
  /**
   * Classifier-free guidance scale (CFG) controls the conditioning factor. Range typically 0.2 to 1.0. For expressive or dramatic speech, try lower cfg values (e.g. ~0.3) and increase exaggeration to around 0.7 or higher. If the reference speaker has a fast speaking style, lowering cfg to around 0.3 can improve pacing. Default value: `0.5`
   */
  cfg?: number;
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
  /**
   * Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file. Set to 0 for random seed.
   */
  seed?: number;
  /**
   * Controls the randomness of generation. Range typically 0.05 to 5. Default value: `0.8`
   */
  temperature?: number;
};
export type ChatterboxhdTextToSpeechOutput = {
  /**
   * The generated audio file.
   */
  audio: Audio;
};
export type ChatterboxOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type ChatterboxSpeechToSpeechInput = {
  /**
   *
   */
  source_audio_url: string | Blob | File;
  /**
   * Optional URL to an audio file to use as a reference for the generated speech. If provided, the model will try to match the style and tone of the reference audio.
   */
  target_voice_audio_url?: string | Blob | File;
};
export type ChatterboxSpeechToSpeechOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type ChatterboxTextToSpeechInput = {
  /**
   * The text to be converted to speech. You can additionally add the following emotive tags: <laugh>, <chuckle>, <sigh>, <cough>, <sniffle>, <groan>, <yawn>, <gasp>
   */
  text: string;
  /**
   * Optional URL to an audio file to use as a reference for the generated speech. If provided, the model will try to match the style and tone of the reference audio. Default value: `"https://storage.googleapis.com/chatterbox-demo-samples/prompts/male_rickmorty.mp3"`
   */
  audio_url?: string | Blob | File;
  /**
   * Exaggeration factor for the generated speech (0.0 = no exaggeration, 1.0 = maximum exaggeration). Default value: `0.25`
   */
  exaggeration?: number;
  /**
   * Temperature for generation (higher = more creative). Default value: `0.7`
   */
  temperature?: number;
  /**
   *  Default value: `0.5`
   */
  cfg?: number;
  /**
   * Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file. Set to 0 for random seed..
   */
  seed?: number;
};
export type ChromaticAberrationInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Red channel shift amount
   */
  red_shift?: number;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
};
export type ChromaticAberrationOutput = {
  /**
   * The processed images with chromatic aberration effect
   */
  images: Array<Image>;
};
export type ClarityUpscalerInput = {
  /**
   * The URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to use for generating the image. Be as descriptive as possible for best results. Default value: `"masterpiece, best quality, highres"`
   */
  prompt?: string;
  /**
   * The upscale factor Default value: `2`
   */
  upscale_factor?: number;
  /**
   * The negative prompt to use. Use it to address details that you don't want in the image. Default value: `"(worst quality, low quality, normal quality:2)"`
   */
  negative_prompt?: string;
  /**
   * The creativity of the model. The higher the creativity, the more the model will deviate from the prompt.
   * Refers to the denoise strength of the sampling. Default value: `0.35`
   */
  creativity?: number;
  /**
   * The resemblance of the upscaled image to the original image. The higher the resemblance, the more the model will try to keep the original image.
   * Refers to the strength of the ControlNet. Default value: `0.6`
   */
  resemblance?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `18`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of Stable Diffusion
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to false, the safety checker will be disabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type ClarityUpscalerOutput = {
  /**
   * The URL of the generated image.
   */
  image: Image;
  /**
   * The seed used to generate the image.
   */
  seed: number;
  /**
   * The timings of the different steps in the workflow.
   */
  timings: any;
};
export type CodeformerInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Weight of the fidelity factor. Default value: `0.5`
   */
  fidelity?: number;
  /**
   * Upscaling factor Default value: `2`
   */
  upscaling?: number;
  /**
   * Should faces etc should be aligned.
   */
  aligned?: boolean;
  /**
   * Should only center face be restored
   */
  only_center_face?: boolean;
  /**
   * Should faces be upscaled Default value: `true`
   */
  face_upscale?: boolean;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type CodeformerOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type Cogvideox5bInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
};
export type Cogvideox5bOutput = {
  /**
   * The URL to the generated video
   */
  video: File;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated video. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * The prompt used for generating the video.
   */
  prompt: string;
};
export type Cogview4Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type Cogview4Output = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type CollectionToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ColorCorrectionInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
};
export type ColorCorrectionOutput = {
  /**
   * The processed images with color correction
   */
  images: Array<Image>;
};
export type ColorTintInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
};
export type ColorTintOutput = {
  /**
   * The processed images with color tint effect
   */
  images: Array<Image>;
};
export type CombineInput = {
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
  /**
   * URL of the audio file to use as the audio track
   */
  audio_url: string | Blob | File;
  /**
   * Offset in seconds for when the audio should start relative to the video
   */
  start_offset?: number;
};
export type CombineOutput = {
  /**
   * Output video with merged audio.
   */
  video: File;
};
export type ComposeOutput = {
  /**
   * URL of the processed video file
   */
  video_url: string | Blob | File;
  /**
   * URL of the video's thumbnail image
   */
  thumbnail_url: string | Blob | File;
};
export type ControlNetUnionInput = {
  /**
   * URL of the image to be used as the control image.
   */
  control_image_url: string | Blob | File;
  /**
   * URL of the mask for the control image.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Control Mode for Flux Controlnet Union. Supported values are:
   * - canny: Uses the edges for guided generation.
   * - tile: Uses the tiles for guided generation.
   * - depth: Utilizes a grayscale depth map for guided generation.
   * - blur: Adds a blur to the image.
   * - pose: Uses the pose of the image for guided generation.
   * - gray: Converts the image to grayscale.
   * - low-quality: Converts the image to a low-quality image.
   */
  control_mode:
    | "canny"
    | "tile"
    | "depth"
    | "blur"
    | "pose"
    | "gray"
    | "low-quality";
  /**
   * The scale of the control net weight. This is used to scale the control net weight
   * before merging it with the base model. Default value: `1`
   */
  conditioning_scale?: number;
  /**
   * Threshold for mask. Default value: `0.5`
   */
  mask_threshold?: number;
  /**
   * The percentage of the image to start applying the controlnet in terms of the total timesteps.
   */
  start_percentage?: number;
  /**
   * The percentage of the image to end applying the controlnet in terms of the total timesteps. Default value: `1`
   */
  end_percentage?: number;
};
export type CreateVoiceInput = {
  /**
   * Voice name (required, max 255 characters).
   */
  name: string;
  /**
   * A list of audio URLs used for cloning (must be between 1 and 5 URLs).
   */
  samples: Array<AudioInput>;
};
export type CreateVoiceOutput = {
  /**
   * The S3 URI of the cloned voice.
   */
  voice: string;
};
export type Csm1bInput = {
  /**
   * The text to generate an audio from.
   */
  scene: Array<Turn>;
  /**
   * The context to generate an audio from.
   */
  context?: Array<Speaker>;
};
export type Csm1bOutput = {
  /**
   * The generated audio.
   */
  audio: File | string;
};
export type DdcolorInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type DdcolorOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type DepthLoraInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * If set to true, the input image will be preprocessed to extract depth information.
   * This is useful for generating depth maps from images. Default value: `true`
   */
  preprocess_depth?: boolean;
};
export type DesaturateInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
};
export type DesaturateOutput = {
  /**
   * The processed images with desaturation effect
   */
  images: Array<Image>;
};
export type DetectionInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Type of detection to perform
   */
  task_type: "bbox_detection" | "point_detection" | "gaze_detection";
  /**
   * Text description of what to detect
   */
  detection_prompt: string;
  /**
   * Whether to use ensemble for gaze detection
   */
  use_ensemble?: boolean;
};
export type DetectionOutput = {
  /**
   * Output image with detection visualization
   */
  image: Image;
  /**
   * Detection results as text
   */
  text_output: string;
};
export type DevImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type DevReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type DevTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type DiaCloneOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type DiaOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type DiaTtsInput = {
  /**
   * The text to be converted to speech.
   */
  text: string;
};
export type DiaTtsOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type DiaTtsVoiceCloneInput = {
  /**
   * The text to be converted to speech.
   */
  text: string;
  /**
   * The URL of the reference audio file.
   */
  ref_audio_url: string | Blob | File;
  /**
   * The reference text to be used for TTS.
   */
  ref_text: string;
};
export type DiaTtsVoiceCloneOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type DifferentialDiffusionInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: "sgm_uniform";
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * URL of image to use as initial image.
   */
  image_url: string | Blob | File;
  /**
   * URL of change map.
   */
  change_map_image_url: string | Blob | File;
  /**
   * The strength to use for differential diffusion. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type DiffrhythmInput = {
  /**
   * The prompt to generate the song from. Must have two sections. Sections start with either [chorus] or a [verse].
   */
  lyrics: string;
  /**
   * The URL of the reference audio to use for the music generation.
   */
  reference_audio_url?: string | Blob | File;
  /**
   * The style prompt to use for the music generation.
   */
  style_prompt?: string;
  /**
   * The duration of the music to generate. Default value: `"95s"`
   */
  music_duration?: "95s" | "285s";
  /**
   * The CFG strength to use for the music generation. Default value: `4`
   */
  cfg_strength?: number;
  /**
   * The scheduler to use for the music generation. Default value: `"euler"`
   */
  scheduler?: "euler" | "midpoint" | "rk4" | "implicit_adams";
  /**
   * The number of inference steps to use for the music generation. Default value: `32`
   */
  num_inference_steps?: number;
};
export type DiffrhythmOutput = {
  /**
   * Generated music file.
   */
  audio: File;
};
export type DissolveInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * URL of second image for dissolve
   */
  dissolve_image_url: string | Blob | File;
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
};
export type DissolveOutput = {
  /**
   * The processed images with dissolve effect
   */
  images: Array<Image>;
};
export type DistilledExtendVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type DistilledImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
};
export type DistilledMultiConditioningVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type DistilledTextToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9, 1:1 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type DocresDewarpInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type DocresDewarpOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type DocresInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Task to perform
   */
  task: "deshadowing" | "appearance" | "deblurring" | "binarization";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type DocResInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Task to perform
   */
  task: "deshadowing" | "appearance" | "deblurring" | "binarization";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
};
export type DocresOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type DodgeBurnInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
};
export type DodgeBurnOutput = {
  /**
   * The processed images with dodge and burn effect
   */
  images: Array<Image>;
};
export type DrctSuperResolutionInput = {
  /**
   * URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * Upscaling factor. Default value: `"4"`
   */
  upscaling_factor?: "4";
};
export type DrctSuperResolutionOutput = {
  /**
   * Upscaled image
   */
  image: Image;
};
export type DreaminaInput = {
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * The size of the generated image. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Whether to use an LLM to enhance the prompt
   */
  enhance_prompt?: boolean;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type DreaminaOutput = {
  /**
   * Generated images
   */
  images: Array<Image>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type DreamoInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * URL of first reference image to use for generation.
   */
  first_image_url?: string | Blob | File;
  /**
   * URL of second reference image to use for generation.
   */
  second_image_url?: string | Blob | File;
  /**
   * Task for first reference image (ip/id/style). Default value: `"ip"`
   */
  first_reference_task?: "ip" | "id" | "style";
  /**
   * Task for second reference image (ip/id/style). Default value: `"ip"`
   */
  second_reference_task?: "ip" | "id" | "style";
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `12`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Resolution for reference images. Default value: `512`
   */
  ref_resolution?: number;
  /**
   * The weight of the CFG loss. Default value: `1`
   */
  true_cfg?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type DreamoOutput = {
  /**
   * The URLs of the generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used to generate the image.
   */
  prompt: string;
};
export type DubbingInput = {
  /**
   * Input video URL to be dubbed.
   */
  video_url: string | Blob | File;
  /**
   * Target language to dub the video to Default value: `"hindi"`
   */
  target_language?: "hindi" | "turkish" | "english";
  /**
   * Whether to lip sync the audio to the video Default value: `true`
   */
  do_lipsync?: boolean;
};
export type DubbingOutput = {
  /**
   * The generated video with the lip sync.
   */
  video: File;
};
export type EaselAvatarInput = {
  /**
   * The face image with which the scene is generated.
   */
  face_image_0: Image;
  /**
   * The gender of the person in the face image
   */
  gender_0: "male" | "female" | "non-binary";
  /**
   * (Optional) The second face image used to generate a two-person scene.
   */
  face_image_1?: Image;
  /**
   * The gender of the person in the second face image Default value: `"female"`
   */
  gender_1?: "male" | "female" | "non-binary";
  /**
   * The prompt to generate the scene
   */
  prompt: string;
  /**
   * The style of the generated image. Hyperrealistic-likeness: preserves more likeness including hair styles; hyperrealistic: ideal for fun and creative scenes; Realistic: photorealistic with good text rendering; Stylistic: softer, more artistic Default value: `"hyperrealistic-likeness"`
   */
  style?:
    | "hyperrealistic-likeness"
    | "hyperrealistic"
    | "realistic"
    | "stylistic";
};
export type EaselAvatarOutput = {
  /**
   * The generated avatar image
   */
  image: Image;
};
export type EaselGifswapInput = {
  /**
   * The face image to swap onto the GIF
   */
  face_image: Image;
  /**
   * The GIF to apply face swap to. GIF size affects generation time.
   */
  gif_image: Image;
};
export type EaselGifswapOutput = {
  /**
   * The generated face-swapped GIF
   */
  image: Image;
};
export type EditImageInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type EffectInput = {
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Hulk"
    | "Venom"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url?: string | Blob | File;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
};
export type EffectOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ElementsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ElevenlabsAudioIsolationInput = {
  /**
   * URL of the audio file to isolate voice from
   */
  audio_url: string | Blob | File;
};
export type ElevenlabsAudioIsolationOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Timestamps for each word in the generated speech. Only returned if `timestamps` is set to True in the request.
   */
  timestamps?: Array<void>;
};
export type ElevenlabsSoundEffectsInput = {
  /**
   * The text describing the sound effect to generate
   */
  text: string;
  /**
   * Duration in seconds (0.5-22). If None, optimal duration will be determined from prompt.
   */
  duration_seconds?: number;
  /**
   * How closely to follow the prompt (0-1). Higher values mean less variation. Default value: `0.3`
   */
  prompt_influence?: number;
};
export type ElevenlabsSoundEffectsOutput = {
  /**
   * The generated sound effect audio file in MP3 format
   */
  audio: File;
};
export type ElevenlabsSpeechToTextInput = {
  /**
   * URL of the audio file to transcribe
   */
  audio_url: string | Blob | File;
  /**
   * Language code of the audio
   */
  language_code?: string;
  /**
   * Tag audio events like laughter, applause, etc. Default value: `true`
   */
  tag_audio_events?: boolean;
  /**
   * Whether to annotate who is speaking Default value: `true`
   */
  diarize?: boolean;
};
export type ElevenlabsSpeechToTextOutput = {
  /**
   * The full transcribed text
   */
  text: string;
  /**
   * Detected or specified language code
   */
  language_code: string;
  /**
   * Confidence in language detection
   */
  language_probability: number;
  /**
   * Word-level transcription details
   */
  words: Array<TranscriptionWord>;
};
export type ElevenlabsTtsMultilingualV2Input = {
  /**
   * The text to convert to speech
   */
  text: string;
  /**
   * The voice to use for speech generation Default value: `"Rachel"`
   */
  voice?: string;
  /**
   * Voice stability (0-1) Default value: `0.5`
   */
  stability?: number;
  /**
   * Similarity boost (0-1) Default value: `0.75`
   */
  similarity_boost?: number;
  /**
   * Style exaggeration (0-1)
   */
  style?: number;
  /**
   * Speech speed (0.7-1.2). Values below 1.0 slow down the speech, above 1.0 speed it up. Extreme values may affect quality. Default value: `1`
   */
  speed?: number;
  /**
   * Whether to return timestamps for each word in the generated speech
   */
  timestamps?: boolean;
  /**
   * The text that came before the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  previous_text?: string;
  /**
   * The text that comes after the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  next_text?: string;
  /**
   * Language code (ISO 639-1) used to enforce a language for the model. Currently only Turbo v2.5 and Flash v2.5 support language enforcement. For other models, an error will be returned if language code is provided.
   */
  language_code?: string;
};
export type ElevenlabsTtsMultilingualV2Output = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Timestamps for each word in the generated speech. Only returned if `timestamps` is set to True in the request.
   */
  timestamps?: Array<void>;
};
export type ElevenlabsTtsTurboV25Input = {
  /**
   * The text to convert to speech
   */
  text: string;
  /**
   * The voice to use for speech generation Default value: `"Rachel"`
   */
  voice?: string;
  /**
   * Voice stability (0-1) Default value: `0.5`
   */
  stability?: number;
  /**
   * Similarity boost (0-1) Default value: `0.75`
   */
  similarity_boost?: number;
  /**
   * Style exaggeration (0-1)
   */
  style?: number;
  /**
   * Speech speed (0.7-1.2). Values below 1.0 slow down the speech, above 1.0 speed it up. Extreme values may affect quality. Default value: `1`
   */
  speed?: number;
  /**
   * Whether to return timestamps for each word in the generated speech
   */
  timestamps?: boolean;
  /**
   * The text that came before the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  previous_text?: string;
  /**
   * The text that comes after the text of the current request. Can be used to improve the speech's continuity when concatenating together multiple generations or to influence the speech's continuity in the current generation.
   */
  next_text?: string;
  /**
   * Language code (ISO 639-1) used to enforce a language for the model. Currently only Turbo v2.5 and Flash v2.5 support language enforcement. For other models, an error will be returned if language code is provided.
   */
  language_code?: string;
};
export type ElevenlabsTtsTurboV25Output = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Timestamps for each word in the generated speech. Only returned if `timestamps` is set to True in the request.
   */
  timestamps?: Array<void>;
};
export type EraserInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * You can use this parameter to specify the type of the input mask from the list. 'manual' opttion should be used in cases in which the mask had been generated by a user (e.g. with a brush tool), and 'automatic' mask type should be used when mask had been generated by an algorithm like 'SAM'. Default value: `"manual"`
   */
  mask_type?: "manual" | "automatic";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, attempts to preserve the alpha channel of the input image.
   */
  preserve_alpha?: boolean;
};
export type EraserOutput = {
  /**
   * The generated image
   */
  image: Image;
};
export type EvfSamInput = {
  /**
   * The prompt to generate segmentation from.
   */
  prompt: string;
  /**
   * Areas to exclude from segmentation (will be subtracted from prompt results)
   */
  negative_prompt?: string;
  /**
   * Enable semantic level segmentation for body parts, background or multi objects
   */
  semantic_type?: boolean;
  /**
   * URL of the input image
   */
  image_url: string | Blob | File;
  /**
   * Output only the binary mask instead of masked image Default value: `true`
   */
  mask_only?: boolean;
  /**
   * Use GroundingDINO instead of SAM for segmentation
   */
  use_grounding_dino?: boolean;
  /**
   * Invert the mask (background becomes foreground and vice versa)
   */
  revert_mask?: boolean;
  /**
   * Apply Gaussian blur to the mask. Value determines kernel size (must be odd number)
   */
  blur_mask?: number;
  /**
   * Expand/dilate the mask by specified pixels
   */
  expand_mask?: number;
  /**
   * Fill holes in the mask using morphological operations
   */
  fill_holes?: boolean;
};
export type EvfSamOutput = {
  /**
   * The segmented output image
   */
  image: File;
};
export type ExpressionChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired facial expression to apply. Default value: `"sad"`
   */
  prompt?: string;
};
export type ExpressionChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ExtendOutput = {
  /**
   * The extended video
   */
  video: File;
};
export type ExtendVideoConditioningInput = {
  /**
   * URL of video to use as conditioning
   */
  video_url: string | Blob | File;
  /**
   * Frame number of the video from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num?: number;
  /**
   * Strength of the conditioning. 0.0 means no conditioning, 1.0 means full conditioning. Default value: `1`
   */
  strength?: number;
  /**
   * Whether to limit the number of frames used from the video. If True, the `max_num_frames` parameter will be used to limit the number of frames.
   */
  limit_num_frames?: boolean;
  /**
   * Maximum number of frames to use from the video. If None, all frames will be used. Default value: `1441`
   */
  max_num_frames?: number;
  /**
   * Whether to resample the video to a specific FPS. If True, the `target_fps` parameter will be used to resample the video.
   */
  resample_fps?: boolean;
  /**
   * Target FPS to resample the video to. Only relevant if `resample_fps` is True. Default value: `24`
   */
  target_fps?: number;
  /**
   * Whether to reverse the video. This is useful for tasks where the video conditioning should be applied in reverse order.
   */
  reverse_video?: boolean;
};
export type ExtendVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type ExtendVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type FaceEnhancementOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type FashionPhotoshootInput = {
  /**
   * The garment image to be used for the fashion shoot.
   */
  garment_image: Image;
  /**
   * The user's face image used for the fashion shoot.
   */
  face_image: Image;
  /**
   * The model's gender for the fashion shoot.
   */
  gender: "male" | "female";
  /**
   * The body size for the fashion shoot. Default value: `"S"`
   */
  body_size?: "XS" | "S" | "M" | "L" | "XL";
  /**
   * Sets the location / background for the fashion shoot. Contact hello@easelapps.ai if you require additional options. Default value: `"park"`
   */
  location?: "park" | "city";
};
export type FashionPhotoshootOutput = {
  /**
   * The resulting image after the fashion shoot.
   */
  image: Image;
};
export type FashionTryonInput = {
  /**
   * The reference person full body image to try clothes on
   */
  full_body_image: Image;
  /**
   * The clothing item to try on the person
   */
  clothing_image: Image;
  /**
   * The model's gender for the try-on. Default value: `"female"`
   */
  gender?: "male" | "female";
};
export type FashionTryonOutput = {
  /**
   * The generated try-on result
   */
  image: Image;
};
export type FashnTryonV15Input = {
  /**
   * URL or base64 of the model image
   */
  model_image: string;
  /**
   * URL or base64 of the garment image
   */
  garment_image: string;
  /**
   * Category of the garment to try-on. 'auto' will attempt to automatically detect the category of the garment. Default value: `"auto"`
   */
  category?: "tops" | "bottoms" | "one-pieces" | "auto";
  /**
   * Specifies the mode of operation. 'performance' mode is faster but may sacrifice quality, 'balanced' mode is a balance between speed and quality, and 'quality' mode is slower but produces higher quality results. Default value: `"balanced"`
   */
  mode?: "performance" | "balanced" | "quality";
  /**
   * Specifies the type of garment photo to optimize internal parameters for better performance. 'model' is for photos of garments on a model, 'flat-lay' is for flat-lay or ghost mannequin images, and 'auto' attempts to automatically detect the photo type. Default value: `"auto"`
   */
  garment_photo_type?: "auto" | "model" | "flat-lay";
  /**
   * Content moderation level for garment images. 'none' disables moderation, 'permissive' blocks only explicit content, 'conservative' also blocks underwear and swimwear. Default value: `"permissive"`
   */
  moderation_level?: "none" | "permissive" | "conservative";
  /**
   * Sets random operations to a fixed state. Use the same seed to reproduce results with the same inputs, or different seed to force different results. Default value: `42`
   */
  seed?: number;
  /**
   * Number of images to generate in a single run. Image generation has a random element in it, so trying multiple images at once increases the chances of getting a good result. Default value: `1`
   */
  num_samples?: number;
  /**
   * Disables human parsing on the model image. Default value: `true`
   */
  segmentation_free?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Output format of the generated images. 'png' is highest quality, while 'jpeg' is faster Default value: `"png"`
   */
  output_format?: "png" | "jpeg";
};
export type FashnTryonV15Output = {
  /**
   *
   */
  images: Array<File>;
};
export type FashnTryonV16Input = {
  /**
   * URL or base64 of the model image
   */
  model_image: string;
  /**
   * URL or base64 of the garment image
   */
  garment_image: string;
  /**
   * Category of the garment to try-on. 'auto' will attempt to automatically detect the category of the garment. Default value: `"auto"`
   */
  category?: "tops" | "bottoms" | "one-pieces" | "auto";
  /**
   * Specifies the mode of operation. 'performance' mode is faster but may sacrifice quality, 'balanced' mode is a balance between speed and quality, and 'quality' mode is slower but produces higher quality results. Default value: `"balanced"`
   */
  mode?: "performance" | "balanced" | "quality";
  /**
   * Specifies the type of garment photo to optimize internal parameters for better performance. 'model' is for photos of garments on a model, 'flat-lay' is for flat-lay or ghost mannequin images, and 'auto' attempts to automatically detect the photo type. Default value: `"auto"`
   */
  garment_photo_type?: "auto" | "model" | "flat-lay";
  /**
   * Content moderation level for garment images. 'none' disables moderation, 'permissive' blocks only explicit content, 'conservative' also blocks underwear and swimwear. Default value: `"permissive"`
   */
  moderation_level?: "none" | "permissive" | "conservative";
  /**
   * Sets random operations to a fixed state. Use the same seed to reproduce results with the same inputs, or different seed to force different results. Default value: `42`
   */
  seed?: number;
  /**
   * Number of images to generate in a single run. Image generation has a random element in it, so trying multiple images at once increases the chances of getting a good result. Default value: `1`
   */
  num_samples?: number;
  /**
   * Disables human parsing on the model image. Default value: `true`
   */
  segmentation_free?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Output format of the generated images. 'png' is highest quality, while 'jpeg' is faster Default value: `"png"`
   */
  output_format?: "png" | "jpeg";
};
export type FashnTryonV16Output = {
  /**
   *
   */
  images: Array<File>;
};
export type FastImageToVideoHailuo02Input = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type FfmpegApiComposeInput = {
  /**
   * List of tracks to be combined into the final media
   */
  tracks: Array<Track>;
};
export type FfmpegApiComposeOutput = {
  /**
   * URL of the processed video file
   */
  video_url: string | Blob | File;
  /**
   * URL of the video's thumbnail image
   */
  thumbnail_url: string | Blob | File;
};
export type FfmpegApiExtractFrameInput = {
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
  /**
   * Type of frame to extract: first, middle, or last frame of the video Default value: `"first"`
   */
  frame_type?: "first" | "middle" | "last";
};
export type FfmpegApiExtractFrameOutput = {
  /**
   *
   */
  images: Array<Image>;
};
export type FfmpegApiLoudnormInput = {
  /**
   * URL of the audio file to normalize
   */
  audio_url: string | Blob | File;
  /**
   * Integrated loudness target in LUFS. Default value: `-18`
   */
  integrated_loudness?: number;
  /**
   * Maximum true peak in dBTP. Default value: `-0.1`
   */
  true_peak?: number;
  /**
   * Loudness range target in LU Default value: `7`
   */
  loudness_range?: number;
  /**
   * Offset gain in dB applied before the true-peak limiter
   */
  offset?: number;
  /**
   * Use linear normalization mode (single-pass). If false, uses dynamic mode (two-pass for better quality).
   */
  linear?: boolean;
  /**
   * Treat mono input files as dual-mono for correct EBU R128 measurement on stereo systems
   */
  dual_mono?: boolean;
  /**
   * Return loudness measurement summary with the normalized audio
   */
  print_summary?: boolean;
  /**
   * Measured integrated loudness of input file in LUFS. Required for linear mode.
   */
  measured_i?: number;
  /**
   * Measured loudness range of input file in LU. Required for linear mode.
   */
  measured_lra?: number;
  /**
   * Measured true peak of input file in dBTP. Required for linear mode.
   */
  measured_tp?: number;
  /**
   * Measured threshold of input file in LUFS. Required for linear mode.
   */
  measured_thresh?: number;
};
export type FfmpegApiLoudnormOutput = {
  /**
   * Normalized audio file
   */
  audio: File;
  /**
   * Structured loudness measurement summary (if requested)
   */
  summary?: LoudnormSummary;
};
export type FfmpegApiMergeAudioVideoInput = {
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
  /**
   * URL of the audio file to use as the audio track
   */
  audio_url: string | Blob | File;
  /**
   * Offset in seconds for when the audio should start relative to the video
   */
  start_offset?: number;
};
export type FfmpegApiMergeAudioVideoOutput = {
  /**
   * Output video with merged audio.
   */
  video: File;
};
export type FfmpegApiMetadataInput = {
  /**
   * URL of the media file (video or audio) to analyze
   */
  media_url: string | Blob | File;
  /**
   * Whether to extract the start and end frames for videos. Note that when true the request will be slower.
   */
  extract_frames?: boolean;
};
export type FfmpegApiMetadataOutput = {
  /**
   * Metadata for the analyzed media file (either Video or Audio)
   */
  media: Video | Audio;
};
export type FfmpegApiWaveformInput = {
  /**
   * URL of the audio file to analyze
   */
  media_url: string | Blob | File;
  /**
   * Controls how many points are sampled per second of audio. Lower values (e.g. 1-2) create a coarser waveform, higher values (e.g. 4-10) create a more detailed one. Default value: `4`
   */
  points_per_second?: number;
  /**
   * Number of decimal places for the waveform values. Higher values provide more precision but increase payload size. Default value: `2`
   */
  precision?: number;
  /**
   * Size of the smoothing window. Higher values create a smoother waveform. Must be an odd number. Default value: `3`
   */
  smoothing_window?: number;
};
export type FfmpegApiWaveformOutput = {
  /**
   * Normalized waveform data as an array of values between -1 and 1. The number of points is determined by audio duration × points_per_second.
   */
  waveform: Array<number>;
  /**
   * Duration of the audio in seconds
   */
  duration: number;
  /**
   * Number of points in the waveform data
   */
  points: number;
  /**
   * Number of decimal places used in the waveform values
   */
  precision: number;
};
export type FILMImageInput = {
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg";
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the function will wait for images to be generated and uploaded before returning. This will increase the response time but ensures that the images are ready for use immediately without going through the CDN. Does not apply if output_type is 'video'.
   */
  sync_mode?: boolean;
};
export type FILMImageOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<Image>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: File;
};
export type FilmInput = {
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg";
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the function will wait for images to be generated and uploaded before returning. This will increase the response time but ensures that the images are ready for use immediately without going through the CDN. Does not apply if output_type is 'video'.
   */
  sync_mode?: boolean;
};
export type FilmOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<Image>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: File;
};
export type FilmVideoInput = {
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
};
export type FILMVideoInput = {
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
};
export type FilmVideoOutput = {
  /**
   * The generated video file with interpolated frames.
   */
  video: File;
};
export type FILMVideoOutput = {
  /**
   * The generated video file with interpolated frames.
   */
  video: File;
};
export type FinegrainEraserBboxInput = {
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * List of bounding box coordinates to erase (only one box prompt is supported)
   */
  box_prompts: Array<BoxPromptBase>;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserBboxOutput = {
  /**
   * The edited image with content erased
   */
  image: File;
  /**
   * Seed used for generation
   */
  used_seed: number;
};
export type FinegrainEraserInput = {
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * Text description of what to erase
   */
  prompt: string;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserMaskInput = {
  /**
   * URL of the image to edit
   */
  image_url: string | Blob | File;
  /**
   * URL of the mask image. Should be a binary mask where white (255) indicates areas to erase
   */
  mask_url: string | Blob | File;
  /**
   * Erase quality mode Default value: `"standard"`
   */
  mode?: "express" | "standard" | "premium";
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type FinegrainEraserMaskOutput = {
  /**
   * The edited image with content erased
   */
  image: File;
  /**
   * Seed used for generation
   */
  used_seed: number;
};
export type FinegrainEraserOutput = {
  /**
   * The edited image with content erased
   */
  image: File;
  /**
   * Seed used for generation
   */
  used_seed: number;
};
export type FLiteStandardInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Negative Prompt for generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type FLiteStandardOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FLiteTextureInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Negative Prompt for generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type FLiteTextureOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FloweditInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * Prompt of the image to be used.
   */
  source_prompt: string;
  /**
   * Prompt of the image to be made.
   */
  target_prompt: string;
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * Steps for which the model should run. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the source. Default value: `1.5`
   */
  src_guidance_scale?: number;
  /**
   * Guidance scale for target. Default value: `5.5`
   */
  tar_guidance_scale?: number;
  /**
   * Average step count Default value: `1`
   */
  n_avg?: number;
  /**
   * Control the strength of the edit Default value: `23`
   */
  n_max?: number;
  /**
   * Minimum step for improved style edits
   */
  n_min?: number;
};
export type FloweditOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type Flux1DevImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1DevImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1DevInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1DevOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1DevReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1DevReduxOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1KreaImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1KreaImageToImageOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1KreaInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1KreaOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1KreaReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1KreaReduxOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1SchnellInput = {
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1SchnellOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Flux1SchnellReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type Flux1SchnellReduxOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxControlLoraCannyImageToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type FluxControlLoraCannyImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxControlLoraCannyInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
};
export type FluxControlLoraCannyOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxControlLoraDepthImageToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type FluxControlLoraDepthImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxControlLoraDepthInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * If set to true, the input image will be preprocessed to extract depth information.
   * This is useful for generating depth maps from images. Default value: `true`
   */
  preprocess_depth?: boolean;
};
export type FluxControlLoraDepthOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxDevImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxDevImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxDevInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxDevOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxGeneralInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * IP-Adapter to use for image generation.
   */
  ip_adapters?: Array<IPAdapter>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  real_cfg_scale?: number;
  /**
   * Uses classical CFG as in SD1.5, SDXL, etc. Increases generation times and price when set to be true.
   * If using XLabs IP-Adapter v1, this will be turned on!.
   */
  use_real_cfg?: boolean;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: "sgm_uniform";
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
};
export type FluxGeneralOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextDevInput = {
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
};
export type FluxKontextDevOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
};
export type FluxKontextLoraInpaintInput = {
  /**
   * The URL of the image to be inpainted.
   */
  image_url: string | Blob | File;
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the reference image for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The URL of the mask for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
};
export type FluxKontextLoraInpaintOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextLoraInput = {
  /**
   * The URL of the image to edit.
   *
   * Max width: 14142px, Max height: 14142px, Timeout: 20s
   */
  image_url: string | Blob | File;
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
};
export type FluxKontextLoraOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextLoraTextToImageInput = {
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxKontextLoraTextToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextMultiInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_urls: Array<string>;
};
export type FluxKontextOutput = {
  /**
   * The generated image files info.
   */
  images: Array<fal__toolkit__image__image__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKontextTrainerInput = {
  /**
   * URL to the input data zip archive.
   *
   * The zip should contain pairs of images. The images should be named:
   *
   * ROOT_start.EXT and ROOT_end.EXT
   * For example:
   * photo_start.jpg and photo_end.jpg
   *
   * The zip can also contain a text file for each image pair. The text file should be named:
   * ROOT.txt
   * For example:
   * photo.txt
   *
   * This text file can be used to specify the edit instructions for the image pair.
   *
   * If no text file is provided, the default_caption will be used.
   *
   * If no default_caption is provided, the training will fail.
   */
  image_data_url: string | Blob | File;
  /**
   * Number of steps to train for Default value: `1000`
   */
  steps?: number;
  /**
   *  Default value: `0.0001`
   */
  learning_rate?: number;
  /**
   * Default caption to use when caption files are missing. If None, missing captions will cause an error.
   */
  default_caption?: string;
  /**
   * Dictates the naming scheme for the output weights Default value: `"fal"`
   */
  output_lora_format?: "fal" | "comfy";
};
export type FluxKontextTrainerOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the configuration file for the trained model.
   */
  config_file: File;
};
export type FluxKreaImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxKreaImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxKreaLoraImageToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type FluxKreaLoraImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaLoraInpaintingInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
};
export type FluxKreaLoraInpaintingOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaLoraInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type FluxKreaLoraOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaLoraStreamInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type FluxKreaLoraStreamOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxKreaReduxOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxKreaTrainerInput = {
  /**
   * URL to zip archive with images. Try to use at least 4 images in general the more the better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   */
  images_data_url: string | Blob | File;
  /**
   * Trigger word to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_word will be used instead of captions. If captions are the trigger word will not be used.
   */
  trigger_word?: string;
  /**
   * If True segmentation masks will be used in the weight the training loss. For people a face mask is used if possible. Default value: `true`
   */
  create_masks?: boolean;
  /**
   * Number of steps to train the LoRA on.
   */
  steps?: number;
  /**
   * If True, the training will be for a style. This will deactivate segmentation, captioning and will use trigger word instead. Use the trigger word to specify the style.
   */
  is_style?: boolean;
  /**
   * Specifies whether the input data is already in a processed format. When set to False (default), the system expects raw input where image files and their corresponding caption files share the same name (e.g., 'photo.jpg' and 'photo.txt'). Set to True if your data is already in a preprocessed format.
   */
  is_input_format_already_preprocessed?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
};
export type FluxKreaTrainerOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the training configuration file.
   */
  config_file: File;
  /**
   * URL to the preprocessed images.
   */
  debug_preprocessed_output?: File;
};
export type FluxLoraCannyInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for canny input
   */
  image_url: string | Blob | File;
};
export type FluxLoraCannyOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxLoraFastTrainingInput = {
  /**
   * URL to zip archive with images. Try to use at least 4 images in general the more the better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   */
  images_data_url: string | Blob | File;
  /**
   * Trigger word to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_word will be used instead of captions. If captions are the trigger word will not be used.
   */
  trigger_word?: string;
  /**
   * If True segmentation masks will be used in the weight the training loss. For people a face mask is used if possible. Default value: `true`
   */
  create_masks?: boolean;
  /**
   * Number of steps to train the LoRA on.
   */
  steps?: number;
  /**
   * If True, the training will be for a style. This will deactivate segmentation, captioning and will use trigger word instead. Use the trigger word to specify the style.
   */
  is_style?: boolean;
  /**
   * Specifies whether the input data is already in a processed format. When set to False (default), the system expects raw input where image files and their corresponding caption files share the same name (e.g., 'photo.jpg' and 'photo.txt'). Set to True if your data is already in a preprocessed format.
   */
  is_input_format_already_preprocessed?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
};
export type FluxLoraFastTrainingOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the training configuration file.
   */
  config_file: File;
  /**
   * URL to the preprocessed images.
   */
  debug_preprocessed_output?: File;
};
export type FluxLoraFillInput = {
  /**
   * The prompt to generate an image from. Default value: `""`
   */
  prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for fill operation
   */
  image_url: string | Blob | File;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
  /**
   * Specifies whether to paste-back the original image onto to the non-inpainted areas of the output Default value: `true`
   */
  paste_back?: boolean;
  /**
   * Use an image fill input to fill in particular images into the masked area.
   */
  fill_image?: ImageFillInput;
  /**
   * Resizes the image back to the original size. Use when you wish to preserve the exact image size as the originally provided image.
   */
  resize_to_original?: boolean;
};
export type FluxLoraFillOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxLoraInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type FluxLoraOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxLoraPortraitTrainerInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   *
   * The captions can include a special string `[trigger]`. If a trigger_word is specified, it will replace `[trigger]` in the captions.
   */
  images_data_url: string | Blob | File;
  /**
   * Trigger phrase to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_work will be used instead of captions. If captions are provided, the trigger word will replace the `[trigger]` string in the captions.
   */
  trigger_phrase?: string;
  /**
   * Learning rate to use for training. Default value: `0.00009`
   */
  learning_rate?: number;
  /**
   * Number of steps to train the LoRA on. Default value: `2500`
   */
  steps?: number;
  /**
   * If True, multiresolution training will be used. Default value: `true`
   */
  multiresolution_training?: boolean;
  /**
   * If True, the subject will be cropped from the image. Default value: `true`
   */
  subject_crop?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
  /**
   * URL to a checkpoint to resume training from. Default value: `""`
   */
  resume_from_checkpoint?: string;
  /**
   * If True, masks will be created for the subject.
   */
  create_masks?: boolean;
};
export type FluxLoraPortraitTrainerOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the training configuration file.
   */
  config_file: File;
};
export type FluxLoraStreamInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type FluxLoraStreamOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProCannyControlFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProCannyControlInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
};
export type FluxProDepthControlFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the depth map from.
   */
  control_image_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProDepthControlInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the depth map from.
   */
  control_image_url: string | Blob | File;
};
export type FluxProFillFinetunedInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProFillInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
};
export type FluxProKontextInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
};
export type FluxProKontextMaxInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
};
export type FluxProKontextMaxMultiInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_urls: Array<string>;
};
export type FluxProKontextMaxMultiOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProKontextMaxOutput = {
  /**
   * The generated image files info.
   */
  images: Array<fal__toolkit__image__image__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProKontextMaxTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
};
export type FluxProKontextMaxTextToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProKontextMultiInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * Image prompt for the omni model.
   */
  image_urls: Array<string>;
};
export type FluxProKontextMultiOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProKontextOutput = {
  /**
   * The generated image files info.
   */
  images: Array<fal__toolkit__image__image__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProKontextTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `"1:1"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
};
export type FluxProKontextTextToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProOutpaintInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The image URL to expand using outpainting
   */
  image_url: string | Blob | File;
  /**
   * Pixels to expand at the top
   */
  expand_top?: number;
  /**
   * Pixels to expand at the bottom
   */
  expand_bottom?: number;
  /**
   * Pixels to expand on the left
   */
  expand_left?: number;
  /**
   * Pixels to expand on the right
   */
  expand_right?: number;
};
export type FluxProPlusTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
};
export type FluxProTextToImageFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
};
export type FluxProTrainerInput = {
  /**
   * URL to the training data
   */
  data_url: string | Blob | File;
  /**
   * Determines the finetuning approach based on your concept Default value: `"character"`
   */
  mode?: "character" | "product" | "style" | "general";
  /**
   * Descriptive note to identify your fine-tune since names are UUIDs. Will be displayed in finetune_details.
   */
  finetune_comment: string;
  /**
   * Defines training duration Default value: `300`
   */
  iterations?: number;
  /**
   * Learning rate for training. Lower values may be needed for certain scenarios. Default is 1e-5 for full and 1e-4 for LoRA.
   */
  learning_rate?: number;
  /**
   * The speed priority will improve training and inference speed Default value: `"quality"`
   */
  priority?: "speed" | "quality" | "high_res_only";
  /**
   * Enables/disables automatic image captioning Default value: `true`
   */
  captioning?: boolean;
  /**
   * Unique word/phrase that will be used in the captions, to reference the newly introduced concepts Default value: `"TOK"`
   */
  trigger_word?: string;
  /**
   * Choose between 32 and 16. A lora_rank of 16 can increase training efficiency and decrease loading times. Default value: `32`
   */
  lora_rank?: number;
  /**
   * Choose between 'full' for a full finetuning + post hoc extraction of the trained weights into a LoRA or 'lora' for a raw LoRA training Default value: `"full"`
   */
  finetune_type?: "full" | "lora";
};
export type FluxProTrainerOutput = {
  /**
   * References your specific model
   */
  finetune_id: string;
};
export type FluxProUltraTextToImageFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProUltraTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
};
export type FluxProV11Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
};
export type FluxProV11Output = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV11UltraFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProV11UltraFinetunedOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV11UltraInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The aspect ratio of the generated image. Default value: `16:9`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21"
    | string;
  /**
   * Generate less processed, more natural-looking images.
   */
  raw?: boolean;
};
export type FluxProV11UltraOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV1CannyFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `30`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProV1CannyFinetunedOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV1CannyInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the Canny edge map from.
   */
  control_image_url: string | Blob | File;
};
export type FluxProV1CannyOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV1DepthFinetunedInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `15`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the depth map from.
   */
  control_image_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProV1DepthFinetunedOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV1DepthInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The control image URL to generate the depth map from.
   */
  control_image_url: string | Blob | File;
};
export type FluxProV1DepthOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxProV1FillFinetunedInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 5 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * References your specific model
   */
  finetune_id: string;
  /**
   * Controls finetune influence.
   * Increase this value if your target concept isn't showing up strongly enough.
   * The optimal setting depends on your finetune and prompt
   */
  finetune_strength: number;
};
export type FluxProV1FillFinetunedOutput = {
  /**
   * The generated image files info.
   */
  images: Array<registry__image__fast_sdxl__models__Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FluxSchnellReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type FluxSchnellReduxOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type FrameInput = {
  /**
   * URL of the video file to use as the video track
   */
  video_url: string | Blob | File;
  /**
   * Type of frame to extract: first, middle, or last frame of the video Default value: `"first"`
   */
  frame_type?: "first" | "middle" | "last";
};
export type FrameOutput = {
  /**
   *
   */
  images: Array<Image>;
};
export type FramepackF1Input = {
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"480p"`
   */
  resolution?: "720p" | "480p";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `1`
   */
  cfg_scale?: number;
  /**
   * Guidance scale for the generation. Default value: `10`
   */
  guidance_scale?: number;
  /**
   * The number of frames to generate. Default value: `180`
   */
  num_frames?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type FramepackF1Output = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type FramepackFlf2vInput = {
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"480p"`
   */
  resolution?: "720p" | "480p";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `1`
   */
  cfg_scale?: number;
  /**
   * Guidance scale for the generation. Default value: `10`
   */
  guidance_scale?: number;
  /**
   * The number of frames to generate. Default value: `240`
   */
  num_frames?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the end image input.
   */
  end_image_url: string | Blob | File;
  /**
   * Determines the influence of the final frame on the generated video. Higher values result in the output being more heavily influenced by the last frame. Default value: `0.8`
   */
  strength?: number;
};
export type FramepackFlf2vOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type FramepackInput = {
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"480p"`
   */
  resolution?: "720p" | "480p";
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `1`
   */
  cfg_scale?: number;
  /**
   * Guidance scale for the generation. Default value: `10`
   */
  guidance_scale?: number;
  /**
   * The number of frames to generate. Default value: `180`
   */
  num_frames?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type FramepackOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type FrenchOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type GeminiFlashEditInput = {
  /**
   * The prompt for image generation or editing
   */
  prompt: string;
  /**
   * Optional URL of an input image for editing. If not provided, generates a new image.
   */
  image_url: string | Blob | File;
};
export type GeminiFlashEditMultiInput = {
  /**
   * The prompt for image generation or editing
   */
  prompt: string;
  /**
   * List of URLs of input images for editing
   */
  input_image_urls: Array<string>;
};
export type GeminiFlashEditMultiOutput = {
  /**
   * The generated or edited image
   */
  image: Image;
  /**
   * Text description or response from Gemini
   */
  description: string;
};
export type GeminiFlashEditOutput = {
  /**
   * The generated or edited image
   */
  image: Image;
  /**
   * Text description or response from Gemini
   */
  description: string;
};
export type GenFillInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The URL of the binary mask image that represents the area that will be cleaned.
   */
  mask_url: string | Blob | File;
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of Images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type GenFillOutput = {
  /**
   * Generated Images
   */
  images: Array<Image>;
};
export type GhiblifyInput = {
  /**
   * The URL of the image to upscale.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for the upscale. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type GhiblifyOutput = {
  /**
   * The URL of the generated image.
   */
  image: Image;
};
export type GlowInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Glow intensity Default value: `1`
   */
  glow_intensity?: number;
  /**
   * Glow blur radius Default value: `5`
   */
  glow_radius?: number;
};
export type GlowOutput = {
  /**
   * The processed images with glow effect
   */
  images: Array<Image>;
};
export type GotOcrV2Input = {
  /**
   * URL of images.
   */
  input_image_urls?: Array<string>;
  /**
   * Generate the output in formatted mode.
   */
  do_format?: boolean;
  /**
   * Use provided images to generate a single output.
   */
  multi_page?: boolean;
};
export type GotOcrV2Output = {
  /**
   * Generated output
   */
  outputs: Array<string>;
};
export type GptImage1EditImageByokInput = {
  /**
   * The URLs of the images to use as a reference for the generation.
   */
  image_urls: Array<string>;
  /**
   * The prompt to edit the image from.
   */
  prompt: string;
  /**
   * The size of the image to generate. Default value: `"auto"`
   */
  image_size?: "auto" | "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The quality of the image to generate. Default value: `"auto"`
   */
  quality?: "auto" | "low" | "medium" | "high";
  /**
   * How hard to try to preserve distinctive features from the input. Default value: `"low"`
   */
  input_fidelity?: "low" | "high";
  /**
   * The OpenAI API key to use for the image generation. This endpoint is currently powered by bring-your-own-key system.
   */
  openai_api_key: string;
};
export type GptImage1EditImageByokOutput = {
  /**
   * The edited images.
   */
  images: Array<Image>;
};
export type GptImage1TextToImageByokInput = {
  /**
   * The prompt to generate the image from.
   */
  prompt: string;
  /**
   * The size of the image to generate. Default value: `"auto"`
   */
  image_size?: "auto" | "1024x1024" | "1536x1024" | "1024x1536";
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The quality of the image to generate. Default value: `"auto"`
   */
  quality?: "auto" | "low" | "medium" | "high";
  /**
   * The background of the image to generate. Default value: `"auto"`
   */
  background?: "auto" | "transparent" | "opaque";
  /**
   * The OpenAI API key to use for the image generation. This endpoint is currently powered by bring-your-own-key system.
   */
  openai_api_key: string;
};
export type GptImage1TextToImageByokOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
};
export type GrainInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Film grain intensity Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
};
export type GrainOutput = {
  /**
   * The processed images with grain effect
   */
  images: Array<Image>;
};
export type GuidanceInput = {
  /**
   * The image that should be used as guidance, in base64 format, with the method defined in guidance_method_1. Accepted formats are jpeg, jpg, png, webp. Maximum file size 12MB. If more then one guidance method is used, all guidance images must be of the same aspect ratio, and this will be the aspect ratio of the generated results. If guidance_method_1 is selected, an image must be provided.
   */
  image_url: string | Blob | File;
  /**
   * Which guidance type you would like to include in the generation. Up to 4 guidance methods can be combined during a single inference. This parameter is optional.
   */
  method?:
    | "controlnet_canny"
    | "controlnet_depth"
    | "controlnet_recoloring"
    | "controlnet_color_grid";
  /**
   * Impact of the guidance. Default value: `1`
   */
  scale?: number;
};
export type HairChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired hair style to apply. Default value: `"bald"`
   */
  prompt?: string;
};
export type HairChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type HidreamE11Input = {
  /**
   * The instruction to edit the image.
   */
  edit_instruction?: string;
  /**
   * The description of the target image after your edits have been made. Leave this blank to allow the model to use its own imagination.
   */
  target_image_description?: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"low resolution, blur"`
   */
  negative_prompt?: string;
  /**
   * URL of an input image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your initial image when looking for a related image to show you. Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type HidreamE11Output = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type HidreamE1FullInput = {
  /**
   * The instruction to edit the image.
   */
  edit_instruction?: string;
  /**
   * The description of the target image after your edits have been made. Leave this blank to allow the model to use its own imagination.
   */
  target_image_description?: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `"low resolution, blur"`
   */
  negative_prompt?: string;
  /**
   * URL of an input image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your initial image when looking for a related image to show you. Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type HidreamE1FullOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type HidreamI1DevInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type HidreamI1DevOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type HidreamI1FastInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `16`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type HidreamI1FastOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type HidreamI1FullImageToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image. Setting to None uses the input image's size.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
  /**
   * The image URL to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * Denoising strength for image-to-image generation. Default value: `0.75`
   */
  strength?: number;
};
export type HidreamI1FullImageToImageOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type HidreamI1FullInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
};
export type HindiOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type Hunyuan_worldImageToWorldInput = {
  /**
   * The URL of the image to convert to a world.
   */
  image_url: string | Blob | File;
  /**
   * Labels for the first foreground object.
   */
  labels_fg1: string;
  /**
   * Labels for the second foreground object.
   */
  labels_fg2: string;
  /**
   * Classes to use for the world generation.
   */
  classes: string;
  /**
   * Whether to export DRC (Dynamic Resource Configuration).
   */
  export_drc?: boolean;
};
export type Hunyuan_worldImageToWorldOutput = {
  /**
   * The generated world.
   */
  world_file: File;
};
export type Hunyuan_worldInput = {
  /**
   * The URL of the image to convert to a panorama.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to use for the panorama generation.
   */
  prompt: string;
};
export type Hunyuan_worldOutput = {
  /**
   * The generated panorama image.
   */
  image: Image;
};
export type Hunyuan3DInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV21Input = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV21Output = {
  /**
   * Generated 3D object.
   */
  model_glb: File;
  /**
   * Generated 3D object with PBR materials.
   */
  model_glb_pbr?: File;
  /**
   * Generated 3D object assets zip.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2Input = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2MiniInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2MiniOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2MiniTurboInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2MiniTurboOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2MultiViewInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  front_image_url: string | Blob | File;
  /**
   * URL of image to use while generating the 3D model.
   */
  back_image_url: string | Blob | File;
  /**
   * URL of image to use while generating the 3D model.
   */
  left_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2MultiViewOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2MultiViewTurboInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  front_image_url: string | Blob | File;
  /**
   * URL of image to use while generating the 3D model.
   */
  back_image_url: string | Blob | File;
  /**
   * URL of image to use while generating the 3D model.
   */
  left_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2MultiViewTurboOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2Output = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type Hunyuan3dV2TurboInput = {
  /**
   * URL of image to use while generating the 3D model.
   */
  input_image_url: string | Blob | File;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for the model. Default value: `7.5`
   */
  guidance_scale?: number;
  /**
   * Octree resolution for the model. Default value: `256`
   */
  octree_resolution?: number;
  /**
   * If set true, textured mesh will be generated and the price charged would be 3 times that of white mesh.
   */
  textured_mesh?: boolean;
};
export type Hunyuan3dV2TurboOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type HunyuanAvatarInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * The URL of the reference image.
   */
  image_url: string | Blob | File;
  /**
   * Text prompt describing the scene. Default value: `"A cat is singing."`
   */
  text?: string;
  /**
   * Number of video frames to generate at 25 FPS. If greater than the input audio length, it will capped to the length of the input audio. Default value: `129`
   */
  num_frames?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
  /**
   * Random seed for generation.
   */
  seed?: number;
};
export type HunyuanAvatarOutput = {
  /**
   * The generated video with the avatar animation.
   */
  video: File;
};
export type HunyuanCustomInput = {
  /**
   * Text prompt for video generation (max 500 characters).
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"Aerial view, aerial view, overexposed, low quality, deformation, a poor composition, bad hands, bad teeth, bad eyes, bad limbs, distortion, blurring, text, subtitles, static, picture, black border."`
   */
  negative_prompt?: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to run. Lower gets faster results, higher gets better results. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. 720p generations cost 1.5x more than 480p generations. Default value: `"512p"`
   */
  resolution?: "512p" | "720p";
  /**
   * The frames per second of the generated video. Default value: `25`
   */
  fps?: number;
  /**
   * Classifier-Free Guidance scale for the generation. Default value: `7.5`
   */
  cfg_scale?: number;
  /**
   * The number of frames to generate. Default value: `129`
   */
  num_frames?: number;
  /**
   * Whether to enable prompt expansion. Default value: `true`
   */
  enable_prompt_expansion?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type HunyuanCustomOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type HunyuanPortraitInput = {
  /**
   * The URL of the driving video.
   */
  video_url: string | Blob | File;
  /**
   * The URL of the source image.
   */
  image_url: string | Blob | File;
  /**
   * Random seed for generation. If None, a random seed will be used.
   */
  seed?: number;
  /**
   * Whether to use ArcFace for face recognition. Default value: `true`
   */
  use_arcface?: boolean;
};
export type HunyuanPortraitOutput = {
  /**
   * The generated video with the portrait animation.
   */
  video: File;
};
export type HunyuanVideoImageToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "720p";
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129";
  /**
   * Turning on I2V Stability reduces hallucination but also reduces motion.
   */
  i2v_stability?: boolean;
};
export type HunyuanVideoImageToVideoOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type HunyuanVideoImg2vidLoraInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The URL to the image to generate the video from. The image must be 960x544 or it will get cropped and resized to that size.
   */
  image_url: string | Blob | File;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
};
export type HunyuanVideoImg2vidLoraOutput = {
  /**
   * The generated video
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type HunyuanVideoLoraInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
};
export type HunyuanVideoLoraOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type HunyuanVideoLoraTrainingInput = {
  /**
   * URL to zip archive with images. Try to use at least 4 images in general the more the better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image file it corresponds to.
   */
  images_data_url: string | Blob | File;
  /**
   * Number of steps to train the LoRA on.
   */
  steps: number;
  /**
   * The trigger word to use. Default value: `""`
   */
  trigger_word?: string;
  /**
   * Learning rate to use for training. Default value: `0.0001`
   */
  learning_rate?: number;
  /**
   * Whether to generate captions for the images. Default value: `true`
   */
  do_caption?: boolean;
  /**
   * The format of the archive. If not specified, the format will be inferred from the URL.
   */
  data_archive_format?: string;
};
export type HunyuanVideoLoraTrainingOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the lora configuration file.
   */
  config_file: File;
};
export type HunyuanVideoLoraVideoToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * URL of the video
   */
  video_url: string | Blob | File;
  /**
   * Strength of video-to-video Default value: `0.75`
   */
  strength?: number;
};
export type HunyuanVideoLoraVideoToVideoOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type HunyuanVideoVideoToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The number of inference steps to run. Lower gets faster results, higher gets better results. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The seed to use for generating the video.
   */
  seed?: number;
  /**
   * By default, generations are done with 35 steps. Pro mode does 55 steps which results in higher quality videos but will take more time and cost 2x more billing units.
   */
  pro_mode?: boolean;
  /**
   * The aspect ratio of the video to generate. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The resolution of the video to generate. Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * The number of frames to generate. Default value: `"129"`
   */
  num_frames?: "129" | "85";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * URL of the video input.
   */
  video_url: string | Blob | File;
  /**
   * Strength for Video-to-Video Default value: `0.85`
   */
  strength?: number;
};
export type HunyuanVideoVideoToVideoOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generating the video.
   */
  seed: number;
};
export type Hyper3dRodinInput = {
  /**
   * A textual prompt to guide model generation. Required for Text-to-3D mode. Optional for Image-to-3D mode. Default value: `""`
   */
  prompt?: string;
  /**
   * URL of images to use while generating the 3D model. Required for Image-to-3D mode. Optional for Text-to-3D mode.
   */
  input_image_urls?: Array<string>;
  /**
   * For fuse mode, One or more images are required.It will generate a model by extracting and fusing features of objects from multiple images.For concat mode, need to upload multiple multi-view images of the same object and generate the model.(You can upload multi-view images in any order, regardless of the order of view.) Default value: `"concat"`
   */
  condition_mode?: "fuse" | "concat";
  /**
   * Seed value for randomization, ranging from 0 to 65535. Optional.
   */
  seed?: number;
  /**
   * Format of the geometry file. Possible values: glb, usdz, fbx, obj, stl. Default is glb. Default value: `"glb"`
   */
  geometry_file_format?: "glb" | "usdz" | "fbx" | "obj" | "stl";
  /**
   * Material type. Possible values: PBR, Shaded. Default is PBR. Default value: `"PBR"`
   */
  material?: "PBR" | "Shaded";
  /**
   * Generation quality. Possible values: high, medium, low, extra-low. Default is medium. Default value: `"medium"`
   */
  quality?: "high" | "medium" | "low" | "extra-low";
  /**
   * Whether to export the model using hyper mode. Default is false.
   */
  use_hyper?: boolean;
  /**
   * Tier of generation. For Rodin Sketch, set to Sketch. For Rodin Regular, set to Regular. Default value: `"Regular"`
   */
  tier?: "Regular" | "Sketch";
  /**
   * When generating the human-like model, this parameter control the generation result to T/A Pose.
   */
  TAPose?: boolean;
  /**
   * An array that specifies the dimensions and scaling factor of the bounding box. Typically, this array contains 3 elements, Length(X-axis), Width(Y-axis) and Height(Z-axis).
   */
  bbox_condition?: Array<number>;
  /**
   * Generation add-on features. Default is []. Possible values are HighPack. The HighPack option will provide 4K resolution textures instead of the default 1K, as well as models with high-poly. It will cost triple the billable units.
   */
  addons?: "HighPack";
};
export type Hyper3dRodinOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
  /**
   * Generated textures for the 3D object.
   */
  textures: Array<Image>;
};
export type I2VDirectorOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type I2VLiveOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type I2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type IdeogramCharacterEditInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes. Default value: `"AUTO"`
   */
  style?: "AUTO" | "REALISTIC" | "FICTION";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
};
export type IdeogramCharacterEditOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramCharacterInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes. Default value: `"AUTO"`
   */
  style?: "AUTO" | "REALISTIC" | "FICTION";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
};
export type IdeogramCharacterOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramCharacterRemixInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes. Default value: `"AUTO"`
   */
  style?: "AUTO" | "REALISTIC" | "FICTION";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * A set of images to use as character references. Currently only 1 image is supported, rest will be ignored. (maximum total size 10MB across all character references). The images should be in JPEG, PNG or WebP format
   */
  reference_image_urls: Array<string>;
};
export type IdeogramCharacterRemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramUpscaleInput = {
  /**
   * The image URL to upscale
   */
  image_url: string | Blob | File;
  /**
   * The prompt to upscale the image with Default value: `""`
   */
  prompt?: string;
  /**
   * The resemblance of the upscaled image to the original image Default value: `50`
   */
  resemblance?: number;
  /**
   * The detail of the upscaled image Default value: `50`
   */
  detail?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality.
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramUpscaleOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2aInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2aOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2aRemixInput = {
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2aRemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2aTurboInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2aTurboOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2aTurboRemixInput = {
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2aTurboRemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2EditInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2EditOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2Input = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * A negative prompt to avoid in the generated image Default value: `""`
   */
  negative_prompt?: string;
};
export type IdeogramV2Output = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2RemixInput = {
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2RemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2TurboEditInput = {
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2TurboEditOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2TurboInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * A negative prompt to avoid in the generated image Default value: `""`
   */
  negative_prompt?: string;
};
export type IdeogramV2TurboOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV2TurboRemixInput = {
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type IdeogramV2TurboRemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV3EditInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The prompt to fill the masked part of the image.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The mask URL to inpaint the image. Needs to match the dimensions of the input image.
   */
  mask_url: string | Blob | File;
};
export type IdeogramV3EditOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV3Input = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   *
   */
  prompt: string;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
};
export type IdeogramV3Output = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV3ReframeInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The image URL to reframe
   */
  image_url: string | Blob | File;
  /**
   * The resolution for the reframed output image
   */
  image_size:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type IdeogramV3ReframeOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV3RemixInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * The resolution of the generated image Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Description of what to exclude from an image. Descriptions in the prompt take precedence to descriptions in the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
};
export type IdeogramV3RemixOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type IdeogramV3ReplaceBackgroundInput = {
  /**
   * A set of images to use as style references (maximum total size 10MB across all style references). The images should be in JPEG, PNG or WebP format
   */
  image_urls?: Array<string>;
  /**
   * The rendering speed to use. Default value: `"BALANCED"`
   */
  rendering_speed?: "TURBO" | "BALANCED" | "QUALITY";
  /**
   * A color palette for generation, must EITHER be specified via one of the presets (name) or explicitly via hexadecimal representations of the color with optional weights (members)
   */
  color_palette?: ColorPalette;
  /**
   * A list of 8 character hexadecimal codes representing the style of the image. Cannot be used in conjunction with style_reference_images or style
   */
  style_codes?: Array<string>;
  /**
   * The style type to generate with. Cannot be used with style_codes.
   */
  style?: "AUTO" | "GENERAL" | "REALISTIC" | "DESIGN";
  /**
   * Determine if MagicPrompt should be used in generating the request or not. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Cyber punk city with neon lights and skyscrappers
   */
  prompt: string;
  /**
   * The image URL whose background needs to be replaced
   */
  image_url: string | Blob | File;
};
export type IdeogramV3ReplaceBackgroundOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type Image2svgInput = {
  /**
   * The image to convert to SVG
   */
  image_url: string | Blob | File;
  /**
   * Choose between color or binary (black and white) output Default value: `"color"`
   */
  colormode?: "color" | "binary";
  /**
   * Hierarchical mode: stacked or cutout Default value: `"stacked"`
   */
  hierarchical?: "stacked" | "cutout";
  /**
   * Mode: spline (curved) or polygon (straight lines) Default value: `"spline"`
   */
  mode?: "spline" | "polygon";
  /**
   * Filter out small speckles and noise Default value: `4`
   */
  filter_speckle?: number;
  /**
   * Color quantization level Default value: `6`
   */
  color_precision?: number;
  /**
   * Layer difference threshold for hierarchical mode Default value: `16`
   */
  layer_difference?: number;
  /**
   * Corner detection threshold in degrees Default value: `60`
   */
  corner_threshold?: number;
  /**
   * Length threshold for curves/lines Default value: `4`
   */
  length_threshold?: number;
  /**
   * Maximum number of iterations for optimization Default value: `10`
   */
  max_iterations?: number;
  /**
   * Splice threshold for joining paths Default value: `45`
   */
  splice_threshold?: number;
  /**
   * Decimal precision for path coordinates Default value: `3`
   */
  path_precision?: number;
};
export type Image2svgOutput = {
  /**
   * The converted SVG file
   */
  images: Array<File>;
};
export type ImageConditioningInput = {
  /**
   * URL of image to use as conditioning
   */
  image_url: string | Blob | File;
  /**
   * Frame number of the image from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num: number;
};
export type ImageEditingAgeProgressionInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The age change to apply. Default value: `"20 years older"`
   */
  prompt?: string;
};
export type ImageEditingAgeProgressionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingBabyVersionInput = {
  /**
   * URL of the image to transform into a baby version.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingBabyVersionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingBackgroundChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired background to apply. Default value: `"beach sunset with palm trees"`
   */
  prompt?: string;
};
export type ImageEditingBackgroundChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingBroccoliHaircutInput = {
  /**
   * URL of the image to apply broccoli haircut style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingBroccoliHaircutOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingCartoonifyInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingCartoonifyOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingColorCorrectionInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingColorCorrectionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingExpressionChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired facial expression to apply. Default value: `"sad"`
   */
  prompt?: string;
};
export type ImageEditingExpressionChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingFaceEnhancementInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingFaceEnhancementOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingHairChangeInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The desired hair style to apply. Default value: `"bald"`
   */
  prompt?: string;
};
export type ImageEditingHairChangeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingObjectRemovalInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Specify which objects to remove from the image. Default value: `"background people"`
   */
  prompt?: string;
};
export type ImageEditingObjectRemovalOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingPhotoRestorationInput = {
  /**
   * URL of the old or damaged photo to restore.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingPhotoRestorationOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingPlushieStyleInput = {
  /**
   * URL of the image to convert to plushie style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingPlushieStyleOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingProfessionalPhotoInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingProfessionalPhotoOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingRealismInput = {
  /**
   * URL of the image to enhance with realism details.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.6`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingRealismOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingReframeInput = {
  /**
   * URL of the old or damaged photo to restore.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The desired aspect ratio for the reframed image. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingReframeOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingRetouchInput = {
  /**
   * URL of the image to retouch.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingRetouchOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingSceneCompositionInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Describe the scene where you want to place the subject. Default value: `"enchanted forest"`
   */
  prompt?: string;
};
export type ImageEditingSceneCompositionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingStyleTransferInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The artistic style to apply. Default value: `"Van Gogh's Starry Night"`
   */
  prompt?: string;
};
export type ImageEditingStyleTransferOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingTextRemovalInput = {
  /**
   * URL of the image containing text to be removed.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingTextRemovalOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingTimeOfDayInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The time of day to transform the scene to. Default value: `"golden hour"`
   */
  prompt?: string;
};
export type ImageEditingTimeOfDayOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingWeatherEffectInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The weather effect to apply. Default value: `"heavy snowfall"`
   */
  prompt?: string;
};
export type ImageEditingWeatherEffectOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingWojakStyleInput = {
  /**
   * URL of the image to convert to wojak style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageEditingWojakStyleOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditingYoutubeThumbnailsInput = {
  /**
   * URL of the image to convert to YouTube thumbnail style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.5`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The text to include in the YouTube thumbnail. Default value: `"Generate youtube thumbnails"`
   */
  prompt?: string;
};
export type ImageEditingYoutubeThumbnailsOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ImageEditInput = {
  /**
   * The prompt to edit the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image to edit.
   */
  image_url: string | Blob | File;
};
export type ImageEditOutput = {
  /**
   * The edited images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type ImageExpansionInput = {
  /**
   * The URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The desired size of the final image, after the expansion. should have an area of less than 5000x5000 pixels.
   */
  canvas_size: Array<number>;
  /**
   * The desired size of the original image, inside the full canvas. Ensure that the ratio of input image foreground or main subject to the canvas area is greater than 15% to achieve optimal results.
   */
  original_image_size: Array<number>;
  /**
   * The desired location of the original image, inside the full canvas. Provide the location of the upper left corner of the original image. The location can also be outside the canvas (the original image will be cropped).
   */
  original_image_location: Array<number>;
  /**
   * Text on which you wish to base the image expansion. This parameter is optional. Bria currently supports prompts in English only, excluding special characters. Default value: `""`
   */
  prompt?: string;
  /**
   * You can choose whether you want your generated expension to be random or predictable. You can recreate the same result in the future by using the seed value of a result from the response. You can exclude this parameter if you are not interested in recreating your results. This parameter is optional.
   */
  seed?: number;
  /**
   * The negative prompt you would like to use to generate images. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ImageExpansionOutput = {
  /**
   * The generated image
   */
  image: Image;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type ImageFillInput = {
  /**
   * URLs of images to be filled into the masked area.
   */
  fill_image_url?: Array<string | Blob | File> | string | Blob | File;
  /**
   * Uses the provided fill image in context with the base image to fill in more faithfully. Will increase price.
   */
  in_context_fill?: boolean;
  /**
   * Whether to use the prompt as well in the generation, along with the redux image.
   */
  use_prompt?: boolean;
};
export type ImageGenInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
  /**
   * Whether to use thought tokens for generation. If set to true, the model will "think" to potentially improve generation quality. Increases generation time and increases the cost by 20%.
   */
  use_thought?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type ImageInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * Url for the Input image.
   */
  image_url: string | Blob | File;
};
export type Imagen3FastInput = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type Imagen3FastOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type Imagen3Input = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type Imagen3Output = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type Imagen4PreviewInput = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type Imagen4PreviewOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type Imagen4PreviewUltraInput = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type Imagen4PreviewUltraOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type ImageOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type ImageProcessingInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Enable film grain effect
   */
  enable_grain?: boolean;
  /**
   * Film grain intensity (when enabled) Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale (when enabled) Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
  /**
   * Enable color correction
   */
  enable_color_correction?: boolean;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
  /**
   * Enable chromatic aberration
   */
  enable_chromatic?: boolean;
  /**
   * Red channel shift amount
   */
  red_shift?: number;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
  /**
   * Enable blur effect
   */
  enable_blur?: boolean;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
  /**
   * Enable vignette effect
   */
  enable_vignette?: boolean;
  /**
   * Vignette strength (when enabled) Default value: `0.5`
   */
  vignette_strength?: number;
  /**
   * Enable parabolize effect
   */
  enable_parabolize?: boolean;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
  /**
   * Enable color tint effect
   */
  enable_tint?: boolean;
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
  /**
   * Enable dissolve effect
   */
  enable_dissolve?: boolean;
  /**
   * URL of second image for dissolve Default value: `""`
   */
  dissolve_image_url?: string | Blob | File;
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
  /**
   * Enable dodge and burn effect
   */
  enable_dodge_burn?: boolean;
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
  /**
   * Enable glow effect
   */
  enable_glow?: boolean;
  /**
   * Glow intensity Default value: `1`
   */
  glow_intensity?: number;
  /**
   * Glow blur radius Default value: `5`
   */
  glow_radius?: number;
  /**
   * Enable sharpen effect
   */
  enable_sharpen?: boolean;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
  /**
   * Enable solarize effect
   */
  enable_solarize?: boolean;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
  /**
   * Enable desaturation effect
   */
  enable_desaturate?: boolean;
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
};
export type ImageTo3dInput = {
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Defines the artistic style or transformation to be applied to the 3D model, altering its appearance according to preset options (extra $0.05 per generation). Omit this option to keep the original style and apperance.
   */
  style?:
    | "person:person2cartoon"
    | "object:clay"
    | "object:steampunk"
    | "animal:venom"
    | "object:barbie"
    | "object:christmas"
    | "gold"
    | "ancient_bronze";
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `original_image`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `default`
   */
  orientation?: "default" | "align_image";
  /**
   * URL of the image to use for model generation.
   */
  image_url: string | Blob | File;
};
export type ImageToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The image to use for control lora. This is used to control the style of the generated image.
   */
  control_lora_image_url?: string | Blob | File;
  /**
   * The strength of the control lora. Default value: `1`
   */
  control_lora_strength?: number;
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type ImageToImageOutput = {
  /**
   * The generated images
   */
  images: Array<File>;
};
export type ImageToImageTurboInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of Image for Image-to-Image
   */
  image_url: string | Blob | File;
  /**
   * Strength for Image-to-Image. Default value: `0.83`
   */
  strength?: number;
};
export type ImageToVideoHailuo02FastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoHailuo02Output = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
  /**
   * The URL to the image to generate the video from.
   */
  image_url: string | Blob | File;
};
export type ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoPreviewInput = {
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 aspect ratio. If the image is not in 16:9 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * Resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
};
export type ImageToVideoPreviewOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoV21MasterOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoV21ProOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoV21StandardOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageToVideoV2MasterOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ImageUnderstandingInput = {
  /**
   * The image for the query.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to query the image with.
   */
  prompt: string;
  /**
   * The seed to use for the generation.
   */
  seed?: number;
};
export type ImageUpscaleOutput = {
  /**
   * The upscaled image.
   */
  image: File;
};
export type Img2ImgOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type InpaintInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
};
export type InpaintTurboInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * URL of Image for inpainting
   */
  image_url: string | Blob | File;
  /**
   * Strength for Image-to-Image. Default value: `0.83`
   */
  strength?: number;
  /**
   * URL of mask image for inpainting.
   */
  mask_image_url: string | Blob | File;
};
export type Input = {
  /**
   * List of tracks to be combined into the final media
   */
  tracks: Array<Track>;
};
export type InstantCharacterInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The LoRAs to use for the image generation. You can use any only 1 lora at a time.
   */
  loras?: Array<LoraWeight>;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The scale of the subject image. Higher values will make the subject image more prominent in the generated image. Default value: `1`
   */
  scale?: number;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type InstantCharacterOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type InvisibleWatermarkInput = {
  /**
   * URL of image to be watermarked or decoded
   */
  image_url: string | Blob | File;
  /**
   * Text to use as watermark (for encoding only) Default value: `"watermark"`
   */
  watermark?: string;
  /**
   * Whether to decode a watermark from the image instead of encoding
   */
  decode?: boolean;
  /**
   * Length of watermark bits to decode (required when decode=True)
   */
  length?: number;
};
export type InvisibleWatermarkOutput = {
  /**
   * The watermarked image file info (when encoding)
   */
  image?: Image;
  /**
   * The extracted watermark text (when decoding)
   */
  extracted_watermark?: string;
  /**
   * Length of the watermark bits used (helpful for future decoding)
   */
  length?: number;
};
export type ItalianOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type JanusInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `square`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Controls randomness in the generation. Higher values make output more random. Default value: `1`
   */
  temperature?: number;
  /**
   * Classifier Free Guidance scale - how closely to follow the prompt. Default value: `5`
   */
  cfg_weight?: number;
  /**
   * Number of images to generate in parallel. Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JanusOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JapaneseOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type JuggernautFluxBaseImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JuggernautFluxBaseImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxBaseInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JuggernautFluxBaseOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxLightningInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JuggernautFluxLightningOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxLoraInpaintingInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
};
export type JuggernautFluxLoraInpaintingOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxLoraInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type JuggernautFluxLoraOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxProImageToImageInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.95`
   */
  strength?: number;
  /**
   * The number of inference steps to perform. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JuggernautFluxProImageToImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type JuggernautFluxProInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type JuggernautFluxProOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KlingV15KolorsVirtualTryOnInput = {
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * Whether to return the result as a base64 encoded data URI.
   */
  sync_mode?: boolean;
};
export type KlingV15KolorsVirtualTryOnOutput = {
  /**
   * The output image.
   */
  image: Image;
};
export type KlingV1I2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoLipsyncAudioToVideoInput = {
  /**
   * The URL of the video to generate the lip sync for.
   */
  video_url: string | Blob | File;
  /**
   * The URL of the audio to generate the lip sync for.
   */
  audio_url: string | Blob | File;
};
export type KlingVideoLipsyncAudioToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoLipsyncTextToVideoInput = {
  /**
   * The URL of the video to generate the lip sync for.
   */
  video_url: string | Blob | File;
  /**
   * Text content for lip-sync video generation. Max 120 characters.
   */
  text: string;
  /**
   * Voice ID to use for speech synthesis
   */
  voice_id:
    | "genshin_vindi2"
    | "zhinen_xuesheng"
    | "AOT"
    | "ai_shatang"
    | "genshin_klee2"
    | "genshin_kirara"
    | "ai_kaiya"
    | "oversea_male1"
    | "ai_chenjiahao_712"
    | "girlfriend_4_speech02"
    | "chat1_female_new-3"
    | "chat_0407_5-1"
    | "cartoon-boy-07"
    | "uk_boy1"
    | "cartoon-girl-01"
    | "PeppaPig_platform"
    | "ai_huangzhong_712"
    | "ai_huangyaoshi_712"
    | "ai_laoguowang_712"
    | "chengshu_jiejie"
    | "you_pingjing"
    | "calm_story1"
    | "uk_man2"
    | "laopopo_speech02"
    | "heainainai_speech02"
    | "reader_en_m-v1"
    | "commercial_lady_en_f-v1"
    | "tiyuxi_xuedi"
    | "tiexin_nanyou"
    | "girlfriend_1_speech02"
    | "girlfriend_2_speech02"
    | "zhuxi_speech02"
    | "uk_oldman3"
    | "dongbeilaotie_speech02"
    | "chongqingxiaohuo_speech02"
    | "chuanmeizi_speech02"
    | "chaoshandashu_speech02"
    | "ai_taiwan_man2_speech02"
    | "xianzhanggui_speech02"
    | "tianjinjiejie_speech02"
    | "diyinnansang_DB_CN_M_04-v2"
    | "yizhipiannan-v1"
    | "guanxiaofang-v2"
    | "tianmeixuemei-v1"
    | "daopianyansang-v1"
    | "mengwa-v1";
  /**
   * The voice language corresponding to the Voice ID Default value: `"en"`
   */
  voice_language?: "zh" | "en";
  /**
   * Speech rate for Text to Video generation Default value: `1`
   */
  voice_speed?: number;
};
export type KlingVideoLipsyncTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV15ProEffectsInput = {
  /**
   * URL of images to be used for hug, kiss or heart_gesture video.
   */
  input_image_urls?: Array<string>;
  /**
   * The effect scene to use for the video generation
   */
  effect_scene:
    | "hug"
    | "kiss"
    | "heart_gesture"
    | "squish"
    | "expansion"
    | "fuzzyfuzzy"
    | "bloombloom"
    | "dizzydizzy";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
};
export type KlingVideoV15ProEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16ProEffectsInput = {
  /**
   * URL of images to be used for hug, kiss or heart_gesture video.
   */
  input_image_urls?: Array<string>;
  /**
   * The effect scene to use for the video generation
   */
  effect_scene:
    | "hug"
    | "kiss"
    | "heart_gesture"
    | "squish"
    | "expansion"
    | "fuzzyfuzzy"
    | "bloombloom"
    | "dizzydizzy";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
};
export type KlingVideoV16ProEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16ProElementsInput = {
  /**
   *
   */
  prompt: string;
  /**
   * List of image URLs to use for video generation. Supports up to 4 images.
   */
  input_image_urls: Array<string>;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
};
export type KlingVideoV16ProElementsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16ProImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * URL of the image to be used for the end of the video
   */
  tail_image_url?: string | Blob | File;
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV16ProImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16ProTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV16ProTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16StandardEffectsInput = {
  /**
   * URL of images to be used for hug, kiss or heart_gesture video.
   */
  input_image_urls?: Array<string>;
  /**
   * The effect scene to use for the video generation
   */
  effect_scene:
    | "hug"
    | "kiss"
    | "heart_gesture"
    | "squish"
    | "expansion"
    | "fuzzyfuzzy"
    | "bloombloom"
    | "dizzydizzy";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
};
export type KlingVideoV16StandardEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16StandardElementsInput = {
  /**
   *
   */
  prompt: string;
  /**
   * List of image URLs to use for video generation. Supports up to 4 images.
   */
  input_image_urls: Array<string>;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
};
export type KlingVideoV16StandardElementsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16StandardImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV16StandardImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV16StandardTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV16StandardTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV1StandardEffectsInput = {
  /**
   * URL of images to be used for hug, kiss or heart_gesture video.
   */
  input_image_urls?: Array<string>;
  /**
   * The effect scene to use for the video generation
   */
  effect_scene:
    | "hug"
    | "kiss"
    | "heart_gesture"
    | "squish"
    | "expansion"
    | "fuzzyfuzzy"
    | "bloombloom"
    | "dizzydizzy";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
};
export type KlingVideoV1StandardEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV1StandardTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
  /**
   * Camera control parameters
   */
  camera_control?:
    | "down_back"
    | "forward_up"
    | "right_turn_forward"
    | "left_turn_forward";
  /**
   * Advanced Camera control parameters
   */
  advanced_camera_control?: CameraControl;
};
export type KlingVideoV1StandardTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV21MasterImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV21MasterImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV21MasterTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV21MasterTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV21ProImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV21ProImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV21StandardImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV21StandardImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV2MasterImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to be used for the video
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV2MasterImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KlingVideoV2MasterTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "10";
  /**
   * The aspect ratio of the generated video frame Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   *  Default value: `"blur, distort, and low quality"`
   */
  negative_prompt?: string;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt. Default value: `0.5`
   */
  cfg_scale?: number;
};
export type KlingVideoV2MasterTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type KokoroAmericanEnglishInput = {
  /**
   *  Default value: `""`
   */
  prompt?: string;
  /**
   * Voice ID for the desired voice. Default value: `"af_heart"`
   */
  voice?:
    | "af_heart"
    | "af_alloy"
    | "af_aoede"
    | "af_bella"
    | "af_jessica"
    | "af_kore"
    | "af_nicole"
    | "af_nova"
    | "af_river"
    | "af_sarah"
    | "af_sky"
    | "am_adam"
    | "am_echo"
    | "am_eric"
    | "am_fenrir"
    | "am_liam"
    | "am_michael"
    | "am_onyx"
    | "am_puck"
    | "am_santa";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroAmericanEnglishOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroBrazilianPortugueseInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "pf_dora" | "pm_alex" | "pm_santa";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroBrazilianPortugueseOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroBritishEnglishInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice:
    | "bf_alice"
    | "bf_emma"
    | "bf_isabella"
    | "bf_lily"
    | "bm_daniel"
    | "bm_fable"
    | "bm_george"
    | "bm_lewis";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroBritishEnglishOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroFrenchInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "ff_siwis";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroFrenchOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroHindiInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "hf_alpha" | "hf_beta" | "hm_omega" | "hm_psi";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroHindiOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroItalianInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "if_sara" | "im_nicola";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroItalianOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroJapaneseInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "jf_alpha" | "jf_gongitsune" | "jf_nezumi" | "jf_tebukuro" | "jm_kumo";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroJapaneseOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroMandarinChineseInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice:
    | "zf_xiaobei"
    | "zf_xiaoni"
    | "zf_xiaoxiao"
    | "zf_xiaoyi"
    | "zm_yunjian"
    | "zm_yunxi"
    | "zm_yunxia"
    | "zm_yunyang";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroMandarinChineseOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KokoroSpanishInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Voice ID for the desired voice.
   */
  voice: "ef_dora" | "em_alex" | "em_santa";
  /**
   * Speed of the generated audio. Default is 1.0. Default value: `1`
   */
  speed?: number;
};
export type KokoroSpanishOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type KontextEditOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KontextImg2ImgOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KontextInpaintOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KontextT2IOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KontextText2ImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KreaOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type KreaReduxOutput = {
  /**
   * The generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type LatentsyncInput = {
  /**
   * The URL of the video to generate the lip sync for.
   */
  video_url: string | Blob | File;
  /**
   * The URL of the audio to generate the lip sync for.
   */
  audio_url: string | Blob | File;
  /**
   * Guidance scale for the model inference Default value: `1`
   */
  guidance_scale?: number;
  /**
   * Random seed for generation. If None, a random seed will be used.
   */
  seed?: number;
  /**
   * Video loop mode when audio is longer than video. Options: pingpong, loop
   */
  loop_mode?: "pingpong" | "loop";
};
export type LatentsyncOutput = {
  /**
   * The generated video with the lip sync.
   */
  video: File;
};
export type LDMTTSInput = {
  /**
   * The dialogue text with turn prefixes to distinguish speakers.
   */
  input: string;
  /**
   * A list of voice definitions for each speaker in the dialogue. Must be between 1 and 2 voices.
   */
  voices?: Array<LDMVoiceInput>;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
  /**
   * An integer number greater than or equal to 0. If equal to null or not provided, a random seed will be used. Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file.
   */
  seed?: number;
};
export type LDMTTSOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type LDMVoiceInput = {
  /**
   * The unique ID of a PlayHT or Cloned Voice, or a name from the available presets.
   */
  voice: string;
  /**
   * A prefix to identify the speaker in multi-turn dialogues. Default value: `"Speaker 1: "`
   */
  turn_prefix?: string;
};
export type LeffaPoseTransferInput = {
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Url for the human image.
   */
  pose_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  person_image_url: string | Blob | File;
};
export type LeffaPoseTransferOutput = {
  /**
   * The output image.
   */
  image: Image;
  /**
   * The seed for the inference.
   */
  seed: number;
  /**
   * Whether the image contains NSFW concepts.
   */
  has_nsfw_concepts: boolean;
};
export type LeffaVirtualTryonInput = {
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * The type of the garment used for virtual try-on.
   */
  garment_type: "upper_body" | "lower_body" | "dresses";
};
export type LeffaVirtualTryonOutput = {
  /**
   * The output image.
   */
  image: Image;
  /**
   * The seed for the inference.
   */
  seed: number;
  /**
   * Whether the image contains NSFW concepts.
   */
  has_nsfw_concepts: boolean;
};
export type LipsyncA2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type LipsyncInput = {
  /**
   *
   */
  video_url: string | Blob | File;
  /**
   *
   */
  audio_url: string | Blob | File;
};
export type LipSyncInput = {
  /**
   * The model to use for lipsyncing Default value: `"lipsync-1.9.0-beta"`
   */
  model?: "lipsync-1.8.0" | "lipsync-1.7.1" | "lipsync-1.9.0-beta";
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
};
export type LipsyncOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type LipSyncOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type LipSyncV2Input = {
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
};
export type LipSyncV2Output = {
  /**
   * The generated video
   */
  video: File;
};
export type LoudnormInput = {
  /**
   * URL of the audio file to normalize
   */
  audio_url: string | Blob | File;
  /**
   * Integrated loudness target in LUFS. Default value: `-18`
   */
  integrated_loudness?: number;
  /**
   * Maximum true peak in dBTP. Default value: `-0.1`
   */
  true_peak?: number;
  /**
   * Loudness range target in LU Default value: `7`
   */
  loudness_range?: number;
  /**
   * Offset gain in dB applied before the true-peak limiter
   */
  offset?: number;
  /**
   * Use linear normalization mode (single-pass). If false, uses dynamic mode (two-pass for better quality).
   */
  linear?: boolean;
  /**
   * Treat mono input files as dual-mono for correct EBU R128 measurement on stereo systems
   */
  dual_mono?: boolean;
  /**
   * Return loudness measurement summary with the normalized audio
   */
  print_summary?: boolean;
  /**
   * Measured integrated loudness of input file in LUFS. Required for linear mode.
   */
  measured_i?: number;
  /**
   * Measured loudness range of input file in LU. Required for linear mode.
   */
  measured_lra?: number;
  /**
   * Measured true peak of input file in dBTP. Required for linear mode.
   */
  measured_tp?: number;
  /**
   * Measured threshold of input file in LUFS. Required for linear mode.
   */
  measured_thresh?: number;
};
export type LoudnormOutput = {
  /**
   * Normalized audio file
   */
  audio: File;
  /**
   * Structured loudness measurement summary (if requested)
   */
  summary?: LoudnormSummary;
};
export type Ltxv13b098DistilledExtendInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * Video to be extended.
   */
  video: ExtendVideoConditioningInput;
};
export type Ltxv13b098DistilledExtendOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type Ltxv13b098DistilledImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
};
export type Ltxv13b098DistilledImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type Ltxv13b098DistilledInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video. Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
};
export type Ltxv13b098DistilledMulticonditioningInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `24`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use a detail pass. If True, the model will perform a second pass to refine the video and enhance details. This incurs a 2.0x cost multiplier on the base price.
   */
  enable_detail_pass?: boolean;
  /**
   * The factor for adaptive instance normalization (AdaIN) applied to generated video chunks after the first. This can help deal with a gradual increase in saturation/contrast in the generated video by normalizing the color distribution across the video. A high value will ensure the color distribution is more consistent across the video, while a low value will allow for more variation in color distribution. Default value: `0.5`
   */
  temporal_adain_factor?: number;
  /**
   * The compression ratio for tone mapping. This is used to compress the dynamic range of the video to improve visual quality. A value of 0.0 means no compression, while a value of 1.0 means maximum compression.
   */
  tone_map_compression_ratio?: number;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `29`
   */
  constant_rate_factor?: number;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type Ltxv13b098DistilledMulticonditioningOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type Ltxv13b098DistilledOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDevExtendInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `30`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `3`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `30`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `17`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type LtxVideo13bDevExtendOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDevImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `30`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `3`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `30`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `17`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
};
export type LtxVideo13bDevImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDevInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9, 1:1 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `30`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `3`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `30`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `17`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type LtxVideo13bDevMulticonditioningInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `30`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `3`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `30`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `17`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type LtxVideo13bDevMulticonditioningOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDevOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDistilledExtendInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type LtxVideo13bDistilledExtendOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDistilledImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
};
export type LtxVideo13bDistilledImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDistilledInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9, 1:1 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type LtxVideo13bDistilledMulticonditioningInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * LoRA weights to use for generation
   */
  loras?: Array<LoRAWeight>;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "9:16" | "1:1" | "16:9" | "auto";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The number of frames in the video. Default value: `121`
   */
  num_frames?: number;
  /**
   * Number of inference steps during the first pass. Default value: `8`
   */
  first_pass_num_inference_steps?: number;
  /**
   * Number of inference steps to skip in the final steps of the first pass. By skipping some steps at the end, the first pass can focus on larger changes instead of smaller details. Default value: `1`
   */
  first_pass_skip_final_steps?: number;
  /**
   * Number of inference steps during the second pass. Default value: `8`
   */
  second_pass_num_inference_steps?: number;
  /**
   * The number of inference steps to skip in the initial steps of the second pass. By skipping some steps at the beginning, the second pass can focus on smaller details instead of larger changes. Default value: `5`
   */
  second_pass_skip_initial_steps?: number;
  /**
   * The frame rate of the video. Default value: `30`
   */
  frame_rate?: number;
  /**
   * Whether to expand the prompt using a language model.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The constant rate factor (CRF) to compress input media with. Compressed input media more closely matches the model's training data, which can improve motion quality. Default value: `35`
   */
  constant_rate_factor?: number;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type LtxVideo13bDistilledMulticonditioningOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideo13bDistilledOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoLoraImageToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  number_of_steps?: number;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The seed to use for generation.
   */
  seed?: number;
  /**
   * Whether to expand the prompt using the LLM.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to use as input.
   */
  image_url: string | Blob | File;
};
export type LtxVideoLoraImageToVideoOutput = {
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video.
   */
  video: File;
};
export type LtxVideoLoraInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  number_of_steps?: number;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The seed to use for generation.
   */
  seed?: number;
  /**
   * Whether to expand the prompt using the LLM.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type LtxVideoLoraMulticonditioningInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  number_of_steps?: number;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The seed to use for generation.
   */
  seed?: number;
  /**
   * Whether to expand the prompt using the LLM.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image conditions to use for generation.
   */
  images?: Array<ImageCondition>;
  /**
   * The video conditions to use for generation.
   */
  videos?: Array<VideoCondition>;
};
export type LtxVideoLoraMulticonditioningOutput = {
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video.
   */
  video: File;
};
export type LtxVideoLoraOutput = {
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
  /**
   * The generated video.
   */
  video: File;
};
export type LtxVideoTrainerInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The rank of the LoRA. Default value: `"128"`
   */
  rank?: "8" | "16" | "32" | "64" | "128";
  /**
   * The number of steps to train for. Default value: `1000`
   */
  number_of_steps?: number;
  /**
   * The number of frames to use for training. This is the number of frames per second multiplied by the number of seconds. Default value: `81`
   */
  number_of_frames?: number;
  /**
   * The target frames per second for the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The resolution to use for training. This is the resolution of the video. Default value: `"medium"`
   */
  resolution?: "low" | "medium" | "high";
  /**
   * The aspect ratio to use for training. This is the aspect ratio of the video. Default value: `"1:1"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to DEFAULT_NUM_FRAMES frames at 16fps.
   */
  auto_scale_input?: boolean;
  /**
   * If true, input above a certain duration threshold will be split into scenes. If you provide captions for a split video, the caption will be applied to each scene. If you do not provide captions, scenes will be auto-captioned. Default value: `true`
   */
  split_input_into_scenes?: boolean;
  /**
   * The duration threshold in seconds. If a video is longer than this, it will be split into scenes. If you provide captions for a split video, the caption will be applied to each scene. If you do not provide captions, scenes will be auto-captioned. Default value: `30`
   */
  split_input_duration_threshold?: number;
  /**
   * A list of validation prompts to use during training. When providing an image, _all_ validation inputs must have an image.
   */
  validation?: Array<Validation>;
  /**
   * A negative prompt to use for validation. Default value: `"blurry, low quality, bad quality, out of focus"`
   */
  validation_negative_prompt?: string;
  /**
   * The number of frames to use for validation. Default value: `81`
   */
  validation_number_of_frames?: number;
  /**
   * The resolution to use for validation. Default value: `"high"`
   */
  validation_resolution?: "low" | "medium" | "high";
  /**
   * The aspect ratio to use for validation. Default value: `"1:1"`
   */
  validation_aspect_ratio?: "16:9" | "1:1" | "9:16";
  /**
   * If true, the validation videos will be reversed. This is useful for effects that are learned in reverse and then applied in reverse.
   */
  validation_reverse?: boolean;
};
export type LtxVideoTrainerOutput = {
  /**
   * The URL to the validations video.
   */
  video: File;
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type LtxVideoV095ExtendInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type LtxVideoV095ExtendOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV095ImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
};
export type LtxVideoV095ImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV095Input = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
};
export type LtxVideoV095MulticonditioningInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type LtxVideoV095MulticonditioningOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV095Output = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV097ExtendInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Video to be extended.
   */
  video: VideoConditioningInput;
};
export type LtxVideoV097ExtendOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV097ImageToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Image URL for Image-to-Video task
   */
  image_url: string | Blob | File;
};
export type LtxVideoV097ImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV097Input = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
};
export type LtxVideoV097MulticonditioningInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type LtxVideoV097MulticonditioningOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LtxVideoV097Output = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type LumaDreamMachineInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
};
export type LumaDreamMachineOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type LumaDreamMachineRay2FlashImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Initial image to start the video from. Can be used together with end_image_url.
   */
  image_url?: string | Blob | File;
  /**
   * Final image to end the video with. Can be used together with image_url.
   */
  end_image_url?: string | Blob | File;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video Default value: `"5s"`
   */
  duration?: "5s" | "9s";
};
export type LumaDreamMachineRay2FlashImageToVideoOutput = {
  /**
   * URL of the generated video
   */
  video: File;
};
export type LumaDreamMachineRay2FlashInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video (9s costs 2x more) Default value: `"5s"`
   */
  duration?: "5s" | "9s";
};
export type LumaDreamMachineRay2FlashModifyInput = {
  /**
   * URL of the input video to modify
   */
  video_url: string | Blob | File;
  /**
   * Optional URL of the first frame image for modification
   */
  image_url?: string | Blob | File;
  /**
   * Instruction for modifying the video
   */
  prompt?: string;
  /**
   * Amount of modification to apply to the video, adhere_1 is the least amount of modification, reimagine_3 is the most Default value: `"flex_1"`
   */
  mode?:
    | "adhere_1"
    | "adhere_2"
    | "adhere_3"
    | "flex_1"
    | "flex_2"
    | "flex_3"
    | "reimagine_1"
    | "reimagine_2"
    | "reimagine_3";
};
export type LumaDreamMachineRay2FlashModifyOutput = {
  /**
   * URL of the modified video
   */
  video: File;
};
export type LumaDreamMachineRay2FlashOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type LumaDreamMachineRay2FlashReframeInput = {
  /**
   * URL of the input video to reframe
   */
  video_url: string | Blob | File;
  /**
   * The aspect ratio of the reframed video
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Optional URL of the first frame image for reframing
   */
  image_url?: string | Blob | File;
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LumaDreamMachineRay2FlashReframeOutput = {
  /**
   * URL of the reframed video
   */
  video: File;
};
export type LumaDreamMachineRay2ImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Initial image to start the video from. Can be used together with end_image_url.
   */
  image_url?: string | Blob | File;
  /**
   * Final image to end the video with. Can be used together with image_url.
   */
  end_image_url?: string | Blob | File;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video Default value: `"5s"`
   */
  duration?: "5s" | "9s";
};
export type LumaDreamMachineRay2ImageToVideoOutput = {
  /**
   * URL of the generated video
   */
  video: File;
};
export type LumaDreamMachineRay2Input = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Whether the video should loop (end of video is blended with the beginning)
   */
  loop?: boolean;
  /**
   * The resolution of the generated video (720p costs 2x more, 1080p costs 4x more) Default value: `"540p"`
   */
  resolution?: "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video (9s costs 2x more) Default value: `"5s"`
   */
  duration?: "5s" | "9s";
};
export type LumaDreamMachineRay2ModifyInput = {
  /**
   * URL of the input video to modify
   */
  video_url: string | Blob | File;
  /**
   * Optional URL of the first frame image for modification
   */
  image_url?: string | Blob | File;
  /**
   * Instruction for modifying the video
   */
  prompt?: string;
  /**
   * Amount of modification to apply to the video, adhere_1 is the least amount of modification, reimagine_3 is the most Default value: `"flex_1"`
   */
  mode?:
    | "adhere_1"
    | "adhere_2"
    | "adhere_3"
    | "flex_1"
    | "flex_2"
    | "flex_3"
    | "reimagine_1"
    | "reimagine_2"
    | "reimagine_3";
};
export type LumaDreamMachineRay2ModifyOutput = {
  /**
   * URL of the modified video
   */
  video: File;
};
export type LumaDreamMachineRay2Output = {
  /**
   * The generated video
   */
  video: File;
};
export type LumaDreamMachineRay2ReframeInput = {
  /**
   * URL of the input video to reframe
   */
  video_url: string | Blob | File;
  /**
   * The aspect ratio of the reframed video
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Optional URL of the first frame image for reframing
   */
  image_url?: string | Blob | File;
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LumaDreamMachineRay2ReframeOutput = {
  /**
   * URL of the reframed video
   */
  video: File;
};
export type LumaPhotonFlashInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"1:1"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:3" | "3:4" | "21:9" | "9:21";
};
export type LumaPhotonFlashModifyInput = {
  /**
   * Instruction for modifying the image
   */
  prompt: string;
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are corresponding to more influence of the initial image on the output.
   */
  strength: number;
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
};
export type LumaPhotonFlashModifyOutput = {
  /**
   * The generated image
   */
  images: Array<File>;
};
export type LumaPhotonFlashOutput = {
  /**
   * The generated image
   */
  images: Array<File>;
};
export type LumaPhotonFlashReframeInput = {
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LumaPhotonFlashReframeOutput = {
  /**
   * The generated image
   */
  images: Array<File>;
};
export type LumaPhotonModifyInput = {
  /**
   * Instruction for modifying the image
   */
  prompt: string;
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are corresponding to more influence of the initial image on the output.
   */
  strength: number;
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
};
export type LumaPhotonModifyOutput = {
  /**
   * The generated image
   */
  images: Array<File>;
};
export type LumaPhotonReframeInput = {
  /**
   * URL of the input image to reframe
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the reframed image
   */
  aspect_ratio: "1:1" | "16:9" | "9:16" | "4:3" | "3:4" | "21:9" | "9:21";
  /**
   * Optional prompt for reframing
   */
  prompt?: string;
  /**
   * X position of the grid for reframing
   */
  grid_position_x?: number;
  /**
   * Y position of the grid for reframing
   */
  grid_position_y?: number;
  /**
   * End X coordinate for reframing
   */
  x_end?: number;
  /**
   * Start X coordinate for reframing
   */
  x_start?: number;
  /**
   * End Y coordinate for reframing
   */
  y_end?: number;
  /**
   * Start Y coordinate for reframing
   */
  y_start?: number;
};
export type LumaPhotonReframeOutput = {
  /**
   * The generated image
   */
  images: Array<File>;
};
export type LuminaImageV2Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The system prompt to use. Default value: `"You are an assistant designed to generate superior images with the superior degree of image-text alignment based on textual prompts or user prompts."`
   */
  system_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to apply normalization-based guidance scale. Default value: `true`
   */
  cfg_normalization?: boolean;
  /**
   * The ratio of the timestep interval to apply normalization-based guidance scale. Default value: `1`
   */
  cfg_trunc_ratio?: number;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type LuminaImageV2Output = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Lyria2Input = {
  /**
   * The text prompt describing the music you want to generate
   */
  prompt: string;
  /**
   * A description of what to exclude from the generated audio Default value: `"low quality"`
   */
  negative_prompt?: string;
  /**
   * A seed for deterministic generation. If provided, the model will attempt to produce the same audio given the same prompt and other parameters.
   */
  seed?: number;
};
export type Lyria2Output = {
  /**
   * The generated music
   */
  audio: File;
};
export type MagiDistilledExtendVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URL of the input video to represent the beginning of the video. If the input video does not match the chosen aspect ratio, it is resized and center cropped.
   */
  video_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * The frame to begin the generation from, with the remaining frames will be treated as the prefix video. The final video will contain the frames up until this number unchanged, followed by the generated frames. The default start frame is 32 frames before the end of the video, which gives optimal results.
   */
  start_frame?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiDistilledExtendVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MagiDistilledImageToVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URL of the input image to represent the first frame of the video. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiDistilledImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MagiDistilledInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiDistilledOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MagiExtendVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URL of the input video to represent the beginning of the video. If the input video does not match the chosen aspect ratio, it is resized and center cropped.
   */
  video_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * The frame to begin the generation from, with the remaining frames will be treated as the prefix video. The final video will contain the frames up until this number unchanged, followed by the generated frames. The default start frame is 32 frames before the end of the video, which gives optimal results.
   */
  start_frame?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiExtendVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MagiImageToVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * URL of the input image to represent the first frame of the video. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MagiInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 96 and 192 (inclusive). Each additional 24 frames beyond 96 incurs an additional billing unit. Default value: `96`
   */
  num_frames?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `"16"`
   */
  num_inference_steps?: "4" | "8" | "16" | "32" | "64";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type MagiOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MandarinOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type MaskInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask image. White pixels (255) indicate areas to remove.
   */
  mask_url: string | Blob | File;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
};
export type MetadataInput = {
  /**
   * URL of the media file (video or audio) to analyze
   */
  media_url: string | Blob | File;
  /**
   * Whether to extract the start and end frames for videos. Note that when true the request will be slower.
   */
  extract_frames?: boolean;
};
export type MetadataOutput = {
  /**
   * Metadata for the analyzed media file (either Video or Audio)
   */
  media: Video | Audio;
};
export type MinimaxHailuo02FastImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxHailuo02FastImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxHailuo02ProImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxHailuo02ProImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxHailuo02ProTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxHailuo02ProTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxHailuo02StandardImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
  /**
   * The resolution of the generated video. Default value: `"768P"`
   */
  resolution?: "512P" | "768P";
};
export type MinimaxHailuo02StandardImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxHailuo02StandardTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxHailuo02StandardTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxImage01Input = {
  /**
   * Text prompt for image generation (max 1500 characters)
   */
  prompt: string;
  /**
   * Aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "16:9"
    | "4:3"
    | "3:2"
    | "2:3"
    | "3:4"
    | "9:16"
    | "21:9";
  /**
   * Number of images to generate (1-9) Default value: `1`
   */
  num_images?: number;
  /**
   * Whether to enable automatic prompt optimization
   */
  prompt_optimizer?: boolean;
};
export type MinimaxImage01Output = {
  /**
   * Generated images
   */
  images: Array<File>;
};
export type MinimaxImage01SubjectReferenceInput = {
  /**
   * Text prompt for image generation (max 1500 characters)
   */
  prompt: string;
  /**
   * URL of the subject reference image to use for consistent character appearance
   */
  image_url: string | Blob | File;
  /**
   * Aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "16:9"
    | "4:3"
    | "3:2"
    | "2:3"
    | "3:4"
    | "9:16"
    | "21:9";
  /**
   * Number of images to generate (1-9) Default value: `1`
   */
  num_images?: number;
  /**
   * Whether to enable automatic prompt optimization
   */
  prompt_optimizer?: boolean;
};
export type MinimaxImage01SubjectReferenceOutput = {
  /**
   * Generated images
   */
  images: Array<File>;
};
export type MinimaxMusicInput = {
  /**
   * Lyrics with optional formatting. You can use a newline to separate each line of lyrics. You can use two newlines to add a pause between lines. You can use double hash marks (##) at the beginning and end of the lyrics to add accompaniment. Maximum 600 characters.
   */
  prompt: string;
  /**
   * Reference song, should contain music and vocals. Must be a .wav or .mp3 file longer than 15 seconds.
   */
  reference_audio_url: string | Blob | File;
};
export type MinimaxMusicOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type MinimaxSpeech02HdInput = {
  /**
   * Text to convert to speech (max 5000 characters)
   */
  text: string;
  /**
   * Voice configuration settings
   */
  voice_setting?: VoiceSetting;
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Enhance recognition of specified languages and dialects
   */
  language_boost?:
    | "Chinese"
    | "Chinese,Yue"
    | "English"
    | "Arabic"
    | "Russian"
    | "Spanish"
    | "French"
    | "Portuguese"
    | "German"
    | "Turkish"
    | "Dutch"
    | "Ukrainian"
    | "Vietnamese"
    | "Indonesian"
    | "Japanese"
    | "Italian"
    | "Korean"
    | "Thai"
    | "Polish"
    | "Romanian"
    | "Greek"
    | "Czech"
    | "Finnish"
    | "Hindi"
    | "auto";
  /**
   * Format of the output content (non-streaming only) Default value: `"hex"`
   */
  output_format?: "url" | "hex";
  /**
   * Custom pronunciation dictionary for text replacement
   */
  pronunciation_dict?: PronunciationDict;
};
export type MinimaxSpeech02HdOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Duration of the audio in milliseconds
   */
  duration_ms: number;
};
export type MinimaxSpeech02TurboInput = {
  /**
   * Text to convert to speech (max 5000 characters)
   */
  text: string;
  /**
   * Voice configuration settings
   */
  voice_setting?: VoiceSetting;
  /**
   * Audio configuration settings
   */
  audio_setting?: AudioSetting;
  /**
   * Enhance recognition of specified languages and dialects
   */
  language_boost?:
    | "Chinese"
    | "Chinese,Yue"
    | "English"
    | "Arabic"
    | "Russian"
    | "Spanish"
    | "French"
    | "Portuguese"
    | "German"
    | "Turkish"
    | "Dutch"
    | "Ukrainian"
    | "Vietnamese"
    | "Indonesian"
    | "Japanese"
    | "Italian"
    | "Korean"
    | "Thai"
    | "Polish"
    | "Romanian"
    | "Greek"
    | "Czech"
    | "Finnish"
    | "Hindi"
    | "auto";
  /**
   * Format of the output content (non-streaming only) Default value: `"hex"`
   */
  output_format?: "url" | "hex";
  /**
   * Custom pronunciation dictionary for text replacement
   */
  pronunciation_dict?: PronunciationDict;
};
export type MinimaxSpeech02TurboOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Duration of the audio in milliseconds
   */
  duration_ms: number;
};
export type MiniMaxTextToImageOutput = {
  /**
   * Generated images
   */
  images: Array<File>;
};
export type MiniMaxTextToImageWithReferenceOutput = {
  /**
   * Generated images
   */
  images: Array<File>;
};
export type MinimaxVideo01DirectorImageToVideoInput = {
  /**
   * Text prompt for video generation. Camera movement instructions can be added using square brackets (e.g. [Pan left] or [Zoom in]). You can use up to 3 combined movements per prompt. Supported movements: Truck left/right, Pan left/right, Push in/Pull out, Pedestal up/down, Tilt up/down, Zoom in/out, Shake, Tracking shot, Static shot. For example: [Truck left, Pan right, Zoom in]. For a more detailed guide, refer https://sixth-switch-2ac.notion.site/T2V-01-Director-Model-Tutorial-with-camera-movement-1886c20a98eb80f395b8e05291ad8645
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01DirectorImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVideo01DirectorInput = {
  /**
   * Text prompt for video generation. Camera movement instructions can be added using square brackets (e.g. [Pan left] or [Zoom in]). You can use up to 3 combined movements per prompt. Supported movements: Truck left/right, Pan left/right, Push in/Pull out, Pedestal up/down, Tilt up/down, Zoom in/out, Shake, Tracking shot, Static shot. For example: [Truck left, Pan right, Zoom in]. For a more detailed guide, refer https://sixth-switch-2ac.notion.site/T2V-01-Director-Model-Tutorial-with-camera-movement-1886c20a98eb80f395b8e05291ad8645
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01DirectorOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVideo01ImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVideo01LiveImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01LiveImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVideo01LiveInput = {
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01LiveOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVideo01SubjectReferenceInput = {
  /**
   *
   */
  prompt: string;
  /**
   * URL of the subject reference image to use for consistent subject appearance
   */
  subject_reference_image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type MinimaxVideo01SubjectReferenceOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type MinimaxVoiceCloneInput = {
  /**
   * URL of the input audio file for voice cloning. Should be at least 10 seconds long.
   */
  audio_url: string | Blob | File;
  /**
   * Enable noise reduction for the cloned voice
   */
  noise_reduction?: boolean;
  /**
   * Enable volume normalization for the cloned voice
   */
  need_volume_normalization?: boolean;
  /**
   * Text validation accuracy threshold (0-1)
   */
  accuracy?: number;
  /**
   * Text to generate a TTS preview with the cloned voice (optional) Default value: `"Hello, this is a preview of your cloned voice! I hope you like it!"`
   */
  text?: string;
  /**
   * TTS model to use for preview. Options: speech-02-hd, speech-02-turbo, speech-01-hd, speech-01-turbo Default value: `"speech-02-hd"`
   */
  model?:
    | "speech-02-hd"
    | "speech-02-turbo"
    | "speech-01-hd"
    | "speech-01-turbo";
};
export type MinimaxVoiceCloneOutput = {
  /**
   * The cloned voice ID for use with TTS
   */
  custom_voice_id: string;
  /**
   * Preview audio generated with the cloned voice (if requested)
   */
  audio?: File;
};
export type MinimaxVoiceDesignInput = {
  /**
   * Voice description prompt for generating a personalized voice
   */
  prompt: string;
  /**
   * Text for audio preview. Limited to 500 characters. A fee of $30 per 1M characters will be charged for the generation of the preview audio.
   */
  preview_text: string;
};
export type MinimaxVoiceDesignOutput = {
  /**
   * The voice_id of the generated voice
   */
  custom_voice_id: string;
  /**
   * The preview audio using the generated voice
   */
  audio: File;
};
export type MixDehazeNetInput = {
  /**
   * URL of image to be used for image enhancement
   */
  image_url: string | Blob | File;
  /**
   * Model to be used for dehazing Default value: `"indoor"`
   */
  model?: "indoor" | "outdoor";
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type MixDehazeNetOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type MmaudioV2Input = {
  /**
   * The URL of the video to generate the audio for.
   */
  video_url: string | Blob | File;
  /**
   * The prompt to generate the audio for.
   */
  prompt: string;
  /**
   * The negative prompt to generate the audio for. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The number of steps to generate the audio for. Default value: `25`
   */
  num_steps?: number;
  /**
   * The duration of the audio to generate. Default value: `8`
   */
  duration?: number;
  /**
   * The strength of Classifier Free Guidance. Default value: `4.5`
   */
  cfg_strength?: number;
  /**
   * Whether to mask away the clip.
   */
  mask_away_clip?: boolean;
};
export type MmaudioV2Output = {
  /**
   * The generated video with the lip sync.
   */
  video: File;
};
export type MmaudioV2TextToAudioInput = {
  /**
   * The prompt to generate the audio for.
   */
  prompt: string;
  /**
   * The negative prompt to generate the audio for. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The number of steps to generate the audio for. Default value: `25`
   */
  num_steps?: number;
  /**
   * The duration of the audio to generate. Default value: `8`
   */
  duration?: number;
  /**
   * The strength of Classifier Free Guidance. Default value: `4.5`
   */
  cfg_strength?: number;
  /**
   * Whether to mask away the clip.
   */
  mask_away_clip?: boolean;
};
export type MmaudioV2TextToAudioOutput = {
  /**
   * The generated audio.
   */
  audio: File;
};
export type ModifyOutput = {
  /**
   * URL of the modified video
   */
  video: File;
};
export type Moondream2Input = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
};
export type Moondream2ObjectDetectionInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Object to be detected in the image
   */
  object: string;
};
export type Moondream2ObjectDetectionOutput = {
  /**
   * Objects detected in the image
   */
  objects: Array<any>;
  /**
   * Image with detected objects
   */
  image: Image;
};
export type Moondream2Output = {
  /**
   * Output for the given query
   */
  output: string;
};
export type Moondream2PointObjectDetectionInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Object to be detected in the image
   */
  object: string;
};
export type Moondream2PointObjectDetectionOutput = {
  /**
   * Objects detected in the image
   */
  objects: Array<any>;
  /**
   * Image with detected objects
   */
  image: Image;
};
export type Moondream2VisualQueryInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Query to be asked in the image
   */
  prompt: string;
};
export type Moondream2VisualQueryOutput = {
  /**
   * Output for the given query
   */
  output: string;
};
export type MoondreamInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
};
export type MoondreamNextBatchInput = {
  /**
   * List of image URLs to be processed (maximum 32 images)
   */
  images_data_url: string | Blob | File;
  /**
   * Single prompt to apply to all images
   */
  prompt: string;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
};
export type MoondreamNextBatchOutput = {
  /**
   * URL to the generated captions JSON file containing filename-caption pairs.
   */
  captions_file: File;
  /**
   * List of generated captions
   */
  outputs: Array<string>;
};
export type MoondreamNextDetectionInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Type of detection to perform
   */
  task_type: "bbox_detection" | "point_detection" | "gaze_detection";
  /**
   * Text description of what to detect
   */
  detection_prompt: string;
  /**
   * Whether to use ensemble for gaze detection
   */
  use_ensemble?: boolean;
};
export type MoondreamNextDetectionOutput = {
  /**
   * Output image with detection visualization
   */
  image: Image;
  /**
   * Detection results as text
   */
  text_output: string;
};
export type MoondreamNextInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Type of task to perform Default value: `"caption"`
   */
  task_type?: "caption" | "query";
  /**
   * Prompt for query task
   */
  prompt: string;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
};
export type MoondreamNextOutput = {
  /**
   * Response from the model
   */
  output: string;
};
export type MoondreamObjectInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Object to be detected in the image
   */
  object: string;
};
export type MoondreamObjectOutput = {
  /**
   * Objects detected in the image
   */
  objects: Array<any>;
  /**
   * Image with detected objects
   */
  image: Image;
};
export type MoondreamOutput = {
  /**
   * Output for the given query
   */
  output: string;
};
export type MoonDreamOutput = {
  /**
   * Response from the model
   */
  output: string;
};
export type MoondreamQueryInput = {
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
  /**
   * Query to be asked in the image
   */
  prompt: string;
};
export type MulticonditioningVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Default value: `"blurry, low quality, low resolution, inconsistent motion, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * The LoRA weights to use for generation.
   */
  loras?: Array<LoRAWeight>;
  /**
   * The resolution of the video. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * The aspect ratio of the video. Default value: `"auto"`
   */
  aspect_ratio?: "16:9" | "1:1" | "9:16" | "auto";
  /**
   * The number of frames in the video. Default value: `89`
   */
  number_of_frames?: number;
  /**
   * The number of inference steps to use. Default value: `30`
   */
  number_of_steps?: number;
  /**
   * The frame rate of the video. Default value: `25`
   */
  frame_rate?: number;
  /**
   * The seed to use for generation.
   */
  seed?: number;
  /**
   * Whether to expand the prompt using the LLM.
   */
  expand_prompt?: boolean;
  /**
   * Whether to reverse the video.
   */
  reverse_video?: boolean;
  /**
   * Whether to enable the safety checker. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The image conditions to use for generation.
   */
  images?: Array<ImageCondition>;
  /**
   * The video conditions to use for generation.
   */
  videos?: Array<VideoCondition>;
};
export type MultiConditioningVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * URL of images to use as conditioning
   */
  images?: Array<ImageConditioningInput>;
  /**
   * Videos to use as conditioning
   */
  videos?: Array<VideoConditioningInput>;
};
export type MulticonditioningVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MultiConditioningVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type MultiViewObjectOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type MultiviewTo3dInput = {
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Defines the artistic style or transformation to be applied to the 3D model, altering its appearance according to preset options (extra $0.05 per generation). Omit this option to keep the original style and apperance.
   */
  style?:
    | "person:person2cartoon"
    | "object:clay"
    | "object:steampunk"
    | "animal:venom"
    | "object:barbie"
    | "object:christmas"
    | "gold"
    | "ancient_bronze";
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `original_image`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `default`
   */
  orientation?: "default" | "align_image";
  /**
   * Front view image of the object.
   */
  front_image_url: string | Blob | File;
  /**
   * Left view image of the object.
   */
  left_image_url?: string | Blob | File;
  /**
   * Back view image of the object.
   */
  back_image_url?: string | Blob | File;
  /**
   * Right view image of the object.
   */
  right_image_url?: string | Blob | File;
};
export type MusicGeneratorInput = {
  /**
   * The prompt to generate music from.
   */
  prompt: string;
  /**
   * The duration of the generated music in seconds.
   */
  duration: number;
};
export type MusicGeneratorOutput = {
  /**
   * The generated music
   */
  audio_file: File;
};
export type NafnetDeblurInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type NafnetDeblurOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type NafnetDenoiseInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type NafnetDenoiseOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type NafnetInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type NafnetOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type ObjectOutput = {
  /**
   * Generated 3D object file.
   */
  model_mesh: File;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type ObjectRemovalBboxInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * List of bounding box coordinates to erase (only one box prompt is supported)
   */
  box_prompts?: Array<BBoxPromptBase>;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
};
export type ObjectRemovalBboxOutput = {
  /**
   * The generated images with objects removed.
   */
  images: Array<Image>;
};
export type ObjectRemovalInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Specify which objects to remove from the image. Default value: `"background people"`
   */
  prompt?: string;
};
export type ObjectRemovalMaskInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * The URL of the mask image. White pixels (255) indicate areas to remove.
   */
  mask_url: string | Blob | File;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
};
export type ObjectRemovalMaskOutput = {
  /**
   * The generated images with objects removed.
   */
  images: Array<Image>;
};
export type ObjectRemovalOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type OmnigenV1Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * URL of images to use while generating the image, Use <img><|image_1|></img> for the first image and so on.
   */
  input_image_urls?: Array<string>;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3`
   */
  guidance_scale?: number;
  /**
   * The Image Guidance scale is a measure of how close you want
   * the model to stick to your input image when looking for a related image to show you. Default value: `1.6`
   */
  img_guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type OmnigenV1Output = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type OmnigenV2Input = {
  /**
   * The prompt to generate or edit an image. Use specific language like 'Add the bird from image 1 to the desk in image 2' for better results.
   */
  prompt: string;
  /**
   * URLs of input images to use for image editing or multi-image generation. Support up to 3 images.
   */
  input_image_urls?: Array<string>;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The Text Guidance scale controls how closely the model follows the text prompt.
   * Higher values make the model stick more closely to the prompt. Default value: `5`
   */
  text_guidance_scale?: number;
  /**
   * The Image Guidance scale controls how closely the model follows the input images.
   * For image editing: 1.3-2.0, for in-context generation: 2.0-3.0 Default value: `2`
   */
  image_guidance_scale?: number;
  /**
   * Negative prompt to guide what should not be in the image. Default value: `"(((deformed))), blurry, over saturation, bad anatomy, disfigured, poorly drawn face, mutation, mutated, (extra_limb), (ugly), (poorly drawn hands), fused fingers, messy drawing, broken legs censor, censored, censor_bar"`
   */
  negative_prompt?: string;
  /**
   * CFG range start value.
   */
  cfg_range_start?: number;
  /**
   * CFG range end value. Default value: `1`
   */
  cfg_range_end?: number;
  /**
   * The scheduler to use for the diffusion process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmsolver";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type OmnigenV2Output = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type OmniHumanInput = {
  /**
   * The URL of the image used to generate the video
   */
  image_url: string | Blob | File;
  /**
   * The URL of the audio file to generate the video. Audio must be under 30s long.
   */
  audio_url: string | Blob | File;
};
export type OmniHumanOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Duration of audio input/video output as used for billing.
   */
  duration: number;
};
export type OrpheusTtsInput = {
  /**
   * The text to be converted to speech. You can additionally add the following emotive tags: <laugh>, <chuckle>, <sigh>, <cough>, <sniffle>, <groan>, <yawn>, <gasp>
   */
  text: string;
  /**
   * Voice ID for the desired voice. Default value: `"tara"`
   */
  voice?: "tara" | "leah" | "jess" | "leo" | "dan" | "mia" | "zac" | "zoe";
  /**
   * Temperature for generation (higher = more creative). Default value: `0.7`
   */
  temperature?: number;
  /**
   * Repetition penalty (>= 1.1 required for stable generations). Default value: `1.2`
   */
  repetition_penalty?: number;
};
export type OrpheusTtsOutput = {
  /**
   * The generated speech audio
   */
  audio: File;
};
export type Output = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type ParabolizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
};
export type ParabolizeOutput = {
  /**
   * The processed images with parabolize effect
   */
  images: Array<Image>;
};
export type PasdInput = {
  /**
   * Input image to super-resolve
   */
  image_url: string | Blob | File;
  /**
   * Upscaling factor (1-4x) Default value: `2`
   */
  scale?: number;
  /**
   * Number of inference steps (10-50) Default value: `25`
   */
  steps?: number;
  /**
   * Guidance scale for diffusion (1.0-20.0) Default value: `7`
   */
  guidance_scale?: number;
  /**
   * ControlNet conditioning scale (0.1-1.0) Default value: `0.8`
   */
  conditioning_scale?: number;
  /**
   * Additional prompt to guide super-resolution Default value: `""`
   */
  prompt?: string;
  /**
   * Negative prompt to avoid unwanted artifacts Default value: `"blurry, dirty, messy, frames, deformed, dotted, noise, raster lines, unclear, lowres, over-smoothed, painting, ai generated"`
   */
  negative_prompt?: string;
};
export type PasdOutput = {
  /**
   * The generated super-resolved images
   */
  images: Array<Image>;
  /**
   * Timing information for different processing stages
   */
  timings?: any;
};
export type PhotoLoraI2IInput = {
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
};
export type PhotoLoraInpaintInput = {
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * URL of image to use for inpainting. or img2img
   */
  image_url: string | Blob | File;
  /**
   * The strength to use for inpainting/image-to-image. Only used if the image_url is provided. 1.0 is completely remakes the image while 0.0 preserves the original. Default value: `0.85`
   */
  strength?: number;
  /**
   * The mask to area to Inpaint in.
   */
  mask_url: string | Blob | File;
};
export type PhotoRestorationInput = {
  /**
   * URL of the old or damaged photo to restore.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type PhotoRestorationOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type Pika22ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Pika22TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikadditionsOutput = {
  /**
   * The generated video with added objects/images
   */
  video: File;
};
export type PikaffectsOutput = {
  /**
   * The generated video with applied effect
   */
  video: File;
};
export type PikaswapsOutput = {
  /**
   * The generated video with swapped regions
   */
  video: File;
};
export type PikaV15PikaffectsInput = {
  /**
   * URL of the input image
   */
  image_url: string | Blob | File;
  /**
   * The Pikaffect to apply
   */
  pikaffect:
    | "Cake-ify"
    | "Crumble"
    | "Crush"
    | "Decapitate"
    | "Deflate"
    | "Dissolve"
    | "Explode"
    | "Eye-pop"
    | "Inflate"
    | "Levitate"
    | "Melt"
    | "Peel"
    | "Poke"
    | "Squish"
    | "Ta-da"
    | "Tear";
  /**
   * Text prompt to guide the effect
   */
  prompt?: string;
  /**
   * Negative prompt to guide the model
   */
  negative_prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
};
export type PikaV15PikaffectsOutput = {
  /**
   * The generated video with applied effect
   */
  video: File;
};
export type PikaV21ImageToVideoInput = {
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV21ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV21TextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV21TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV22ImageToVideoInput = {
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV22ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV22PikascenesInput = {
  /**
   * List of images to use for video generation
   */
  images: Array<PikaImage>;
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
  /**
   * Mode for integrating multiple images Default value: `"creative"`
   */
  ingredients_mode?: "creative" | "precise";
};
export type PikaV22PikascenesOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV22TextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV22TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV2TurboImageToVideoInput = {
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV2TurboImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PikaV2TurboTextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * A negative prompt to guide the model Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1" | "4:5" | "5:4" | "3:2" | "2:3";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `5`
   */
  duration?: number;
};
export type PikaV2TurboTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseExtendFastInput = {
  /**
   * URL of the input video to extend
   */
  video_url: string | Blob | File;
  /**
   * Prompt describing how to extend the video
   */
  prompt: string;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the extended video
   */
  style?: "anime" | "3d_animation" | "day" | "cyberpunk" | "comic";
  /**
   * The resolution of the generated video. Fast mode doesn't support 1080p Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * The model version to use for generation Default value: `"v4.5"`
   */
  model?: "v3.5" | "v4" | "v4.5";
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type PixverseExtendFastOutput = {
  /**
   * The extended video
   */
  video: File;
};
export type PixverseExtendInput = {
  /**
   * URL of the input video to extend
   */
  video_url: string | Blob | File;
  /**
   * Prompt describing how to extend the video
   */
  prompt: string;
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the extended video
   */
  style?: "anime" | "3d_animation" | "day" | "cyberpunk" | "comic";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * The model version to use for generation Default value: `"v4.5"`
   */
  model?: "v3.5" | "v4" | "v4.5";
  /**
   * Random seed for generation
   */
  seed?: number;
};
export type PixverseExtendOutput = {
  /**
   * The extended video
   */
  video: File;
};
export type PixverseLipsyncInput = {
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * URL of the input audio. If not provided, TTS will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * Voice to use for TTS when audio_url is not provided Default value: `"Auto"`
   */
  voice_id?:
    | "Emily"
    | "James"
    | "Isabella"
    | "Liam"
    | "Chloe"
    | "Adrian"
    | "Harper"
    | "Ava"
    | "Sophia"
    | "Julia"
    | "Mason"
    | "Jack"
    | "Oliver"
    | "Ethan"
    | "Auto";
  /**
   * Text content for TTS when audio_url is not provided
   */
  text?: string;
};
export type PixverseLipsyncOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseSoundEffectsInput = {
  /**
   * URL of the input video to add sound effects to
   */
  video_url: string | Blob | File;
  /**
   * Whether to keep the original audio from the video
   */
  original_sound_switch?: boolean;
  /**
   * Description of the sound effect to generate. If empty, a random sound effect will be generated Default value: `""`
   */
  prompt?: string;
};
export type PixverseSoundEffectsOutput = {
  /**
   * The video with added sound effects
   */
  video: File;
};
export type PixverseV35EffectsInput = {
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Hulk"
    | "Venom"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url?: string | Blob | File;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
};
export type PixverseV35EffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV35ImageToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV35ImageToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV35ImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV35ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV35TextToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV35TextToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV35TextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV35TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV35TransitionInput = {
  /**
   * The prompt for the transition
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  first_image_url: string | Blob | File;
  /**
   * URL of the image to use as the last frame
   */
  last_image_url: string | Blob | File;
};
export type PixverseV35TransitionOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45EffectsInput = {
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Hulk"
    | "Venom"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url?: string | Blob | File;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
};
export type PixverseV45EffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45ImageToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV45ImageToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45ImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV45ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45TextToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV45TextToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45TextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV45TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV45TransitionInput = {
  /**
   * The prompt for the transition
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  first_image_url: string | Blob | File;
  /**
   * URL of the image to use as the last frame
   */
  last_image_url: string | Blob | File;
};
export type PixverseV45TransitionOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV4EffectsInput = {
  /**
   * The effect to apply to the video
   */
  effect:
    | "Kiss Me AI"
    | "Kiss"
    | "Muscle Surge"
    | "Warmth of Jesus"
    | "Anything, Robot"
    | "The Tiger Touch"
    | "Hug"
    | "Holy Wings"
    | "Hulk"
    | "Venom"
    | "Microwave"
    | "Zombie Mode"
    | "Squid Game"
    | "Baby Face"
    | "Black Myth: Wukong"
    | "Long Hair Magic"
    | "Leggy Run"
    | "Fin-tastic Mermaid"
    | "Punch Face"
    | "Creepy Devil Smile"
    | "Thunder God"
    | "Eye Zoom Challenge"
    | "Who's Arrested?"
    | "Baby Arrived"
    | "Werewolf Rage"
    | "Bald Swipe"
    | "BOOM DROP"
    | "Huge Cutie"
    | "Liquid Metal"
    | "Sharksnap!"
    | "Dust Me Away";
  /**
   * Optional URL of the image to use as the first frame. If not provided, generates from text
   */
  image_url?: string | Blob | File;
  /**
   * The resolution of the generated video. Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
};
export type PixverseV4EffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV4ImageToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV4ImageToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV4ImageToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
};
export type PixverseV4ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV4TextToVideoFastInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV4TextToVideoFastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PixverseV4TextToVideoInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "360p" | "540p" | "720p" | "1080p";
  /**
   * The duration of the generated video in seconds. 8s videos cost double. 1080p videos are limited to 5 seconds Default value: `"5"`
   */
  duration?: "5" | "8";
  /**
   * Negative prompt to be used for the generation Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The style of the generated video
   */
  style?: "anime" | "3d_animation" | "clay" | "comic" | "cyberpunk";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
};
export type PixverseV4TextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type PlayaiInpaintDiffusionInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * Transcription of the input audio.
   */
  text: string;
  /**
   * Desired audio text. This is the text that will be spoken in the output audio. The model will find the difference between the input and output text and inpaint the audio accordingly.
   */
  output_text: string;
  /**
   * Word timestamps for the input audio. Each word is represented by an object containing the word text and its start/end timestamps. You can generate these timestamps using an ASR (Automatic Speech Recognition) on https://fal.ai/models/fal-ai/whisper.
   */
  chunks: Array<WordTime>;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
};
export type PlayaiInpaintDiffusionOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type PlayaiTtsDialogInput = {
  /**
   * The dialogue text with turn prefixes to distinguish speakers.
   */
  input: string;
  /**
   * A list of voice definitions for each speaker in the dialogue. Must be between 1 and 2 voices.
   */
  voices?: Array<LDMVoiceInput>;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
  /**
   * An integer number greater than or equal to 0. If equal to null or not provided, a random seed will be used. Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file.
   */
  seed?: number;
};
export type PlayaiTtsDialogOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type PlayaiTtsV3Input = {
  /**
   * The text to be converted to speech.
   */
  input: string;
  /**
   * The unique ID of a PlayHT or Cloned Voice, or a name from the available presets.
   */
  voice: string;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
  /**
   * An integer number greater than or equal to 0. If equal to null or not provided, a random seed will be used. Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file.
   */
  seed?: number;
};
export type PlayaiTtsV3Output = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type PlayDiffusionInpaintInput = {
  /**
   * The URL of the audio file.
   */
  audio_url: string | Blob | File;
  /**
   * Transcription of the input audio.
   */
  text: string;
  /**
   * Desired audio text. This is the text that will be spoken in the output audio. The model will find the difference between the input and output text and inpaint the audio accordingly.
   */
  output_text: string;
  /**
   * Word timestamps for the input audio. Each word is represented by an object containing the word text and its start/end timestamps. You can generate these timestamps using an ASR (Automatic Speech Recognition) on https://fal.ai/models/fal-ai/whisper.
   */
  chunks: Array<WordTime>;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
};
export type PlayDiffusionInpaintOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type PlayDiffusionTTSInput = {
  /**
   * The text to be converted to speech.
   */
  input: string;
  /**
   * The unique ID of a PlayHT or Cloned Voice, or a name from the available presets.
   */
  voice: string;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
  /**
   * An integer number greater than or equal to 0. If equal to null or not provided, a random seed will be used. Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file.
   */
  seed?: number;
};
export type PlayDiffusionTTSOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type PlushieStyleInput = {
  /**
   * URL of the image to convert to plushie style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type PlushieStyleOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type PlushifyInput = {
  /**
   * URL of the image to apply cartoon style to
   */
  image_url: string | Blob | File;
  /**
   * Prompt for the generation. Default is empty which is usually best, but sometimes it can help to add a description of the subject. Default value: `""`
   */
  prompt?: string;
  /**
   * Scale factor for the Cartoon effect Default value: `1`
   */
  scale?: number;
  /**
   * Guidance scale for the generation Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to use CFG zero
   */
  use_cfg_zero?: boolean;
  /**
   * The seed for image generation. Same seed with same parameters will generate same image.
   */
  seed?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
};
export type PlushifyOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type PoseTransferInput = {
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Url for the human image.
   */
  pose_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  person_image_url: string | Blob | File;
};
export type PoseTransferOutput = {
  /**
   * The output image.
   */
  image: Image;
  /**
   * The seed for the inference.
   */
  seed: number;
  /**
   * Whether the image contains NSFW concepts.
   */
  has_nsfw_concepts: boolean;
};
export type PostProcessingBlurInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
};
export type PostProcessingBlurOutput = {
  /**
   * The processed images with blur effect
   */
  images: Array<Image>;
};
export type PostProcessingChromaticAberrationInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Red channel shift amount
   */
  red_shift?: number;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
};
export type PostProcessingChromaticAberrationOutput = {
  /**
   * The processed images with chromatic aberration effect
   */
  images: Array<Image>;
};
export type PostProcessingColorCorrectionInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
};
export type PostProcessingColorCorrectionOutput = {
  /**
   * The processed images with color correction
   */
  images: Array<Image>;
};
export type PostProcessingColorTintInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
};
export type PostProcessingColorTintOutput = {
  /**
   * The processed images with color tint effect
   */
  images: Array<Image>;
};
export type PostProcessingDesaturateInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
};
export type PostProcessingDesaturateOutput = {
  /**
   * The processed images with desaturation effect
   */
  images: Array<Image>;
};
export type PostProcessingDissolveInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * URL of second image for dissolve
   */
  dissolve_image_url: string | Blob | File;
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
};
export type PostProcessingDissolveOutput = {
  /**
   * The processed images with dissolve effect
   */
  images: Array<Image>;
};
export type PostProcessingDodgeBurnInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
};
export type PostProcessingDodgeBurnOutput = {
  /**
   * The processed images with dodge and burn effect
   */
  images: Array<Image>;
};
export type PostProcessingGrainInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Film grain intensity Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
};
export type PostProcessingGrainOutput = {
  /**
   * The processed images with grain effect
   */
  images: Array<Image>;
};
export type PostProcessingInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Enable film grain effect
   */
  enable_grain?: boolean;
  /**
   * Film grain intensity (when enabled) Default value: `0.4`
   */
  grain_intensity?: number;
  /**
   * Film grain scale (when enabled) Default value: `10`
   */
  grain_scale?: number;
  /**
   * Style of film grain to apply Default value: `"modern"`
   */
  grain_style?:
    | "modern"
    | "analog"
    | "kodak"
    | "fuji"
    | "cinematic"
    | "newspaper";
  /**
   * Enable color correction
   */
  enable_color_correction?: boolean;
  /**
   * Color temperature adjustment
   */
  temperature?: number;
  /**
   * Brightness adjustment
   */
  brightness?: number;
  /**
   * Contrast adjustment
   */
  contrast?: number;
  /**
   * Saturation adjustment
   */
  saturation?: number;
  /**
   * Gamma adjustment Default value: `1`
   */
  gamma?: number;
  /**
   * Enable chromatic aberration
   */
  enable_chromatic?: boolean;
  /**
   * Red channel shift amount
   */
  red_shift?: number;
  /**
   * Red channel shift direction Default value: `"horizontal"`
   */
  red_direction?: "horizontal" | "vertical";
  /**
   * Green channel shift amount
   */
  green_shift?: number;
  /**
   * Green channel shift direction Default value: `"horizontal"`
   */
  green_direction?: "horizontal" | "vertical";
  /**
   * Blue channel shift amount
   */
  blue_shift?: number;
  /**
   * Blue channel shift direction Default value: `"horizontal"`
   */
  blue_direction?: "horizontal" | "vertical";
  /**
   * Enable blur effect
   */
  enable_blur?: boolean;
  /**
   * Type of blur to apply Default value: `"gaussian"`
   */
  blur_type?: "gaussian" | "kuwahara";
  /**
   * Blur radius Default value: `3`
   */
  blur_radius?: number;
  /**
   * Sigma for Gaussian blur Default value: `1`
   */
  blur_sigma?: number;
  /**
   * Enable vignette effect
   */
  enable_vignette?: boolean;
  /**
   * Vignette strength (when enabled) Default value: `0.5`
   */
  vignette_strength?: number;
  /**
   * Enable parabolize effect
   */
  enable_parabolize?: boolean;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
  /**
   * Enable color tint effect
   */
  enable_tint?: boolean;
  /**
   * Tint strength Default value: `1`
   */
  tint_strength?: number;
  /**
   * Tint color mode Default value: `"sepia"`
   */
  tint_mode?:
    | "sepia"
    | "red"
    | "green"
    | "blue"
    | "cyan"
    | "magenta"
    | "yellow"
    | "purple"
    | "orange"
    | "warm"
    | "cool"
    | "lime"
    | "navy"
    | "vintage"
    | "rose"
    | "teal"
    | "maroon"
    | "peach"
    | "lavender"
    | "olive";
  /**
   * Enable dissolve effect
   */
  enable_dissolve?: boolean;
  /**
   * URL of second image for dissolve Default value: `""`
   */
  dissolve_image_url?: string | Blob | File;
  /**
   * Dissolve blend factor Default value: `0.5`
   */
  dissolve_factor?: number;
  /**
   * Enable dodge and burn effect
   */
  enable_dodge_burn?: boolean;
  /**
   * Dodge and burn intensity Default value: `0.5`
   */
  dodge_burn_intensity?: number;
  /**
   * Dodge and burn mode Default value: `"dodge"`
   */
  dodge_burn_mode?:
    | "dodge"
    | "burn"
    | "dodge_and_burn"
    | "burn_and_dodge"
    | "color_dodge"
    | "color_burn"
    | "linear_dodge"
    | "linear_burn";
  /**
   * Enable glow effect
   */
  enable_glow?: boolean;
  /**
   * Glow intensity Default value: `1`
   */
  glow_intensity?: number;
  /**
   * Glow blur radius Default value: `5`
   */
  glow_radius?: number;
  /**
   * Enable sharpen effect
   */
  enable_sharpen?: boolean;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
  /**
   * Enable solarize effect
   */
  enable_solarize?: boolean;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
  /**
   * Enable desaturation effect
   */
  enable_desaturate?: boolean;
  /**
   * Desaturation factor Default value: `1`
   */
  desaturate_factor?: number;
  /**
   * Desaturation method Default value: `"luminance (Rec.709)"`
   */
  desaturate_method?:
    | "luminance (Rec.709)"
    | "luminance (Rec.601)"
    | "average"
    | "lightness";
};
export type PostProcessingOutput = {
  /**
   * The processed images
   */
  images: Array<Image>;
};
export type PostProcessingParabolizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Parabolize coefficient Default value: `1`
   */
  parabolize_coeff?: number;
  /**
   * Vertex X position Default value: `0.5`
   */
  vertex_x?: number;
  /**
   * Vertex Y position Default value: `0.5`
   */
  vertex_y?: number;
};
export type PostProcessingParabolizeOutput = {
  /**
   * The processed images with parabolize effect
   */
  images: Array<Image>;
};
export type PostProcessingSharpenInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
};
export type PostProcessingSharpenOutput = {
  /**
   * The processed images with sharpen effect
   */
  images: Array<Image>;
};
export type PostProcessingSolarizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
};
export type PostProcessingSolarizeOutput = {
  /**
   * The processed images with solarize effect
   */
  images: Array<Image>;
};
export type PostProcessingVignetteInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Vignette strength Default value: `0.5`
   */
  vignette_strength?: number;
};
export type PostProcessingVignetteOutput = {
  /**
   * The processed images with vignette effect
   */
  images: Array<Image>;
};
export type ProcessedOutput = {
  /**
   * The processed images
   */
  images: Array<Image>;
};
export type ProductShotInput = {
  /**
   * The URL of the product shot to be placed in a lifestyle shot. If both image_url and image_file are provided, image_url will be used. Accepted formats are jpeg, jpg, png, webp. Maximum file size 12MB.
   */
  image_url: string | Blob | File;
  /**
   * Text description of the new scene or background for the provided product shot. Bria currently supports prompts in English only, excluding special characters.
   */
  scene_description?: string;
  /**
   * The URL of the reference image to be used for generating the new scene or background for the product shot. Use "" to leave empty.Either ref_image_url or scene_description has to be provided but not both. If both ref_image_url and ref_image_file are provided, ref_image_url will be used. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  ref_image_url?: string | Blob | File;
  /**
   * Whether to optimize the scene description Default value: `true`
   */
  optimize_description?: boolean;
  /**
   * The number of lifestyle product shots you would like to generate. You will get num_results x 10 results when placement_type=automatic and according to the number of required placements x num_results if placement_type=manual_placement. Default value: `1`
   */
  num_results?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * This parameter allows you to control the positioning of the product in the image. Choosing 'original' will preserve the original position of the product in the image. Choosing 'automatic' will generate results with the 10 recommended positions for the product. Choosing 'manual_placement' will allow you to select predefined positions (using the parameter 'manual_placement_selection'). Selecting 'manual_padding' will allow you to control the position and size of the image by defining the desired padding in pixels around the product. Default value: `"manual_placement"`
   */
  placement_type?:
    | "original"
    | "automatic"
    | "manual_placement"
    | "manual_padding";
  /**
   * This flag is only relevant when placement_type=original. If true, the output image retains the original input image's size; otherwise, the image is scaled to 1 megapixel (1MP) while preserving its aspect ratio.
   */
  original_quality?: boolean;
  /**
   * The desired size of the final product shot. For optimal results, the total number of pixels should be around 1,000,000. This parameter is only relevant when placement_type=automatic or placement_type=manual_placement.
   */
  shot_size?: Array<number>;
  /**
   * If you've selected placement_type=manual_placement, you should use this parameter to specify which placements/positions you would like to use from the list. You can select more than one placement in one request. Default value: `"bottom_center"`
   */
  manual_placement_selection?:
    | "upper_left"
    | "upper_right"
    | "bottom_left"
    | "bottom_right"
    | "right_center"
    | "left_center"
    | "upper_center"
    | "bottom_center"
    | "center_vertical"
    | "center_horizontal";
  /**
   * The desired padding in pixels around the product, when using placement_type=manual_padding. The order of the values is [left, right, top, bottom]. For optimal results, the total number of pixels, including padding, should be around 1,000,000. It is recommended to first use the product cutout API, get the cutout and understand the size of the result, and then define the required padding and use the cutout as an input for this API.
   */
  padding_values?: Array<number>;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ProductShotOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
};
export type ProfessionalPhotoOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type ProImageToVideoHailuo02Input = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type PromptInput = {
  /**
   * The URL of the image to remove objects from.
   */
  image_url: string | Blob | File;
  /**
   * Text description of the object to remove.
   */
  prompt: string;
  /**
   *  Default value: `"best_quality"`
   */
  model?: "low_quality" | "medium_quality" | "high_quality" | "best_quality";
  /**
   * Amount of pixels to expand the mask by. Range: 0-50 Default value: `15`
   */
  mask_expansion?: number;
};
export type ProTextToVideoHailuo02Input = {
  /**
   *
   */
  prompt: string;
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type Q1ImageToVideoOutput = {
  /**
   * The generated video using the Q1 model from a single image
   */
  video: File;
};
export type Q1ReferenceToVideoOutput = {
  /**
   * The generated video with consistent subjects from reference images using the Q1 model
   */
  video: File;
};
export type Q1StartEndToVideoOutput = {
  /**
   * The generated transition video between start and end frames using the Q1 model
   */
  video: File;
};
export type Q1TextToVideoOutput = {
  /**
   * The generated video using the Q1 model
   */
  video: File;
};
export type QueryInput = {
  /**
   * Image URL to be processed
   */
  image_url: string | Blob | File;
  /**
   * Type of task to perform Default value: `"caption"`
   */
  task_type?: "caption" | "query";
  /**
   * Prompt for query task
   */
  prompt: string;
  /**
   * Maximum number of tokens to generate Default value: `64`
   */
  max_tokens?: number;
};
export type QwenImageInput = {
  /**
   * The prompt to generate the image with
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The negative prompt for the generation Default value: `" "`
   */
  negative_prompt?: string;
  /**
   * Acceleration level for image generation. Options: 'none', 'regular', 'high'. Higher acceleration increases speed. 'regular' balances speed and quality. 'high' is recommended for images without text. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type QwenImageOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Ray2I2VOutput = {
  /**
   * URL of the generated video
   */
  video: File;
};
export type Ray2T2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type RealismInput = {
  /**
   * URL of the image to enhance with realism details.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.6`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type RealismOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type Recraft20bInput = {
  /**
   *
   */
  prompt: string;
  /**
   *  Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/enterprise"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/motion_blur"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/3d"
    | "digital_illustration/80s"
    | "digital_illustration/engraving_color"
    | "digital_illustration/glow"
    | "digital_illustration/grain"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/kawaii"
    | "digital_illustration/pixel_art"
    | "digital_illustration/psychedelic"
    | "digital_illustration/seamless"
    | "digital_illustration/voxel"
    | "digital_illustration/watercolor"
    | "vector_illustration/cartoon"
    | "vector_illustration/doodle_line_art"
    | "vector_illustration/engraving"
    | "vector_illustration/flat_2"
    | "vector_illustration/kawaii"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut"
    | "vector_illustration/seamless"
    | "icon/broken_line"
    | "icon/colored_outline"
    | "icon/colored_shapes"
    | "icon/colored_shapes_gradient"
    | "icon/doodle_fill"
    | "icon/doodle_offset_fill"
    | "icon/offset_fill"
    | "icon/outline"
    | "icon/outline_gradient"
    | "icon/uneven_fill";
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type Recraft20bOutput = {
  /**
   *
   */
  images: Array<File>;
};
export type Recraft20BTextToImageInput = {
  /**
   *
   */
  prompt: string;
  /**
   *  Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/enterprise"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/motion_blur"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/3d"
    | "digital_illustration/80s"
    | "digital_illustration/engraving_color"
    | "digital_illustration/glow"
    | "digital_illustration/grain"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/kawaii"
    | "digital_illustration/pixel_art"
    | "digital_illustration/psychedelic"
    | "digital_illustration/seamless"
    | "digital_illustration/voxel"
    | "digital_illustration/watercolor"
    | "vector_illustration/cartoon"
    | "vector_illustration/doodle_line_art"
    | "vector_illustration/engraving"
    | "vector_illustration/flat_2"
    | "vector_illustration/kawaii"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut"
    | "vector_illustration/seamless"
    | "icon/broken_line"
    | "icon/colored_outline"
    | "icon/colored_shapes"
    | "icon/colored_shapes_gradient"
    | "icon/doodle_fill"
    | "icon/doodle_offset_fill"
    | "icon/offset_fill"
    | "icon/outline"
    | "icon/outline_gradient"
    | "icon/uneven_fill";
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type RecraftUpscaleCreativeInput = {
  /**
   * The URL of the image to be upscaled. Must be in PNG format.
   */
  image_url: string | Blob | File;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type RecraftUpscaleCreativeOutput = {
  /**
   * The upscaled image.
   */
  image: File;
};
export type RecraftUpscaleCrispInput = {
  /**
   * The URL of the image to be upscaled. Must be in PNG format.
   */
  image_url: string | Blob | File;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type RecraftUpscaleCrispOutput = {
  /**
   * The upscaled image.
   */
  image: File;
};
export type RecraftV3CreateStyleInput = {
  /**
   * URL to zip archive with images, use PNG format. Maximum 5 images are allowed.
   */
  images_data_url: string | Blob | File;
  /**
   * The base style of the generated images, this topic is covered above. Default value: `"digital_illustration"`
   */
  base_style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
};
export type RecraftV3CreateStyleOutput = {
  /**
   * The ID of the created style, this ID can be used to reference the style in the future.
   */
  style_id: string;
};
export type RecraftV3ImageToImageInput = {
  /**
   * A text description of areas to change.
   */
  prompt: string;
  /**
   * The URL of the image to modify. Must be less than 5 MB in size, have resolution less than 16 MP and max dimension less than 4096 pixels.
   */
  image_url: string | Blob | File;
  /**
   * Defines the difference with the original image, should lie in [0, 1], where 0 means almost identical, and 1 means miserable similarity Default value: `0.5`
   */
  strength?: number;
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
  /**
   * A text description of undesired elements on an image
   */
  negative_prompt?: string;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type RecraftV3ImageToImageOutput = {
  /**
   * The generated images
   */
  images: Array<File>;
};
export type RecraftV3TextToImageInput = {
  /**
   *
   */
  prompt: string;
  /**
   *  Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The style of the generated images. Vector images cost 2X as much. Default value: `"realistic_image"`
   */
  style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
  /**
   * An array of preferable colors
   */
  colors?: Array<RGBColor>;
  /**
   * The ID of the custom style reference (optional)
   */
  style_id?: string;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
};
export type RecraftV3TextToImageOutput = {
  /**
   *
   */
  images: Array<File>;
};
export type RecraftVectorizeInput = {
  /**
   * The URL of the image to be vectorized. Must be in PNG, JPG or WEBP format, less than 5 MB in size, have resolution less than 16 MP and max dimension less than 4096 pixels, min dimension more than 256 pixels.
   */
  image_url: string | Blob | File;
};
export type RecraftVectorizeOutput = {
  /**
   * The vectorized image.
   */
  image: File;
};
export type ReferenceToVideoOutput = {
  /**
   * The generated video with consistent subjects from reference images
   */
  video: File;
};
export type ReframeInput = {
  /**
   * URL of the old or damaged photo to restore.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The desired aspect ratio for the reframed image. Default value: `"16:9"`
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ReframeOutput = {
  /**
   * URL of the reframed video
   */
  video: File;
};
export type ReimagineInput = {
  /**
   * The prompt you would like to use to generate images.
   */
  prompt: string;
  /**
   * The URL of the structure reference image. Use "" to leave empty. Accepted formats are jpeg, jpg, png, webp. Default value: `""`
   */
  structure_image_url?: string | Blob | File;
  /**
   * The influence of the structure reference on the generated image. Default value: `0.75`
   */
  structure_ref_influence?: number;
  /**
   * How many images you would like to generate. When using any Guidance Method, Value is set to 1. Default value: `1`
   */
  num_results?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * Whether to use the fast model Default value: `true`
   */
  fast?: boolean;
  /**
   * The number of iterations the model goes through to refine the generated image. This parameter is optional. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type ReimagineOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   * Seed value used for generation.
   */
  seed: number;
};
export type RembgEnhanceInput = {
  /**
   * URL of the input image
   */
  image_url: string | Blob | File;
};
export type RembgEnhanceOutput = {
  /**
   * The segmented output image
   */
  image: File;
};
export type RemixImageInput = {
  /**
   * The prompt to remix the image with
   */
  prompt: string;
  /**
   * The image URL to remix
   */
  image_url: string | Blob | File;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Strength of the input image in the remix Default value: `0.8`
   */
  strength?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type RetouchInput = {
  /**
   * URL of the image to retouch.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type RetouchOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type RFInversionInput = {
  /**
   * The prompt to edit the image with
   */
  prompt: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The LoRAs to use for the image generation which use a control image. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  control_loras?: Array<ControlLoraWeight>;
  /**
   * The controlnets to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnets?: Array<ControlNet>;
  /**
   * The controlnet unions to use for the image generation. Only one controlnet is supported at the moment.
   */
  controlnet_unions?: Array<ControlNetUnion>;
  /**
   * EasyControl Inputs to use for image generation.
   */
  easycontrols?: Array<EasyControlWeight>;
  /**
   * Use an image input to influence the generation. Can be used to fill images in masked areas.
   */
  fill_image?: ImageFillInput;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Uses CFG-zero init sampling as in https://arxiv.org/abs/2503.18886.
   */
  use_cfg_zero?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. This is always set to 1 for streaming output. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * URL of Image for Reference-Only
   */
  reference_image_url?: string | Blob | File;
  /**
   * Strength of reference_only generation. Only used if a reference image is provided. Default value: `0.65`
   */
  reference_strength?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to bestarted.
   */
  reference_start?: number;
  /**
   * The percentage of the total timesteps when the reference guidance is to be ended. Default value: `1`
   */
  reference_end?: number;
  /**
   * Base shift for the scheduled timesteps Default value: `0.5`
   */
  base_shift?: number;
  /**
   * Max shift for the scheduled timesteps Default value: `1.15`
   */
  max_shift?: number;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Specifies whether beta sigmas ought to be used.
   */
  use_beta_schedule?: boolean;
  /**
   * Sigmas schedule for the denoising process.
   */
  sigma_schedule?: "sgm_uniform";
  /**
   * Scheduler for the denoising process. Default value: `"euler"`
   */
  scheduler?: "euler" | "dpmpp_2m";
  /**
   * Negative prompt to steer the image generation away from unwanted features.
   * By default, we will be using NAG for processing the negative prompt. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The scale for NAG. Higher values will result in a image that is more distant
   * to the negative prompt. Default value: `3`
   */
  nag_scale?: number;
  /**
   * The tau for NAG. Controls the normalization of the hidden state.
   * Higher values will result in a less aggressive normalization,
   * but may also lead to unexpected changes with respect to the original image.
   * Not recommended to change this value. Default value: `2.5`
   */
  nag_tau?: number;
  /**
   * The alpha value for NAG. This value is used as a final weighting
   * factor for steering the normalized guidance (positive and negative prompts)
   * in the direction of the positive prompt. Higher values will result in less
   * steering on the normalized guidance where lower values will result in
   * considering the positive prompt guidance more. Default value: `0.25`
   */
  nag_alpha?: number;
  /**
   * The proportion of steps to apply NAG. After the specified proportion
   * of steps has been iterated, the remaining steps will use original
   * attention processors in FLUX. Default value: `0.25`
   */
  nag_end?: number;
  /**
   * URL of image to be edited
   */
  image_url: string | Blob | File;
  /**
   * The controller guidance (gamma) used in the creation of structured noise. Default value: `0.6`
   */
  controller_guidance_forward?: number;
  /**
   * The controller guidance (eta) used in the denoising process.Using values closer to 1 will result in an image closer to input. Default value: `0.75`
   */
  controller_guidance_reverse?: number;
  /**
   * Timestep to start guidance during reverse process.
   */
  reverse_guidance_start?: number;
  /**
   * Timestep to stop guidance during reverse process. Default value: `8`
   */
  reverse_guidance_end?: number;
  /**
   * Scheduler for applying reverse guidance. Default value: `"constant"`
   */
  reverse_guidance_schedule?:
    | "constant"
    | "linear_increase"
    | "linear_decrease";
};
export type RIFEImageInput = {
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg";
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the function will wait for images to be generated and uploaded before returning. This will increase the response time but ensures that the images are ready for use immediately without going through the CDN. Does not apply if output_type is 'video'.
   */
  sync_mode?: boolean;
};
export type RIFEImageOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<Image>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: File;
};
export type RifeInput = {
  /**
   * The URL of the first image to use as the starting point for interpolation.
   */
  start_image_url: string | Blob | File;
  /**
   * The URL of the second image to use as the ending point for interpolation.
   */
  end_image_url: string | Blob | File;
  /**
   * The type of output to generate; either individual images or a video. Default value: `"images"`
   */
  output_type?: "images" | "video";
  /**
   * The format of the output images. Only applicable if output_type is 'images'. Default value: `"jpeg"`
   */
  output_format?: "png" | "jpeg";
  /**
   * The number of frames to generate between the input images. Default value: `1`
   */
  num_frames?: number;
  /**
   * Whether to include the start image in the output.
   */
  include_start?: boolean;
  /**
   * Whether to include the end image in the output.
   */
  include_end?: boolean;
  /**
   * Frames per second for the output video. Only applicable if output_type is 'video'. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the function will wait for images to be generated and uploaded before returning. This will increase the response time but ensures that the images are ready for use immediately without going through the CDN. Does not apply if output_type is 'video'.
   */
  sync_mode?: boolean;
};
export type RifeOutput = {
  /**
   * The generated frames as individual images.
   */
  images?: Array<Image>;
  /**
   * The generated video file, if output_type is 'video'.
   */
  video?: File;
};
export type RifeVideoInput = {
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
};
export type RIFEVideoInput = {
  /**
   * The URL of the video to use for interpolation.
   */
  video_url: string | Blob | File;
  /**
   * The number of frames to generate between the input video frames. Default value: `1`
   */
  num_frames?: number;
  /**
   * If True, the input video will be split into scenes before interpolation. This removes smear frames between scenes, but can result in false positives if the scene detection is not accurate. If False, the entire video will be treated as a single scene.
   */
  use_scene_detection?: boolean;
  /**
   * If True, the function will use the calculated FPS of the input video multiplied by the number of frames to determine the output FPS. If False, the passed FPS will be used. Default value: `true`
   */
  use_calculated_fps?: boolean;
  /**
   * Frames per second for the output video. Only applicable if use_calculated_fps is False. Default value: `8`
   */
  fps?: number;
  /**
   * If True, the final frame will be looped back to the first frame to create a seamless loop. If False, the final frame will not loop back.
   */
  loop?: boolean;
};
export type RifeVideoOutput = {
  /**
   * The generated video file with interpolated frames.
   */
  video: File;
};
export type RIFEVideoOutput = {
  /**
   * The generated video file with interpolated frames.
   */
  video: File;
};
export type RundiffusionPhotoFluxInput = {
  /**
   * LoRA Scale of the photo lora model Default value: `0.75`
   */
  photo_lora_scale?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type RundiffusionPhotoFluxOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type Sa2va4bImageInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * Url for the Input image.
   */
  image_url: string | Blob | File;
};
export type Sa2va4bImageOutput = {
  /**
   * Generated output
   */
  output: string;
  /**
   * Dictionary of label: mask image
   */
  masks: Array<Image>;
};
export type Sa2va4bVideoInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * The URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * Number of frames to sample from the video. If not provided, all frames are sampled.
   */
  num_frames_to_sample?: number;
};
export type Sa2va4bVideoOutput = {
  /**
   * Generated output
   */
  output: string;
  /**
   * Dictionary of label: mask image
   */
  masks: Array<Image>;
};
export type Sa2va8bImageInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * Url for the Input image.
   */
  image_url: string | Blob | File;
};
export type Sa2va8bImageOutput = {
  /**
   * Generated output
   */
  output: string;
  /**
   * Dictionary of label: mask image
   */
  masks: Array<Image>;
};
export type Sa2va8bVideoInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * The URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * Number of frames to sample from the video. If not provided, all frames are sampled.
   */
  num_frames_to_sample?: number;
};
export type Sa2va8bVideoOutput = {
  /**
   * Generated output
   */
  output: string;
  /**
   * Dictionary of label: mask image
   */
  masks: Array<Image>;
};
export type SadTalkerInput = {
  /**
   * URL of the source image
   */
  source_image_url: string | Blob | File;
  /**
   * URL of the driven audio
   */
  driven_audio_url: string | Blob | File;
  /**
   * The style of the pose
   */
  pose_style?: number;
  /**
   * The resolution of the face model Default value: `"256"`
   */
  face_model_resolution?: "256" | "512";
  /**
   * The scale of the expression Default value: `1`
   */
  expression_scale?: number;
  /**
   * The type of face enhancer to use
   */
  face_enhancer?: "gfpgan";
  /**
   * Whether to use still mode. Fewer head motion, works with preprocess `full`.
   */
  still_mode?: boolean;
  /**
   * The type of preprocessing to use Default value: `"crop"`
   */
  preprocess?: "crop" | "extcrop" | "resize" | "full" | "extfull";
};
export type SadtalkerReferenceInput = {
  /**
   * URL of the source image
   */
  source_image_url: string | Blob | File;
  /**
   * URL of the driven audio
   */
  driven_audio_url: string | Blob | File;
  /**
   * URL of the reference video
   */
  reference_pose_video_url: string | Blob | File;
  /**
   * The style of the pose
   */
  pose_style?: number;
  /**
   * The resolution of the face model Default value: `"256"`
   */
  face_model_resolution?: "256" | "512";
  /**
   * The scale of the expression Default value: `1`
   */
  expression_scale?: number;
  /**
   * The type of face enhancer to use
   */
  face_enhancer?: "gfpgan";
  /**
   * Whether to use still mode. Fewer head motion, works with preprocess `full`.
   */
  still_mode?: boolean;
  /**
   * The type of preprocessing to use Default value: `"crop"`
   */
  preprocess?: "crop" | "extcrop" | "resize" | "full" | "extfull";
};
export type SadtalkerReferenceOutput = {
  /**
   * URL of the generated video
   */
  video: File;
};
export type Sam2AutoSegmentInput = {
  /**
   * URL of the image to be automatically segmented
   */
  image_url: string | Blob | File;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Number of points to sample along each side of the image. Default value: `32`
   */
  points_per_side?: number;
  /**
   * Threshold for predicted IOU score. Default value: `0.88`
   */
  pred_iou_thresh?: number;
  /**
   * Threshold for stability score. Default value: `0.95`
   */
  stability_score_thresh?: number;
  /**
   * Minimum area of a mask region. Default value: `100`
   */
  min_mask_region_area?: number;
};
export type Sam2AutoSegmentOutput = {
  /**
   * Combined segmentation mask.
   */
  combined_mask: Image;
  /**
   * Individual segmentation masks.
   */
  individual_masks: Array<Image>;
};
export type SAM2ImageInput = {
  /**
   * URL of the image to be segmented
   */
  image_url: string | Blob | File;
  /**
   * List of prompts to segment the image
   */
  prompts?: Array<PointPrompt>;
  /**
   * Coordinates for boxes
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * Apply the mask on the image.
   */
  apply_mask?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
};
export type SAM2ImageOutput = {
  /**
   * Segmented image.
   */
  image: Image;
};
export type SAM2RLEFileOutput = {
  /**
   * Run Length Encoding of the mask.
   */
  rle: File;
};
export type SAM2RLEOutput = {
  /**
   * Run Length Encoding of the mask.
   */
  rle: string | Array<string>;
};
export type SAM2VideoOutput = {
  /**
   * The segmented video.
   */
  video: File;
};
export type SAM2VideoRLEInput = {
  /**
   * The URL of the video to be segmented.
   */
  video_url: string | Blob | File;
  /**
   * The URL of the mask to be applied initially.
   */
  mask_url?: string | Blob | File;
  /**
   * List of prompts to segment the video
   */
  prompts?: Array<PointPrompt>;
  /**
   * Coordinates for boxes
   */
  box_prompts?: Array<BoxPrompt>;
  /**
   * Apply the mask on the video.
   */
  apply_mask?: boolean;
};
export type SanaSprintInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `2`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The style to generate the image in. Default value: `"(No style)"`
   */
  style_name?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
};
export type SanaSprintOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type SanaV1516bInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `18`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The style to generate the image in. Default value: `"(No style)"`
   */
  style_name?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
};
export type SanaV1516bOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type SanaV1548bInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `18`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The style to generate the image in. Default value: `"(No style)"`
   */
  style_name?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
};
export type SanaV1548bOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type SceneCompositionInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Describe the scene where you want to place the subject. Default value: `"enchanted forest"`
   */
  prompt?: string;
};
export type SceneCompositionOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type SchnellFlux1ReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type SchnellFlux1TextToImageInput = {
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type SchnellReduxInput = {
  /**
   * The URL of the image to generate an image from.
   */
  image_url: string | Blob | File;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type SchnellTextToImageInput = {
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type SeedanceImageToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
  /**
   * The URL of the image the video ends with. Defaults to None.
   */
  end_image_url?: string | Blob | File;
};
export type SeedanceProI2VVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type SeedanceProImageToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
  /**
   * The URL of the image used to generate video
   */
  image_url: string | Blob | File;
};
export type SeedanceProT2VVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type SeedanceProTextToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "21:9" | "16:9" | "4:3" | "1:1" | "3:4" | "9:16";
  /**
   * Video resolution - 480p for faster generation, 720p for balance, 1080p for higher quality Default value: `"1080p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceTextToVideoInput = {
  /**
   * The text prompt used to generate the video
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "4:3" | "1:1" | "3:4" | "9:16" | "9:21";
  /**
   * Video resolution - 480p for faster generation, 720p for higher quality Default value: `"720p"`
   */
  resolution?: "480p" | "720p" | "1080p";
  /**
   * Duration of the video in seconds Default value: `"5"`
   */
  duration?: "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
  /**
   * Whether to fix the camera position
   */
  camera_fixed?: boolean;
  /**
   * Random seed to control video generation. Use -1 for random.
   */
  seed?: number;
};
export type SeedanceVideoOutput = {
  /**
   * Generated video file
   */
  video: File;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type SeedDreamInput = {
  /**
   * The text prompt used to generate the image
   */
  prompt: string;
  /**
   * Use for finer control over the output image size. Will be used over aspect_ratio, if both are provided. Width and height must be between 512 and 2048.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * Number of images to generate Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type SeedDreamOutput = {
  /**
   * Generated images
   */
  images: Array<Image>;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type SeedEditInput = {
  /**
   * The text prompt used to edit the image
   */
  prompt: string;
  /**
   * URL of the image to be edited.
   */
  image_url: string | Blob | File;
  /**
   * Controls how closely the output image aligns with the input prompt. Higher values mean stronger prompt correlation. Default value: `0.5`
   */
  guidance_scale?: number;
  /**
   * Random seed to control the stochasticity of image generation.
   */
  seed?: number;
};
export type SeedEditOutput = {
  /**
   * Generated image
   */
  image: Image;
  /**
   * Seed used for generation
   */
  seed: number;
};
export type SharpenInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Type of sharpening to apply Default value: `"basic"`
   */
  sharpen_mode?: "basic" | "smart" | "cas";
  /**
   * Sharpen radius (for basic mode) Default value: `1`
   */
  sharpen_radius?: number;
  /**
   * Sharpen strength (for basic mode) Default value: `1`
   */
  sharpen_alpha?: number;
  /**
   * Noise radius for smart sharpen Default value: `7`
   */
  noise_radius?: number;
  /**
   * Edge preservation factor Default value: `0.75`
   */
  preserve_edges?: number;
  /**
   * Smart sharpen strength Default value: `5`
   */
  smart_sharpen_strength?: number;
  /**
   * Smart sharpen blend ratio Default value: `0.5`
   */
  smart_sharpen_ratio?: number;
  /**
   * CAS sharpening amount Default value: `0.8`
   */
  cas_amount?: number;
};
export type SharpenOutput = {
  /**
   * The processed images with sharpen effect
   */
  images: Array<Image>;
};
export type SkyRaccoonInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality.
   */
  turbo_mode?: boolean;
};
export type SkyRaccoonOutput = {
  /**
   * The generated image file.
   */
  image: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type SkyreelsI2vInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * URL of the image input.
   */
  image_url: string | Blob | File;
  /**
   * Random seed for generation. If not provided, a random seed will be used.
   */
  seed?: number;
  /**
   * Guidance scale for generation (between 1.0 and 20.0) Default value: `6`
   */
  guidance_scale?: number;
  /**
   * Number of denoising steps (between 1 and 50). Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Negative prompt to guide generation away from certain attributes.
   */
  negative_prompt?: string;
  /**
   * Aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
};
export type SkyreelsI2vOutput = {
  /**
   *
   */
  video: File;
  /**
   * The seed used for generation
   */
  seed: number;
};
export type SmartTurnInput = {
  /**
   * The URL of the audio file to be processed.
   */
  audio_url: string | Blob | File;
};
export type SmartTurnOutput = {
  /**
   * The predicted turn type. 1 for Complete, 0 for Incomplete.
   */
  prediction: number;
  /**
   * The probability of the predicted turn type.
   */
  probability: number;
  /**
   * The metrics of the inference.
   */
  metrics: any;
};
export type SolarizeInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Solarize threshold Default value: `0.5`
   */
  solarize_threshold?: number;
};
export type SolarizeOutput = {
  /**
   * The processed images with solarize effect
   */
  images: Array<Image>;
};
export type SoundEffectOutput = {
  /**
   * The video with added sound effects
   */
  video: File;
};
export type SoundEffectsGeneratorInput = {
  /**
   * The prompt to generate SFX.
   */
  prompt: string;
  /**
   * The duration of the generated SFX in seconds.
   */
  duration: number;
};
export type SoundEffectsGeneratorOutput = {
  /**
   * The generated SFX
   */
  audio_file: File;
};
export type SpanishOutput = {
  /**
   * The generated music
   */
  audio: File;
};
export type SpeechOutput = {
  /**
   * The partial or final transcription output from Canary
   */
  output: string;
  /**
   * Indicates if this is a partial (in-progress) transcript
   */
  partial?: boolean;
};
export type SpeechToTextInput = {
  /**
   * Local filesystem path (or remote URL) to a long audio file
   */
  audio_url: string | Blob | File;
  /**
   * Whether to use Canary's built-in punctuation & capitalization Default value: `true`
   */
  use_pnc?: boolean;
};
export type SpeechToTextOutput = {
  /**
   * The partial or final transcription output from Canary
   */
  output: string;
  /**
   * Indicates if this is a partial (in-progress) transcript
   */
  partial?: boolean;
};
export type SpeechToTextStreamInput = {
  /**
   * Local filesystem path (or remote URL) to a long audio file
   */
  audio_url: string | Blob | File;
  /**
   * Whether to use Canary's built-in punctuation & capitalization Default value: `true`
   */
  use_pnc?: boolean;
};
export type SpeechToTextTurboInput = {
  /**
   * Local filesystem path (or remote URL) to a long audio file
   */
  audio_url: string | Blob | File;
  /**
   * Whether to use Canary's built-in punctuation & capitalization Default value: `true`
   */
  use_pnc?: boolean;
};
export type SpeechToTextTurboOutput = {
  /**
   * The partial or final transcription output from Canary
   */
  output: string;
  /**
   * Indicates if this is a partial (in-progress) transcript
   */
  partial?: boolean;
};
export type SpeechToTextTurboStreamInput = {
  /**
   * Local filesystem path (or remote URL) to a long audio file
   */
  audio_url: string | Blob | File;
  /**
   * Whether to use Canary's built-in punctuation & capitalization Default value: `true`
   */
  use_pnc?: boolean;
};
export type SprintInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `2`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The style to generate the image in. Default value: `"(No style)"`
   */
  style_name?:
    | "(No style)"
    | "Cinematic"
    | "Photographic"
    | "Anime"
    | "Manga"
    | "Digital Art"
    | "Pixel art"
    | "Fantasy art"
    | "Neonpunk"
    | "3D Model";
};
export type StableDiffusionV35LargeInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * ControlNet for inference.
   */
  controlnet?: ControlNet;
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * IP-Adapter to use during inference.
   */
  ip_adapter?: IPAdapter;
};
export type StableDiffusionV35LargeOutput = {
  /**
   * The generated image files info.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type StandardImageToVideoHailuo02Input = {
  /**
   *
   */
  prompt: string;
  /**
   *
   */
  image_url: string | Blob | File;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
  /**
   * The resolution of the generated video. Default value: `"768P"`
   */
  resolution?: "512P" | "768P";
};
export type StandardTextToVideoHailuo02Input = {
  /**
   *
   */
  prompt: string;
  /**
   * The duration of the video in seconds. 10 seconds videos are not supported for 1080p resolution. Default value: `"6"`
   */
  duration?: "6" | "10";
  /**
   * Whether to use the model's prompt optimizer Default value: `true`
   */
  prompt_optimizer?: boolean;
};
export type StartEndToVideoOutput = {
  /**
   * The generated transition video between start and end frames
   */
  video: File;
};
export type StarVectorInput = {
  /**
   * URL of image to be used for relighting
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
};
export type StarVectorOutput = {
  /**
   * The generated image file info.
   */
  image: File;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type Step1xEditInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The image URL to generate an image from. Needs to match the dimensions of the mask.
   */
  image_url: string | Blob | File;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type Step1xEditOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type StreamingDevTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type StreamingFastTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `16`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type StreamingFlux1Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingFullTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The size of the generated image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * A list of LoRAs to apply to the model. Each LoRA specifies its path, scale, and optional weight name.
   */
  loras?: Array<LoraWeight>;
};
export type StreamingInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingKontextEditInput = {
  /**
   * The prompt to edit the image.
   */
  prompt: string;
  /**
   * The URL of the image to edit.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * Determines how the output resolution is set for image editing.
   * - `auto`: The model selects an optimal resolution from a predefined set that best matches the input image's aspect ratio. This is the recommended setting for most use cases as it's what the model was trained on.
   * - `match_input`: The model will attempt to use the same resolution as the input image. The resolution will be adjusted to be compatible with the model's requirements (e.g. dimensions must be multiples of 16 and within supported limits).
   * Apart from these, a few aspect ratios are also supported. Default value: `"match_input"`
   */
  resolution_mode?:
    | "auto"
    | "match_input"
    | "1:1"
    | "16:9"
    | "21:9"
    | "3:2"
    | "2:3"
    | "4:5"
    | "5:4"
    | "3:4"
    | "4:3"
    | "9:16"
    | "9:21";
};
export type StreamingKontextImg2ImgInput = {
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The URL of the image for image-to-image.
   */
  image_url: string | Blob | File;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingKontextInpaintInput = {
  /**
   * The URL of the image to be inpainted.
   */
  image_url: string | Blob | File;
  /**
   * The prompt for the image to image task.
   */
  prompt: string;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The LoRAs to use for the image generation. You can use any number of LoRAs
   * and they will be merged together to generate the final image.
   */
  loras?: Array<LoraWeight>;
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
  /**
   * The URL of the reference image for inpainting.
   */
  reference_image_url: string | Blob | File;
  /**
   * The URL of the mask for inpainting.
   */
  mask_url: string | Blob | File;
  /**
   * The strength of the initial image. Higher strength values are better for this model. Default value: `0.88`
   */
  strength?: number;
};
export type StreamingKontextInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * Output format Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingKreaFlux1Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"regular"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingKreaInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The speed of the generation. The higher the speed, the faster the generation. Default value: `"none"`
   */
  acceleration?: "none" | "regular" | "high";
};
export type StreamingTextToImageInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The size of the generated image. Default value: `landscape_4_3`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The system prompt to use. Default value: `"You are an assistant designed to generate superior images with the superior degree of image-text alignment based on textual prompts or user prompts."`
   */
  system_prompt?: string;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `4`
   */
  guidance_scale?: number;
  /**
   * The number of inference steps to perform. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to apply normalization-based guidance scale. Default value: `true`
   */
  cfg_normalization?: boolean;
  /**
   * The ratio of the timestep interval to apply normalization-based guidance scale. Default value: `1`
   */
  cfg_trunc_ratio?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type STSInput = {
  /**
   * URL to the source audio file to be voice-converted.
   */
  source_audio_url: string | Blob | File;
  /**
   * The voice to use for the speech-to-speech request. If neither target_voice nor target_voice_audio_url are provided, a random target voice will be used.
   */
  target_voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
  /**
   * URL to the audio file which represents the voice of the output audio. If provided, this will override the target_voice setting. If neither target_voice nor target_voice_audio_url are provided, the default target voice will be used.
   */
  target_voice_audio_url?: string | Blob | File;
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
};
export type STSOutput = {
  /**
   * The generated voice-converted audio file.
   */
  audio: Audio;
};
export type StyleReferenceInput = {
  /**
   * URL to zip archive with images, use PNG format. Maximum 5 images are allowed.
   */
  images_data_url: string | Blob | File;
  /**
   * The base style of the generated images, this topic is covered above. Default value: `"digital_illustration"`
   */
  base_style?:
    | "any"
    | "realistic_image"
    | "digital_illustration"
    | "vector_illustration"
    | "realistic_image/b_and_w"
    | "realistic_image/hard_flash"
    | "realistic_image/hdr"
    | "realistic_image/natural_light"
    | "realistic_image/studio_portrait"
    | "realistic_image/enterprise"
    | "realistic_image/motion_blur"
    | "realistic_image/evening_light"
    | "realistic_image/faded_nostalgia"
    | "realistic_image/forest_life"
    | "realistic_image/mystic_naturalism"
    | "realistic_image/natural_tones"
    | "realistic_image/organic_calm"
    | "realistic_image/real_life_glow"
    | "realistic_image/retro_realism"
    | "realistic_image/retro_snapshot"
    | "realistic_image/urban_drama"
    | "realistic_image/village_realism"
    | "realistic_image/warm_folk"
    | "digital_illustration/pixel_art"
    | "digital_illustration/hand_drawn"
    | "digital_illustration/grain"
    | "digital_illustration/infantile_sketch"
    | "digital_illustration/2d_art_poster"
    | "digital_illustration/handmade_3d"
    | "digital_illustration/hand_drawn_outline"
    | "digital_illustration/engraving_color"
    | "digital_illustration/2d_art_poster_2"
    | "digital_illustration/antiquarian"
    | "digital_illustration/bold_fantasy"
    | "digital_illustration/child_book"
    | "digital_illustration/child_books"
    | "digital_illustration/cover"
    | "digital_illustration/crosshatch"
    | "digital_illustration/digital_engraving"
    | "digital_illustration/expressionism"
    | "digital_illustration/freehand_details"
    | "digital_illustration/grain_20"
    | "digital_illustration/graphic_intensity"
    | "digital_illustration/hard_comics"
    | "digital_illustration/long_shadow"
    | "digital_illustration/modern_folk"
    | "digital_illustration/multicolor"
    | "digital_illustration/neon_calm"
    | "digital_illustration/noir"
    | "digital_illustration/nostalgic_pastel"
    | "digital_illustration/outline_details"
    | "digital_illustration/pastel_gradient"
    | "digital_illustration/pastel_sketch"
    | "digital_illustration/pop_art"
    | "digital_illustration/pop_renaissance"
    | "digital_illustration/street_art"
    | "digital_illustration/tablet_sketch"
    | "digital_illustration/urban_glow"
    | "digital_illustration/urban_sketching"
    | "digital_illustration/vanilla_dreams"
    | "digital_illustration/young_adult_book"
    | "digital_illustration/young_adult_book_2"
    | "vector_illustration/bold_stroke"
    | "vector_illustration/chemistry"
    | "vector_illustration/colored_stencil"
    | "vector_illustration/contour_pop_art"
    | "vector_illustration/cosmics"
    | "vector_illustration/cutout"
    | "vector_illustration/depressive"
    | "vector_illustration/editorial"
    | "vector_illustration/emotional_flat"
    | "vector_illustration/infographical"
    | "vector_illustration/marker_outline"
    | "vector_illustration/mosaic"
    | "vector_illustration/naivector"
    | "vector_illustration/roundish_flat"
    | "vector_illustration/segmented_colors"
    | "vector_illustration/sharp_contrast"
    | "vector_illustration/thin"
    | "vector_illustration/vector_photo"
    | "vector_illustration/vivid_shapes"
    | "vector_illustration/engraving"
    | "vector_illustration/line_art"
    | "vector_illustration/line_circuit"
    | "vector_illustration/linocut";
};
export type StyleReferenceOutput = {
  /**
   * The ID of the created style, this ID can be used to reference the style in the future.
   */
  style_id: string;
};
export type StyleTransferInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The artistic style to apply. Default value: `"Van Gogh's Starry Night"`
   */
  prompt?: string;
};
export type StyleTransferOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type SubjectCustomizeInput = {
  /**
   * The text prompt describing what you want to see, using [1] to reference the subject
   */
  prompt: string;
  /**
   * Type of subject in the reference images
   */
  subject_type: "product" | "animal";
  /**
   * 1-4 reference images of the subject to customize
   */
  reference_images: Array<ReferenceImage>;
  /**
   * Optional description of the subject in the reference images
   */
  subject_description?: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type SubjectReferenceOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Swin2srInput = {
  /**
   * URL of image to be used for image enhancement
   */
  image_url: string | Blob | File;
  /**
   * seed to be used for generation
   */
  seed?: number;
  /**
   * Task to perform Default value: `"classical_sr"`
   */
  task?: "classical_sr" | "compressed_sr" | "real_sr";
};
export type Swin2srOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
};
export type Switti512Input = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of top-k tokens to sample from. Default value: `400`
   */
  sampling_top_k?: number;
  /**
   * The top-p probability to sample from. Default value: `0.95`
   */
  sampling_top_p?: number;
  /**
   * Smoothing with Gumbel softmax sampling Default value: `true`
   */
  more_smooth?: boolean;
  /**
   * More diverse sampling
   */
  more_diverse?: boolean;
  /**
   * Smoothing starting scale Default value: `2`
   */
  smooth_start_si?: number;
  /**
   * Disable CFG starting scale Default value: `8`
   */
  turn_off_cfg_start_si?: number;
  /**
   * Temperature after disabling CFG Default value: `0.1`
   */
  last_scale_temp?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `6`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type Switti512Output = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type SwittiInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of top-k tokens to sample from. Default value: `400`
   */
  sampling_top_k?: number;
  /**
   * The top-p probability to sample from. Default value: `0.95`
   */
  sampling_top_p?: number;
  /**
   * Smoothing with Gumbel softmax sampling Default value: `true`
   */
  more_smooth?: boolean;
  /**
   * More diverse sampling
   */
  more_diverse?: boolean;
  /**
   * Smoothing starting scale Default value: `2`
   */
  smooth_start_si?: number;
  /**
   * Disable CFG starting scale Default value: `8`
   */
  turn_off_cfg_start_si?: number;
  /**
   * Temperature after disabling CFG Default value: `0.1`
   */
  last_scale_temp?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `6`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
};
export type SwittiOutput = {
  /**
   * The generated images
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used for generating the image.
   */
  prompt: string;
};
export type SyncLipsyncInput = {
  /**
   * The model to use for lipsyncing Default value: `"lipsync-1.9.0-beta"`
   */
  model?: "lipsync-1.8.0" | "lipsync-1.7.1" | "lipsync-1.9.0-beta";
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
};
export type SyncLipsyncOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type SyncLipsyncV2Input = {
  /**
   * URL of the input video
   */
  video_url: string | Blob | File;
  /**
   * URL of the input audio
   */
  audio_url: string | Blob | File;
  /**
   * Lipsync mode when audio and video durations are out of sync. Default value: `"cut_off"`
   */
  sync_mode?: "cut_off" | "loop" | "bounce" | "silence" | "remap";
};
export type SyncLipsyncV2Output = {
  /**
   * The generated video
   */
  video: File;
};
export type T2VDirectorOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type T2VLiveOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type T2VOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type TavusHummingbirdLipsyncV0Input = {
  /**
   * A direct link to the video that will be modified. This should be a publicly accessible / presigned S3 URL.
   */
  video_url: string | Blob | File;
  /**
   * A direct link to the audio file that will be synchronized with the video. This should be a publicly accessible / presigned S3 URL.
   */
  audio_url: string | Blob | File;
};
export type TavusHummingbirdLipsyncV0Output = {
  /**
   * The generated video
   */
  video: File;
};
export type TemplateToVideoOutput = {
  /**
   * The generated video using a predefined template
   */
  video: File;
};
export type Text2VideoInput = {
  /**
   * The avatar to use for the video
   */
  avatar_id:
    | "emily_vertical_primary"
    | "emily_vertical_secondary"
    | "marcus_vertical_primary"
    | "marcus_vertical_secondary"
    | "mira_vertical_primary"
    | "mira_vertical_secondary"
    | "jasmine_vertical_primary"
    | "jasmine_vertical_secondary"
    | "jasmine_vertical_walking"
    | "aisha_vertical_walking"
    | "elena_vertical_primary"
    | "elena_vertical_secondary"
    | "any_male_vertical_primary"
    | "any_female_vertical_primary"
    | "any_male_vertical_secondary"
    | "any_female_vertical_secondary"
    | "any_female_vertical_walking"
    | "emily_primary"
    | "emily_side"
    | "marcus_primary"
    | "marcus_side"
    | "aisha_walking"
    | "elena_primary"
    | "elena_side"
    | "any_male_primary"
    | "any_female_primary"
    | "any_male_side"
    | "any_female_side";
  /**
   *
   */
  text: string;
};
export type TextOutput = {
  /**
   * The answer to the query.
   */
  text: string;
  /**
   * The seed used for the generation.
   */
  seed: number;
  /**
   * The query used for the generation.
   */
  prompt: string;
  /**
   * The timings of the generation.
   */
  timings: any;
};
export type TextRemovalInput = {
  /**
   * URL of the image containing text to be removed.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type TextRemovalOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type TextTo3dInput = {
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Defines the artistic style or transformation to be applied to the 3D model, altering its appearance according to preset options (extra $0.05 per generation). Omit this option to keep the original style and apperance.
   */
  style?:
    | "person:person2cartoon"
    | "object:clay"
    | "object:steampunk"
    | "animal:venom"
    | "object:barbie"
    | "object:christmas"
    | "gold"
    | "ancient_bronze";
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Text input that directs the model generation. The maximum prompt length is 1024 characters, equivalent to approximately 100 words. The API supports multiple languages. However, emojis and certain special Unicode characters are not supported.
   */
  prompt: string;
  /**
   * Unlike prompt, it provides a reverse direction to assist in generating content contrasting with the original prompt. The maximum length is 255 characters.
   */
  negative_prompt?: string;
  /**
   * This is the random seed used for the process based on the prompt. This parameter is an integer and is randomly chosen if not set.
   */
  image_seed?: number;
};
export type TextToImage32Input = {
  /**
   * Prompt for image generation.
   */
  prompt: string;
  /**
   * Number of inference steps. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Random seed for reproducibility. Default value: `5555`
   */
  seed?: number;
  /**
   * Aspect ratio. Options: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9 Default value: `"1:1"`
   */
  aspect_ratio?:
    | "1:1"
    | "2:3"
    | "3:2"
    | "3:4"
    | "4:3"
    | "4:5"
    | "5:4"
    | "9:16"
    | "16:9";
  /**
   * Negative prompt for image generation. Default value: `"Logo,Watermark,Ugly,Morbid,Extra fingers,Poorly drawn hands,Mutation,Blurry,Extra limbs,Gross proportions,Missing arms,Mutated hands,Long neck,Duplicate,Mutilated,Mutilated hands,Poorly drawn face,Deformed,Bad anatomy,Cloned face,Malformed limbs,Missing legs,Too many fingers"`
   */
  negative_prompt?: string;
  /**
   * Guidance scale for text. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * Whether to truncate the prompt. Default value: `true`
   */
  truncate_prompt?: boolean;
  /**
   * Whether to improve the prompt. Default value: `true`
   */
  prompt_enhancer?: boolean;
  /**
   * If true, returns the image directly in the response (increases latency).
   */
  sync_mode?: boolean;
};
export type TextToImage32Output = {
  /**
   * Generated image.
   */
  image: Image;
};
export type TextToImageInput = {
  /**
   *
   */
  prompt: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?:
    | "10:16"
    | "16:10"
    | "9:16"
    | "16:9"
    | "4:3"
    | "3:4"
    | "1:1"
    | "1:3"
    | "3:1"
    | "3:2"
    | "2:3";
  /**
   * Whether to expand the prompt with MagicPrompt functionality. Default value: `true`
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The style of the generated image Default value: `"auto"`
   */
  style?: "auto" | "general" | "realistic" | "design" | "render_3D" | "anime";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * A negative prompt to avoid in the generated image Default value: `""`
   */
  negative_prompt?: string;
};
export type TextToImageOutput = {
  /**
   *
   */
  images: Array<File>;
};
export type TextToImageTurboInput = {
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * The negative prompt to use. Use it to address details that you don't want
   * in the image. This could be colors, objects, scenery and even the small details
   * (e.g. moustache, blurry, low resolution). Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `4`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you.
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The size of the generated image. Defaults to landscape_4_3 if no controlnet has been passed, otherwise defaults to the size of the controlnet conditioning image.
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type TextToImageV4FastInput = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type TextToImageV4Input = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type TextToImageV4UltraInput = {
  /**
   * The text prompt describing what you want to see
   */
  prompt: string;
  /**
   * A description of what to discourage in the generated images Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The aspect ratio of the generated image Default value: `"1:1"`
   */
  aspect_ratio?: "1:1" | "16:9" | "9:16" | "3:4" | "4:3";
  /**
   * Number of images to generate (1-4) Default value: `1`
   */
  num_images?: number;
  /**
   * Random seed for reproducible generation
   */
  seed?: number;
};
export type TextToSpeechOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Duration of the audio in milliseconds
   */
  duration_ms: number;
};
export type TextToVideoHailuo02Output = {
  /**
   * The generated video
   */
  video: File;
};
export type TextToVideoInput = {
  /**
   * Text prompt to guide generation
   */
  prompt: string;
  /**
   * Negative prompt for generation Default value: `"worst quality, inconsistent motion, blurry, jittery, distorted"`
   */
  negative_prompt?: string;
  /**
   * Resolution of the generated video (480p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * Number of inference steps Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * Whether to expand the prompt using the model's own capabilities. Default value: `true`
   */
  expand_prompt?: boolean;
};
export type TextToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type TextToVideoPreviewInput = {
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video. If it is not set to 16:9, the video will be outpainted. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * A negative prompt to guide the video generation
   */
  negative_prompt?: string;
  /**
   * Whether to enhance the video generation Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * A seed to use for the video generation
   */
  seed?: number;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
};
export type TextToVideoPreviewOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type TextToVideoV21MasterOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type TextToVideoV2MasterOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type TheraInput = {
  /**
   * URL of image to be used for upscaling
   */
  image_url: string | Blob | File;
  /**
   *  Default value: `2`
   */
  upscaling?: number;
  /**
   * Backbone to use for upscaling
   */
  backbone: "edsr" | "rdn";
  /**
   * Random seed for reproducible generation.
   */
  seed?: number;
};
export type TheraOutput = {
  /**
   * The generated image file info.
   */
  image: Image;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
};
export type ThinksoundAudioInput = {
  /**
   * The URL of the video to generate the audio for.
   */
  video_url: string | Blob | File;
  /**
   * A prompt to guide the audio generation. If not provided, it will be extracted from the video. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The number of inference steps for audio generation. Default value: `24`
   */
  num_inference_steps?: number;
  /**
   * The classifier-free guidance scale for audio generation. Default value: `5`
   */
  cfg_scale?: number;
};
export type ThinksoundAudioOutput = {
  /**
   * The generated audio file.
   */
  audio: File;
  /**
   * The prompt used to generate the audio.
   */
  prompt: string;
};
export type ThinksoundInput = {
  /**
   * The URL of the video to generate the audio for.
   */
  video_url: string | Blob | File;
  /**
   * A prompt to guide the audio generation. If not provided, it will be extracted from the video. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed for the random number generator
   */
  seed?: number;
  /**
   * The number of inference steps for audio generation. Default value: `24`
   */
  num_inference_steps?: number;
  /**
   * The classifier-free guidance scale for audio generation. Default value: `5`
   */
  cfg_scale?: number;
};
export type ThinksoundOutput = {
  /**
   * The generated video with audio.
   */
  video: File;
  /**
   * The prompt used to generate the audio.
   */
  prompt: string;
};
export type TimeOfDayInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The time of day to transform the scene to. Default value: `"golden hour"`
   */
  prompt?: string;
};
export type TimeOfDayOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type TopazUpscaleVideoInput = {
  /**
   * URL of the video to upscale
   */
  video_url: string | Blob | File;
  /**
   * Factor to upscale the video by (e.g. 2.0 doubles width and height) Default value: `2`
   */
  upscale_factor?: number;
  /**
   * Target FPS for frame interpolation. If set, frame interpolation will be enabled.
   */
  target_fps?: number;
};
export type TopazUpscaleVideoOutput = {
  /**
   * The upscaled video file
   */
  video: File;
};
export type TrainingInput = {
  /**
   * The name of the training job (required, max 255 characters).
   */
  name: string;
  /**
   * A list of audio URLs used for training (must be between 1 and 5 URLs).
   */
  training_data: Array<AudioInput>;
};
export type TranscriptionOutput = {
  /**
   * The full transcribed text
   */
  text: string;
  /**
   * Detected or specified language code
   */
  language_code: string;
  /**
   * Confidence in language detection
   */
  language_probability: number;
  /**
   * Word-level transcription details
   */
  words: Array<TranscriptionWord>;
};
export type TransitionOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type TranspixarInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The number of inference steps to perform. Default value: `24`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * The target FPS of the video Default value: `8`
   */
  export_fps?: number;
};
export type TranspixarOutput = {
  /**
   * The URL to the generated video
   */
  videos: Array<File>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated video. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * The prompt used for generating the video.
   */
  prompt: string;
};
export type TrellisInput = {
  /**
   * URL of the input image to convert to 3D
   */
  image_url: string | Blob | File;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Guidance strength for sparse structure generation Default value: `7.5`
   */
  ss_guidance_strength?: number;
  /**
   * Sampling steps for sparse structure generation Default value: `12`
   */
  ss_sampling_steps?: number;
  /**
   * Guidance strength for structured latent generation Default value: `3`
   */
  slat_guidance_strength?: number;
  /**
   * Sampling steps for structured latent generation Default value: `12`
   */
  slat_sampling_steps?: number;
  /**
   * Mesh simplification factor Default value: `0.95`
   */
  mesh_simplify?: number;
  /**
   * Texture resolution Default value: `"1024"`
   */
  texture_size?: "512" | "1024" | "2048";
};
export type TrellisMultiInput = {
  /**
   * List of URLs of input images to convert to 3D
   */
  image_urls: Array<string>;
  /**
   * Random seed for reproducibility
   */
  seed?: number;
  /**
   * Guidance strength for sparse structure generation Default value: `7.5`
   */
  ss_guidance_strength?: number;
  /**
   * Sampling steps for sparse structure generation Default value: `12`
   */
  ss_sampling_steps?: number;
  /**
   * Guidance strength for structured latent generation Default value: `3`
   */
  slat_guidance_strength?: number;
  /**
   * Sampling steps for structured latent generation Default value: `12`
   */
  slat_sampling_steps?: number;
  /**
   * Mesh simplification factor Default value: `0.95`
   */
  mesh_simplify?: number;
  /**
   * Texture resolution Default value: `"1024"`
   */
  texture_size?: "512" | "1024" | "2048";
  /**
   * Algorithm for multi-image generation Default value: `"stochastic"`
   */
  multiimage_algo?: "stochastic" | "multidiffusion";
};
export type TrellisMultiOutput = {
  /**
   * Generated 3D mesh file
   */
  model_mesh: File;
  /**
   * Processing timings
   */
  timings: any;
};
export type TrellisOutput = {
  /**
   * Generated 3D mesh file
   */
  model_mesh: File;
  /**
   * Processing timings
   */
  timings: any;
};
export type TripoV25ImageTo3dInput = {
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Defines the artistic style or transformation to be applied to the 3D model, altering its appearance according to preset options (extra $0.05 per generation). Omit this option to keep the original style and apperance.
   */
  style?:
    | "person:person2cartoon"
    | "object:clay"
    | "object:steampunk"
    | "animal:venom"
    | "object:barbie"
    | "object:christmas"
    | "gold"
    | "ancient_bronze";
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `original_image`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `default`
   */
  orientation?: "default" | "align_image";
  /**
   * URL of the image to use for model generation.
   */
  image_url: string | Blob | File;
};
export type TripoV25ImageTo3dOutput = {
  /**
   * The task id of the 3D model generation.
   */
  task_id: string;
  /**
   * Model
   */
  model_mesh?: File;
  /**
   * Base model
   */
  base_model?: File;
  /**
   * Pbr model
   */
  pbr_model?: File;
  /**
   * A preview image of the model
   */
  rendered_image?: File;
};
export type TripoV25MultiviewTo3dInput = {
  /**
   * This is the random seed for model generation. The seed controls the geometry generation process, ensuring identical models when the same seed is used. This parameter is an integer and is randomly chosen if not set.
   */
  seed?: number;
  /**
   * Limits the number of faces on the output model. If this option is not set, the face limit will be adaptively determined.
   */
  face_limit?: number;
  /**
   * A boolean option to enable pbr. The default value is True, set False to get a model without pbr. If this option is set to True, texture will be ignored and used as True.
   */
  pbr?: boolean;
  /**
   * An option to enable texturing. Default is 'standard', set 'no' to get a model without any textures, and set 'HD' to get a model with hd quality textures. Default value: `"standard"`
   */
  texture?: "no" | "standard" | "HD";
  /**
   * This is the random seed for texture generation. Using the same seed will produce identical textures. This parameter is an integer and is randomly chosen if not set. If you want a model with different textures, please use same seed and different texture_seed.
   */
  texture_seed?: number;
  /**
   * Automatically scale the model to real-world dimensions, with the unit in meters. The default value is False.
   */
  auto_size?: boolean;
  /**
   * Defines the artistic style or transformation to be applied to the 3D model, altering its appearance according to preset options (extra $0.05 per generation). Omit this option to keep the original style and apperance.
   */
  style?:
    | "person:person2cartoon"
    | "object:clay"
    | "object:steampunk"
    | "animal:venom"
    | "object:barbie"
    | "object:christmas"
    | "gold"
    | "ancient_bronze";
  /**
   * Set True to enable quad mesh output (extra $0.05 per generation). If quad=True and face_limit is not set, the default face_limit will be 10000. Note: Enabling this option will force the output to be an FBX model.
   */
  quad?: boolean;
  /**
   * Determines the prioritization of texture alignment in the 3D model. The default value is original_image. Default value: `original_image`
   */
  texture_alignment?: "original_image" | "geometry";
  /**
   * Set orientation=align_image to automatically rotate the model to align the original image. The default value is default. Default value: `default`
   */
  orientation?: "default" | "align_image";
  /**
   * Front view image of the object.
   */
  front_image_url: string | Blob | File;
  /**
   * Left view image of the object.
   */
  left_image_url?: string | Blob | File;
  /**
   * Back view image of the object.
   */
  back_image_url?: string | Blob | File;
  /**
   * Right view image of the object.
   */
  right_image_url?: string | Blob | File;
};
export type TripoV25MultiviewTo3dOutput = {
  /**
   * The task id of the 3D model generation.
   */
  task_id: string;
  /**
   * Model
   */
  model_mesh?: File;
  /**
   * Base model
   */
  base_model?: File;
  /**
   * Pbr model
   */
  pbr_model?: File;
  /**
   * A preview image of the model
   */
  rendered_image?: File;
};
export type TTSInput = {
  /**
   * Text to synthesize into speech. Default value: `"My name is Maximus Decimus Meridius, commander of the Armies of the North, General of the Felix Legions and loyal servant to the true emperor, Marcus Aurelius. Father to a murdered son, husband to a murdered wife. And I will have my vengeance, in this life or the next."`
   */
  text?: string;
  /**
   * The voice to use for the TTS request. If neither voice nor audio are provided, a random voice will be used.
   */
  voice?:
    | "Aurora"
    | "Blade"
    | "Britney"
    | "Carl"
    | "Cliff"
    | "Richard"
    | "Rico"
    | "Siobhan"
    | "Vicky";
  /**
   * URL to the audio sample to use as a voice prompt for zero-shot TTS voice cloning. Providing a audio sample will override the voice setting. If neither voice nor audio_url are provided, a random voice will be used.
   */
  audio_url?: string | Blob | File;
  /**
   * Controls emotion exaggeration. Range typically 0.25 to 2.0. Default value: `0.5`
   */
  exaggeration?: number;
  /**
   * Classifier-free guidance scale (CFG) controls the conditioning factor. Range typically 0.2 to 1.0. For expressive or dramatic speech, try lower cfg values (e.g. ~0.3) and increase exaggeration to around 0.7 or higher. If the reference speaker has a fast speaking style, lowering cfg to around 0.3 can improve pacing. Default value: `0.5`
   */
  cfg?: number;
  /**
   * If True, the generated audio will be upscaled to 48kHz. The generation of the audio will take longer, but the quality will be higher. If False, the generated audio will be 24kHz.
   */
  high_quality_audio?: boolean;
  /**
   * Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file. Set to 0 for random seed.
   */
  seed?: number;
  /**
   * Controls the randomness of generation. Range typically 0.05 to 5. Default value: `0.8`
   */
  temperature?: number;
};
export type TTSOutput = {
  /**
   * The generated audio file
   */
  audio: File;
  /**
   * Timestamps for each word in the generated speech. Only returned if `timestamps` is set to True in the request.
   */
  timestamps?: Array<void>;
};
export type TurboFluxTrainerInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images, although more is better.
   */
  images_data_url: string | Blob | File;
  /**
   * Trigger phrase to be used in the captions. If None, a trigger word will not be used.
   * If no captions are provide the trigger_work will be used instead of captions. If captions are provided, the trigger word will replace the `[trigger]` string in the captions. Default value: `"ohwx"`
   */
  trigger_phrase?: string;
  /**
   * Number of steps to train the LoRA on. Default value: `1000`
   */
  steps?: number;
  /**
   * Learning rate for the training. Default value: `0.00115`
   */
  learning_rate?: number;
  /**
   * Training style to use. Default value: `"subject"`
   */
  training_style?: "subject" | "style";
  /**
   * Whether to try to detect the face and crop the images to the face. Default value: `true`
   */
  face_crop?: boolean;
};
export type TurboFluxTrainerOutput = {
  /**
   * URL to the trained diffusers lora weights.
   */
  diffusers_lora_file: File;
  /**
   * URL to the trained diffusers config file.
   */
  config_file: File;
};
export type UnoInput = {
  /**
   * URL of images to use while generating the image.
   */
  input_image_urls: Array<string>;
  /**
   * The size of the generated image. You can choose between some presets or custom height and width
   * that **must be multiples of 8**. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The prompt to generate an image from.
   */
  prompt: string;
  /**
   * Random seed for reproducible generation. If set none, a random seed will be used.
   */
  seed?: number;
  /**
   * The number of images to generate. Default value: `1`
   */
  num_images?: number;
  /**
   * The number of inference steps to perform. Default value: `28`
   */
  num_inference_steps?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type UnoOutput = {
  /**
   * The URLs of the generated images.
   */
  images: Array<Image>;
  /**
   *
   */
  timings: any;
  /**
   * Seed of the generated Image. It will be the same value of the one passed in the
   * input or the randomly generated that was used in case none was passed.
   */
  seed: number;
  /**
   * Whether the generated images contain NSFW concepts.
   */
  has_nsfw_concepts: Array<boolean>;
  /**
   * The prompt used to generate the image.
   */
  prompt: string;
};
export type UpscaleImageInput = {
  /**
   * The image URL to upscale
   */
  image_url: string | Blob | File;
  /**
   * The prompt to upscale the image with Default value: `""`
   */
  prompt?: string;
  /**
   * The resemblance of the upscaled image to the original image Default value: `50`
   */
  resemblance?: number;
  /**
   * The detail of the upscaled image Default value: `50`
   */
  detail?: number;
  /**
   * Whether to expand the prompt with MagicPrompt functionality.
   */
  expand_prompt?: boolean;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type UpscaleInput = {
  /**
   * Input Image to erase from
   */
  image_url: string | Blob | File;
  /**
   * The desired increase in resolution. Default value: `"2"`
   */
  desired_increase?: "2" | "4";
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type UpscaleOutput = {
  /**
   *
   */
  images: Array<File>;
  /**
   * Seed used for the random number generator
   */
  seed: number;
};
export type V16Input = {
  /**
   * URL or base64 of the model image
   */
  model_image: string;
  /**
   * URL or base64 of the garment image
   */
  garment_image: string;
  /**
   * Category of the garment to try-on. 'auto' will attempt to automatically detect the category of the garment. Default value: `"auto"`
   */
  category?: "tops" | "bottoms" | "one-pieces" | "auto";
  /**
   * Specifies the mode of operation. 'performance' mode is faster but may sacrifice quality, 'balanced' mode is a balance between speed and quality, and 'quality' mode is slower but produces higher quality results. Default value: `"balanced"`
   */
  mode?: "performance" | "balanced" | "quality";
  /**
   * Specifies the type of garment photo to optimize internal parameters for better performance. 'model' is for photos of garments on a model, 'flat-lay' is for flat-lay or ghost mannequin images, and 'auto' attempts to automatically detect the photo type. Default value: `"auto"`
   */
  garment_photo_type?: "auto" | "model" | "flat-lay";
  /**
   * Content moderation level for garment images. 'none' disables moderation, 'permissive' blocks only explicit content, 'conservative' also blocks underwear and swimwear. Default value: `"permissive"`
   */
  moderation_level?: "none" | "permissive" | "conservative";
  /**
   * Sets random operations to a fixed state. Use the same seed to reproduce results with the same inputs, or different seed to force different results. Default value: `42`
   */
  seed?: number;
  /**
   * Number of images to generate in a single run. Image generation has a random element in it, so trying multiple images at once increases the chances of getting a good result. Default value: `1`
   */
  num_samples?: number;
  /**
   * Disables human parsing on the model image. Default value: `true`
   */
  segmentation_free?: boolean;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * Output format of the generated images. 'png' is highest quality, while 'jpeg' is faster Default value: `"png"`
   */
  output_format?: "png" | "jpeg";
};
export type V16Output = {
  /**
   *
   */
  images: Array<File>;
};
export type V3TTSInput = {
  /**
   * The text to be converted to speech.
   */
  input: string;
  /**
   * The unique ID of a PlayHT or Cloned Voice, or a name from the available presets.
   */
  voice: string;
  /**
   * The format of the response. Default value: `"url"`
   */
  response_format?: "url" | "bytes";
  /**
   * An integer number greater than or equal to 0. If equal to null or not provided, a random seed will be used. Useful to control the reproducibility of the generated audio. Assuming all other properties didn't change, a fixed seed should always generate the exact same audio file.
   */
  seed?: number;
};
export type V3TTSOutput = {
  /**
   * The generated audio file.
   */
  audio: AudioFile;
};
export type VectorizeInput = {
  /**
   * The URL of the image to be vectorized. Must be in PNG, JPG or WEBP format, less than 5 MB in size, have resolution less than 16 MP and max dimension less than 4096 pixels, min dimension more than 256 pixels.
   */
  image_url: string | Blob | File;
};
export type VectorizeOutput = {
  /**
   * The vectorized image.
   */
  image: File;
};
export type Veo2ImageToVideoInput = {
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 aspect ratio. If the image is not in 16:9 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"5s"`
   */
  duration?: "5s" | "6s" | "7s" | "8s";
};
export type Veo2ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Veo2Input = {
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
  /**
   * The duration of the generated video in seconds Default value: `"5s"`
   */
  duration?: "5s" | "6s" | "7s" | "8s";
  /**
   * A negative prompt to guide the video generation
   */
  negative_prompt?: string;
  /**
   * Whether to enhance the video generation Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * A seed to use for the video generation
   */
  seed?: number;
};
export type Veo2Output = {
  /**
   * The generated video
   */
  video: File;
};
export type Veo3FastImageToVideoInput = {
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 aspect ratio. If the image is not in 16:9 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * Resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
};
export type Veo3FastImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Veo3FastInput = {
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video. If it is not set to 16:9, the video will be outpainted. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * A negative prompt to guide the video generation
   */
  negative_prompt?: string;
  /**
   * Whether to enhance the video generation Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * A seed to use for the video generation
   */
  seed?: number;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
};
export type Veo3FastOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Veo3ImageToVideoInput = {
  /**
   * The text prompt describing how the image should be animated
   */
  prompt: string;
  /**
   * URL of the input image to animate. Should be 720p or higher resolution in 16:9 aspect ratio. If the image is not in 16:9 aspect ratio, it will be cropped to fit.
   */
  image_url: string | Blob | File;
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
  /**
   * Resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
};
export type Veo3ImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type Veo3Input = {
  /**
   * The text prompt describing the video you want to generate
   */
  prompt: string;
  /**
   * The aspect ratio of the generated video. If it is not set to 16:9, the video will be outpainted. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The duration of the generated video in seconds Default value: `"8s"`
   */
  duration?: "8s";
  /**
   * A negative prompt to guide the video generation
   */
  negative_prompt?: string;
  /**
   * Whether to enhance the video generation Default value: `true`
   */
  enhance_prompt?: boolean;
  /**
   * A seed to use for the video generation
   */
  seed?: number;
  /**
   * The resolution of the generated video Default value: `"720p"`
   */
  resolution?: "720p" | "1080p";
  /**
   * Whether to generate audio for the video. If false, %33 less credits will be used. Default value: `true`
   */
  generate_audio?: boolean;
};
export type Veo3Output = {
  /**
   * The generated video
   */
  video: File;
};
export type VideoBackgroundRemovalInput = {
  /**
   * Input video to remove background from. Size should be less than 14142x14142 and duration less than 30s.
   */
  video_url: string | Blob | File;
  /**
   * Background color. Options: Transparent, Black, White, Gray, Red, Green, Blue, Yellow, Cyan, Magenta, Orange. Default value: `"Black"`
   */
  background_color?:
    | "Transparent"
    | "Black"
    | "White"
    | "Gray"
    | "Red"
    | "Green"
    | "Blue"
    | "Yellow"
    | "Cyan"
    | "Magenta"
    | "Orange";
};
export type VideoBackgroundRemovalOutput = {
  /**
   * Video with removed background and audio.
   */
  video: Video | File;
};
export type VideoConditioningInput = {
  /**
   * URL of video to be extended
   */
  video_url: string | Blob | File;
  /**
   * Frame number of the video from which the conditioning starts. Must be a multiple of 8.
   */
  start_frame_num: number;
};
export type VideoEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type VideoInput = {
  /**
   * Prompt to be used for the chat completion
   */
  prompt: string;
  /**
   * The URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * Number of frames to sample from the video. If not provided, all frames are sampled.
   */
  num_frames_to_sample?: number;
};
export type VideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type VideoPromptGeneratorInput = {
  /**
   * Core concept or thematic input for the video prompt
   */
  input_concept: string;
  /**
   * Style of the video prompt Default value: `"Simple"`
   */
  style?:
    | "Minimalist"
    | "Simple"
    | "Detailed"
    | "Descriptive"
    | "Dynamic"
    | "Cinematic"
    | "Documentary"
    | "Animation"
    | "Action"
    | "Experimental";
  /**
   * Camera movement style Default value: `"None"`
   */
  camera_style?:
    | "None"
    | "Steadicam flow"
    | "Drone aerials"
    | "Handheld urgency"
    | "Crane elegance"
    | "Dolly precision"
    | "VR 360"
    | "Multi-angle rig"
    | "Static tripod"
    | "Gimbal smoothness"
    | "Slider motion"
    | "Jib sweep"
    | "POV immersion"
    | "Time-slice array"
    | "Macro extreme"
    | "Tilt-shift miniature"
    | "Snorricam character"
    | "Whip pan dynamics"
    | "Dutch angle tension"
    | "Underwater housing"
    | "Periscope lens";
  /**
   * Camera direction Default value: `"None"`
   */
  camera_direction?:
    | "None"
    | "Zoom in"
    | "Zoom out"
    | "Pan left"
    | "Pan right"
    | "Tilt up"
    | "Tilt down"
    | "Orbital rotation"
    | "Push in"
    | "Pull out"
    | "Track forward"
    | "Track backward"
    | "Spiral in"
    | "Spiral out"
    | "Arc movement"
    | "Diagonal traverse"
    | "Vertical rise"
    | "Vertical descent";
  /**
   * Pacing rhythm Default value: `"None"`
   */
  pacing?:
    | "None"
    | "Slow burn"
    | "Rhythmic pulse"
    | "Frantic energy"
    | "Ebb and flow"
    | "Hypnotic drift"
    | "Time-lapse rush"
    | "Stop-motion staccato"
    | "Gradual build"
    | "Quick cut rhythm"
    | "Long take meditation"
    | "Jump cut energy"
    | "Match cut flow"
    | "Cross-dissolve dreamscape"
    | "Parallel action"
    | "Slow motion impact"
    | "Ramping dynamics"
    | "Montage tempo"
    | "Continuous flow"
    | "Episodic breaks";
  /**
   * Special effects approach Default value: `"None"`
   */
  special_effects?:
    | "None"
    | "Practical effects"
    | "CGI enhancement"
    | "Analog glitches"
    | "Light painting"
    | "Projection mapping"
    | "Nanosecond exposures"
    | "Double exposure"
    | "Smoke diffusion"
    | "Lens flare artistry"
    | "Particle systems"
    | "Holographic overlay"
    | "Chromatic aberration"
    | "Digital distortion"
    | "Wire removal"
    | "Motion capture"
    | "Miniature integration"
    | "Weather simulation"
    | "Color grading"
    | "Mixed media composite"
    | "Neural style transfer";
  /**
   * Custom technical elements (optional) Default value: `""`
   */
  custom_elements?: string;
  /**
   * URL of an image to analyze and incorporate into the video prompt (optional)
   */
  image_url?: string | Blob | File;
  /**
   * Model to use Default value: `"google/gemini-2.0-flash-001"`
   */
  model?:
    | "anthropic/claude-3.5-sonnet"
    | "anthropic/claude-3-5-haiku"
    | "anthropic/claude-3-haiku"
    | "google/gemini-pro-1.5"
    | "google/gemini-flash-1.5"
    | "google/gemini-flash-1.5-8b"
    | "google/gemini-2.0-flash-001"
    | "meta-llama/llama-3.2-1b-instruct"
    | "meta-llama/llama-3.2-3b-instruct"
    | "meta-llama/llama-3.1-8b-instruct"
    | "meta-llama/llama-3.1-70b-instruct"
    | "openai/gpt-4o-mini"
    | "openai/gpt-4o"
    | "deepseek/deepseek-r1";
  /**
   * Length of the prompt Default value: `"Medium"`
   */
  prompt_length?: "Short" | "Medium" | "Long";
};
export type VideoPromptGeneratorOutput = {
  /**
   * Generated video prompt
   */
  prompt: string;
};
export type VideoSoundEffectsGeneratorInput = {
  /**
   * A video file to analyze & re-sound with generated SFX.
   */
  video_url: Video;
};
export type VideoSoundEffectsGeneratorOutput = {
  /**
   * The final video with the newly generated SFX track.
   */
  video: File;
};
export type VideoToVideoInput = {
  /**
   * The prompt to generate the video from.
   */
  prompt: string;
  /**
   * The size of the generated video.
   */
  video_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
  /**
   * The negative prompt to generate video from Default value: `""`
   */
  negative_prompt?: string;
  /**
   * The LoRAs to use for the image generation. We currently support one lora.
   */
  loras?: Array<LoraWeight>;
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same prompt given to the same version of the model
   * will output the same video every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your prompt when looking for a related video to show you. Default value: `7`
   */
  guidance_scale?: number;
  /**
   * Use RIFE for video interpolation Default value: `true`
   */
  use_rife?: boolean;
  /**
   * The target FPS of the video Default value: `16`
   */
  export_fps?: number;
  /**
   * The video to generate the video from.
   */
  video_url: string | Blob | File;
  /**
   * The strength to use for Video to Video.  1.0 completely remakes the video while 0.0 preserves the original. Default value: `0.8`
   */
  strength?: number;
};
export type VideoUnderstandingInput = {
  /**
   * URL of the video to analyze
   */
  video_url: string | Blob | File;
  /**
   * The question or prompt about the video content
   */
  prompt: string;
  /**
   * Whether to request a more detailed analysis of the video
   */
  detailed_analysis?: boolean;
};
export type VideoUnderstandingOutput = {
  /**
   * The analysis of the video content based on the prompt
   */
  output: string;
};
export type VideoUpscalerInput = {
  /**
   * The URL of the video to upscale
   */
  video_url: string | Blob | File;
  /**
   * The scale factor Default value: `2`
   */
  scale?: number;
};
export type VideoUpscalerOutput = {
  /**
   * The stitched video
   */
  video: File;
};
export type ViduImageToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type ViduQ1ImageToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  image_url: string | Blob | File;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduQ1ImageToVideoOutput = {
  /**
   * The generated video using the Q1 model from a single image
   */
  video: File;
};
export type ViduQ1ReferenceToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URLs of the reference images to use for consistent subject appearance. Q1 model supports up to 7 reference images.
   */
  reference_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
  /**
   * Whether to add background music to the generated video
   */
  bgm?: boolean;
};
export type ViduQ1ReferenceToVideoOutput = {
  /**
   * The generated video with consistent subjects from reference images using the Q1 model
   */
  video: File;
};
export type ViduQ1StartEndToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  start_image_url: string | Blob | File;
  /**
   * URL of the image to use as the last frame
   */
  end_image_url: string | Blob | File;
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduQ1StartEndToVideoOutput = {
  /**
   * The generated transition video between start and end frames using the Q1 model
   */
  video: File;
};
export type ViduQ1TextToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * The style of output video Default value: `"general"`
   */
  style?: "general" | "anime";
  /**
   * Seed for the random number generator
   */
  seed?: number;
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduQ1TextToVideoOutput = {
  /**
   * The generated video using the Q1 model
   */
  video: File;
};
export type ViduReferenceToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URLs of the reference images to use for consistent subject appearance
   */
  reference_image_urls: Array<string>;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduReferenceToVideoOutput = {
  /**
   * The generated video with consistent subjects from reference images
   */
  video: File;
};
export type ViduStartEndToVideoInput = {
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * URL of the image to use as the first frame
   */
  start_image_url: string | Blob | File;
  /**
   * URL of the image to use as the last frame
   */
  end_image_url: string | Blob | File;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The movement amplitude of objects in the frame Default value: `"auto"`
   */
  movement_amplitude?: "auto" | "small" | "medium" | "large";
};
export type ViduStartEndToVideoOutput = {
  /**
   * The generated transition video between start and end frames
   */
  video: File;
};
export type ViduTemplateToVideoInput = {
  /**
   * AI video template to use. Pricing varies by template: Standard templates (hug, kiss, love_pose, etc.) cost 4 credits ($0.20), Premium templates (lunar_newyear, dynasty_dress, dreamy_wedding, etc.) cost 6 credits ($0.30), and Advanced templates (live_photo) cost 10 credits ($0.50). Default value: `"hug"`
   */
  template?:
    | "dreamy_wedding"
    | "romantic_lift"
    | "sweet_proposal"
    | "couple_arrival"
    | "cupid_arrow"
    | "pet_lovers"
    | "lunar_newyear"
    | "hug"
    | "kiss"
    | "dynasty_dress"
    | "wish_sender"
    | "love_pose"
    | "hair_swap"
    | "youth_rewind"
    | "morphlab"
    | "live_photo"
    | "emotionlab"
    | "live_memory"
    | "interaction"
    | "christmas";
  /**
   * URLs of the images to use with the template. Number of images required varies by template: 'dynasty_dress' and 'shop_frame' accept 1-2 images, 'wish_sender' requires exactly 3 images, all other templates accept only 1 image.
   */
  input_image_urls: Array<string>;
  /**
   * Text prompt for video generation, max 1500 characters
   */
  prompt: string;
  /**
   * Random seed for generation
   */
  seed?: number;
  /**
   * The aspect ratio of the output video Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16";
};
export type ViduTemplateToVideoOutput = {
  /**
   * The generated video using a predefined template
   */
  video: File;
};
export type VignetteInput = {
  /**
   * URL of image to process
   */
  image_url: string | Blob | File;
  /**
   * Vignette strength Default value: `0.5`
   */
  vignette_strength?: number;
};
export type VignetteOutput = {
  /**
   * The processed images with vignette effect
   */
  images: Array<Image>;
};
export type VisionInput = {
  /**
   * Prompt to be used for the image
   */
  prompt: string;
  /**
   * System prompt to provide context or instructions to the model
   */
  system_prompt?: string;
  /**
   * Should reasoning be the part of the final answer.
   */
  reasoning?: boolean;
  /**
   * Name of the model to use. Premium models are charged at 3x the rate of standard models, they include: openai/gpt-5-chat, anthropic/claude-3.5-sonnet, google/gemini-pro-1.5, meta-llama/llama-3.2-90b-vision-instruct, anthropic/claude-3.7-sonnet, openai/gpt-4o. Default value: `"google/gemini-flash-1.5"`
   */
  model?:
    | "anthropic/claude-3.7-sonnet"
    | "anthropic/claude-3.5-sonnet"
    | "anthropic/claude-3-haiku"
    | "google/gemini-pro-1.5"
    | "google/gemini-flash-1.5"
    | "google/gemini-flash-1.5-8b"
    | "google/gemini-2.0-flash-001"
    | "openai/gpt-4o"
    | "openai/gpt-5-chat"
    | "meta-llama/llama-3.2-90b-vision-instruct"
    | "meta-llama/llama-4-maverick"
    | "meta-llama/llama-4-scout";
  /**
   * URL of the image to be processed
   */
  image_url: string | Blob | File;
};
export type VoiceCloneOutput = {
  /**
   * The cloned voice ID for use with TTS
   */
  custom_voice_id: string;
  /**
   * Preview audio generated with the cloned voice (if requested)
   */
  audio?: File;
};
export type VoiceCloningOutput = {
  /**
   * The id of the cloned voice
   */
  voice_id: string;
};
export type VoiceDeleteOutput = {
  /**
   * The voice_id of the voice that was deleted
   */
  voice_id: string;
};
export type VoiceDesignOutput = {
  /**
   * The voice_id of the generated voice
   */
  custom_voice_id: string;
  /**
   * The preview audio using the generated voice
   */
  audio: File;
};
export type VTONInput = {
  /**
   * The number of inference steps to perform. Default value: `50`
   */
  num_inference_steps?: number;
  /**
   * The same seed and the same input given to the same version of the model
   * will output the same image every time.
   */
  seed?: number;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want
   * the model to stick to your input when generating the image. Default value: `2.5`
   */
  guidance_scale?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded
   * before returning the response. This will increase the latency of the function but
   * it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * If set to true, the safety checker will be enabled. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The format of the generated image. Default value: `"png"`
   */
  output_format?: "jpeg" | "png";
  /**
   * Url for the human image.
   */
  human_image_url: string | Blob | File;
  /**
   * Url to the garment image.
   */
  garment_image_url: string | Blob | File;
  /**
   * The type of the garment used for virtual try-on.
   */
  garment_type: "upper_body" | "lower_body" | "dresses";
};
export type VTONOutput = {
  /**
   * The output image.
   */
  image: Image;
  /**
   * The seed for the inference.
   */
  seed: number;
  /**
   * Whether the image contains NSFW concepts.
   */
  has_nsfw_concepts: boolean;
};
export type WanEffectsInput = {
  /**
   * The subject to insert into the predefined prompt template for the selected effect.
   */
  subject: string;
  /**
   * URL of the input image.
   */
  image_url: string | Blob | File;
  /**
   * The type of effect to apply to the video. Default value: `"cakeify"`
   */
  effect_type?:
    | "squish"
    | "muscle"
    | "inflate"
    | "crush"
    | "rotate"
    | "gun-shooting"
    | "deflate"
    | "cakeify"
    | "hulk"
    | "baby"
    | "bride"
    | "classy"
    | "puppy"
    | "snow-white"
    | "disney-princess"
    | "mona-lisa"
    | "painting"
    | "pirate-captain"
    | "princess"
    | "jungle"
    | "samurai"
    | "vip"
    | "warrior"
    | "zen"
    | "assassin"
    | "timelapse"
    | "tsunami"
    | "fire"
    | "zoom-call"
    | "doom-fps"
    | "fus-ro-dah"
    | "hug-jesus"
    | "robot-face-reveal"
    | "super-saiyan"
    | "jumpscare"
    | "laughing"
    | "cartoon-jaw-drop"
    | "crying"
    | "kissing"
    | "angry-face"
    | "selfie-younger-self"
    | "animeify"
    | "blast";
  /**
   * Number of frames to generate. Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Aspect ratio of the output video. Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The scale of the LoRA weight. Used to adjust effect intensity. Default value: `1`
   */
  lora_scale?: number;
  /**
   * Whether to use turbo mode. If True, the video will be generated faster but with lower quality.
   */
  turbo_mode?: boolean;
};
export type WanEffectsOutput = {
  /**
   * The generated video
   */
  video: File;
  /**
   *
   */
  seed: number;
};
export type WanFlf2vInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * URL of the starting image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  start_image_url: string | Blob | File;
  /**
   * URL of the ending image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  end_image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type WanFlf2vOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanI2vInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'regular'. Default value: `"regular"`
   */
  acceleration?: "none" | "regular";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
};
export type WanI2vLoraInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). If the number of frames is greater than 81, the video will be generated with 1.25x more billing units. Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p or 720p). 480p is 0.5 billing units, and 720p is 1 billing unit. Default value: `"720p"`
   */
  resolution?: "480p" | "720p";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `5`
   */
  guide_scale?: number;
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Aspect ratio of the output video. Default value: `"16:9"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoraWeight>;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
};
export type WanI2vLoraOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanI2vOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanProImageToVideoInput = {
  /**
   * The prompt to generate the video
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The URL of the image to generate the video from
   */
  image_url: string | Blob | File;
};
export type WanProImageToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type WanProTextToVideoInput = {
  /**
   * The prompt to generate the video
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Whether to enable the safety checker Default value: `true`
   */
  enable_safety_checker?: boolean;
};
export type WanProTextToVideoOutput = {
  /**
   * The generated video
   */
  video: File;
};
export type WanT2vInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality.
   */
  turbo_mode?: boolean;
};
export type WanT2vLoraInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"480p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "9:16" | "16:9";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * If true, the video will be generated faster with no noticeable degradation in the visual quality. Default value: `true`
   */
  turbo_mode?: boolean;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoraWeight>;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
};
export type WanT2vLoraOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanT2vOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanTrainerFlf2v720pInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
};
export type WanTrainerFlf2v720pOutput = {
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type WanTrainerI2v720pInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
};
export type WanTrainerI2v720pOutput = {
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type WanTrainerInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
};
export type WanTrainerOutput = {
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type WanTrainerT2v14bInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
};
export type WanTrainerT2v14bOutput = {
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type WanTrainerT2vInput = {
  /**
   * URL to zip archive with images of a consistent style. Try to use at least 10 images and/or videos, although more is better.
   *
   * In addition to images the archive can contain text files with captions. Each text file should have the same name as the image/video file it corresponds to.
   */
  training_data_url: string | Blob | File;
  /**
   * The number of steps to train for. Default value: `400`
   */
  number_of_steps?: number;
  /**
   * The rate at which the model learns. Higher values can lead to faster training, but over-fitting. Default value: `0.0002`
   */
  learning_rate?: number;
  /**
   * The phrase that will trigger the model to generate an image. Default value: `""`
   */
  trigger_phrase?: string;
  /**
   * If true, the input will be automatically scale the video to 81 frames at 16fps.
   */
  auto_scale_input?: boolean;
};
export type WanTrainerT2vOutput = {
  /**
   * URL to the trained LoRA weights.
   */
  lora_file: File;
  /**
   * Configuration used for setting up the inference endpoints.
   */
  config_file: File;
};
export type WanV225bImageToVideoInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
};
export type WanV225bImageToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV225bTextToImageInput = {
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type WanV225bTextToImageOutput = {
  /**
   * The generated image file.
   */
  image: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV225bTextToVideoDistillInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `1`
   */
  guidance_scale?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
};
export type WanV225bTextToVideoDistillOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV225bTextToVideoFastWanInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
};
export type WanV225bTextToVideoFastWanOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV225bTextToVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `24`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (580p or 720p). Default value: `"720p"`
   */
  resolution?: "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `40`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4.
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
};
export type WanV225bTextToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bImageToVideoLoraInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
};
export type WanV22A14bImageToVideoLoraOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bImageToVideoTurboInput = {
  /**
   * URL of the input image. If the input image does not match the chosen aspect ratio, it is resized and center cropped.
   */
  image_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input image. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
};
export type WanV22A14bImageToVideoTurboOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bTextToImageInput = {
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type WanV22A14bTextToImageLoraInput = {
  /**
   * The text prompt to guide image generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the image. Must be between 1.0 and 10.0. Default value: `2`
   */
  shift?: number;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
  /**
   * The size of the generated image. Default value: `square_hd`
   */
  image_size?:
    | ImageSize
    | "square_hd"
    | "square"
    | "portrait_4_3"
    | "portrait_16_9"
    | "landscape_4_3"
    | "landscape_16_9";
};
export type WanV22A14bTextToImageLoraOutput = {
  /**
   * The generated image file.
   */
  image: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bTextToImageOutput = {
  /**
   * The generated image file.
   */
  image: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bTextToVideoInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
};
export type WanV22A14bTextToVideoLoraInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * LoRA weights to be used in the inference.
   */
  loras?: Array<LoRAWeight>;
  /**
   * If true, the video will be reversed.
   */
  reverse_video?: boolean;
};
export type WanV22A14bTextToVideoLoraOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bTextToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bTextToVideoTurboInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "16:9" | "9:16" | "1:1";
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
};
export type WanV22A14bTextToVideoTurboOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanV22A14bVideoToVideoInput = {
  /**
   * URL of the input video.
   */
  video_url: string | Blob | File;
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Strength of the video transformation. A value of 1.0 means the output will be completely based on the prompt, while a value of 0.0 means the output will be identical to the input video. Default value: `0.9`
   */
  strength?: number;
  /**
   * Number of frames to generate. Must be between 81 to 121 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 4 to 60. When using interpolation and `adjust_fps_for_interpolation` is set to true (default true,) the final FPS will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If `adjust_fps_for_interpolation` is set to false, this value will be used as-is. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Negative prompt for video generation. Default value: `""`
   */
  negative_prompt?: string;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p, 580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video. If 'auto', the aspect ratio will be determined automatically based on the input video. Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "9:16" | "1:1";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `27`
   */
  num_inference_steps?: number;
  /**
   * If set to true, input data will be checked for safety before processing.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion. This will use a large language model to expand the prompt with additional details while maintaining the original meaning.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration level to use. The more acceleration, the faster the generation, but with lower quality. The recommended value is 'none'. Default value: `"none"`
   */
  acceleration?: "none" | "regular";
  /**
   * Classifier-free guidance scale. Higher values give better adherence to the prompt but may decrease quality. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Guidance scale for the second stage of the model. This is used to control the adherence to the prompt in the second stage of the model. Default value: `4`
   */
  guidance_scale_2?: number;
  /**
   * Shift value for the video. Must be between 1.0 and 10.0. Default value: `5`
   */
  shift?: number;
  /**
   * The model to use for frame interpolation. If None, no interpolation is applied. Default value: `"film"`
   */
  interpolator_model?: "none" | "film" | "rife";
  /**
   * Number of frames to interpolate between each pair of generated frames. Must be between 0 and 4. Default value: `1`
   */
  num_interpolated_frames?: number;
  /**
   * If true, the number of frames per second will be multiplied by the number of interpolated frames plus one. For example, if the generated frames per second is 16 and the number of interpolated frames is 1, the final frames per second will be 32. If false, the passed frames per second will be used as-is. Default value: `true`
   */
  adjust_fps_for_interpolation?: boolean;
  /**
   * If true, the video will be resampled to the passed frames per second. If false, the video will not be resampled.
   */
  resample_fps?: boolean;
};
export type WanV22A14bVideoToVideoOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The text prompt used for video generation. Default value: `""`
   */
  prompt?: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace13bInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Works only with only reference images as input if source video or mask video is provided output len would be same as source up to 241 frames Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Task type for the model. Default value: `"depth"`
   */
  task?: "depth" | "inpainting" | "pose";
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "auto" | "9:16" | "16:9";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * URL to the source video file. If provided, the model will use this video as a reference.
   */
  video_url?: string | Blob | File;
  /**
   * URL to the source mask file. If provided, the model will use this mask as a reference.
   */
  mask_video_url?: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video. If provided mask video url will be ignored.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
};
export type WanVace13bOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bDepthInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. Required for depth task.
   */
  video_url: string | Blob | File;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
};
export type WanVace14bDepthOutput = {
  /**
   * The generated depth video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bInpaintingInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. Required for inpainting.
   */
  video_url: string | Blob | File;
  /**
   * URL to the source mask file. Required for inpainting.
   */
  mask_video_url: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video using salient mask tracking. Will be ignored if mask_video_url is provided.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
};
export type WanVace14bInpaintingOutput = {
  /**
   * The generated inpainting video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Task type for the model. Default value: `"depth"`
   */
  task?: "depth" | "pose" | "inpainting" | "outpainting" | "reframe";
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. If provided, the model will use this video as a reference.
   */
  video_url: string | Blob | File;
  /**
   * URL to the source mask file. If provided, the model will use this mask as a reference.
   */
  mask_video_url?: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video. If provided mask video url will be ignored.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
};
export type WanVace14bOutpaintingInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. Required for outpainting.
   */
  video_url: string | Blob | File;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
  /**
   * Whether to expand the video to the left.
   */
  expand_left?: boolean;
  /**
   * Whether to expand the video to the right.
   */
  expand_right?: boolean;
  /**
   * Whether to expand the video to the top.
   */
  expand_top?: boolean;
  /**
   * Whether to expand the video to the bottom.
   */
  expand_bottom?: boolean;
  /**
   * Amount of expansion. This is a float value between 0 and 1, where 0.25 adds 25% to the original video size on the specified sides. Default value: `0.25`
   */
  expand_ratio?: number;
};
export type WanVace14bOutpaintingOutput = {
  /**
   * The generated outpainting video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bPoseInput = {
  /**
   * The text prompt to guide video generation. For pose task, the prompt should describe the desired pose and action of the subject in the video.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter.
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter.
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. Required for pose task.
   */
  video_url: string | Blob | File;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
};
export type WanVace14bPoseOutput = {
  /**
   * The generated pose video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVace14bReframeInput = {
  /**
   * The text prompt to guide video generation. Optional for reframing. Default value: `""`
   */
  prompt?: string;
  /**
   * Negative prompt for video generation. Default value: `"letterboxing, borders, black bars, bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * If true, the number of frames in the generated video will match the number of frames in the input video. If false, the number of frames will be determined by the num_frames parameter. Default value: `true`
   */
  match_input_num_frames?: boolean;
  /**
   * Number of frames to generate. Must be between 81 to 241 (inclusive). Default value: `81`
   */
  num_frames?: number;
  /**
   * If true, the frames per second of the generated video will match the input video. If false, the frames per second will be determined by the frames_per_second parameter. Default value: `true`
   */
  match_input_frames_per_second?: boolean;
  /**
   * Frames per second of the generated video. Must be between 5 to 30. Ignored if match_input_frames_per_second is true. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"auto"`
   */
  aspect_ratio?: "auto" | "16:9" | "1:1" | "9:16";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Guidance scale for classifier-free guidance. Higher values encourage the model to generate images closely related to the text prompt. Default value: `5`
   */
  guidance_scale?: number;
  /**
   * URL to the source video file. This video will be used as a reference for the reframe task.
   */
  video_url: string | Blob | File;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Acceleration to use for inference. Options are 'none' or 'regular'. Accelerated inference will very slightly affect output, but will be significantly faster.
   */
  acceleration?: "none" | "regular";
  /**
   * Zoom factor for the video. When this value is greater than 0, the video will be zoomed in by this factor (in relation to the canvas size,) cutting off the edges of the video. A value of 0 means no zoom.
   */
  zoom_factor?: number;
  /**
   * Temporal downsample factor for the video. This is an integer value that determines how many frames to skip in the video. A value of 0 means no downsampling. For each downsample factor, one upsample factor will automatically be applied.
   */
  temporal_downsample_factor?: number;
  /**
   * The model to use for frame interpolation. Options are 'rife' or 'film'. Default value: `"film"`
   */
  interpolator_model?: "rife" | "film";
};
export type WanVace14bReframeOutput = {
  /**
   * The generated reframe video file.
   */
  video: File;
  /**
   * The prompt used for generation.
   */
  prompt: string;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WanVaceInput = {
  /**
   * The text prompt to guide video generation.
   */
  prompt: string;
  /**
   * Negative prompt for video generation. Default value: `"bright colors, overexposed, static, blurred details, subtitles, style, artwork, painting, picture, still, overall gray, worst quality, low quality, JPEG compression residue, ugly, incomplete, extra fingers, poorly drawn hands, poorly drawn faces, deformed, disfigured, malformed limbs, fused fingers, still picture, cluttered background, three legs, many people in the background, walking backwards"`
   */
  negative_prompt?: string;
  /**
   * Number of frames to generate. Must be between 81 to 100 (inclusive). Works only with only reference images as input if source video or mask video is provided output len would be same as source up to 241 frames Default value: `81`
   */
  num_frames?: number;
  /**
   * Frames per second of the generated video. Must be between 5 to 24. Default value: `16`
   */
  frames_per_second?: number;
  /**
   * Task type for the model. Default value: `"depth"`
   */
  task?: "depth" | "inpainting";
  /**
   * Shift parameter for video generation. Default value: `5`
   */
  shift?: number;
  /**
   * Random seed for reproducibility. If None, a random seed is chosen.
   */
  seed?: number;
  /**
   * Resolution of the generated video (480p,580p, or 720p). Default value: `"720p"`
   */
  resolution?: "480p" | "580p" | "720p";
  /**
   * Aspect ratio of the generated video (16:9 or 9:16). Default value: `"16:9"`
   */
  aspect_ratio?: "auto" | "9:16" | "16:9";
  /**
   * Number of inference steps for sampling. Higher values give better quality but take longer. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * URL to the source video file. If provided, the model will use this video as a reference.
   */
  video_url?: string | Blob | File;
  /**
   * URL to the source mask file. If provided, the model will use this mask as a reference.
   */
  mask_video_url?: string | Blob | File;
  /**
   * URL to the guiding mask file. If provided, the model will use this mask as a reference to create masked video. If provided mask video url will be ignored.
   */
  mask_image_url?: string | Blob | File;
  /**
   * Urls to source reference image. If provided, the model will use this image as reference.
   */
  ref_image_urls?: Array<string>;
  /**
   * If set to true, the safety checker will be enabled.
   */
  enable_safety_checker?: boolean;
  /**
   * Whether to enable prompt expansion.
   */
  enable_prompt_expansion?: boolean;
  /**
   * Whether to preprocess the input video.
   */
  preprocess?: boolean;
};
export type WanVaceOutput = {
  /**
   * The generated video file.
   */
  video: File;
  /**
   * The seed used for generation.
   */
  seed: number;
};
export type WaveformInput = {
  /**
   * URL of the audio file to analyze
   */
  media_url: string | Blob | File;
  /**
   * Controls how many points are sampled per second of audio. Lower values (e.g. 1-2) create a coarser waveform, higher values (e.g. 4-10) create a more detailed one. Default value: `4`
   */
  points_per_second?: number;
  /**
   * Number of decimal places for the waveform values. Higher values provide more precision but increase payload size. Default value: `2`
   */
  precision?: number;
  /**
   * Size of the smoothing window. Higher values create a smoother waveform. Must be an odd number. Default value: `3`
   */
  smoothing_window?: number;
};
export type WaveformOutput = {
  /**
   * Normalized waveform data as an array of values between -1 and 1. The number of points is determined by audio duration × points_per_second.
   */
  waveform: Array<number>;
  /**
   * Duration of the audio in seconds
   */
  duration: number;
  /**
   * Number of points in the waveform data
   */
  points: number;
  /**
   * Number of decimal places used in the waveform values
   */
  precision: number;
};
export type WeatherEffectInput = {
  /**
   * Image prompt for the omni model.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * The safety tolerance level for the generated image. 1 being the most strict and 6 being the most permissive. Default value: `"2"`
   */
  safety_tolerance?: "1" | "2" | "3" | "4" | "5" | "6";
  /**
   * The format of the generated image. Default value: `"jpeg"`
   */
  output_format?: "jpeg" | "png";
  /**
   * The aspect ratio of the generated image.
   */
  aspect_ratio?:
    | "21:9"
    | "16:9"
    | "4:3"
    | "3:2"
    | "1:1"
    | "2:3"
    | "3:4"
    | "9:16"
    | "9:21";
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The weather effect to apply. Default value: `"heavy snowfall"`
   */
  prompt?: string;
};
export type WeatherEffectOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type WojakStyleInput = {
  /**
   * URL of the image to convert to wojak style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `1`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
};
export type WojakStyleOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type XAilabNsfwInput = {
  /**
   * List of image URLs to check. If more than 10 images are provided, only the first 10 will be checked.
   */
  image_urls: Array<string>;
};
export type XAilabNsfwOutput = {
  /**
   * List of booleans indicating if the image has an NSFW concept
   */
  has_nsfw_concepts: Array<boolean>;
};
export type YouTubeThumbnailsInput = {
  /**
   * URL of the image to convert to YouTube thumbnail style.
   */
  image_url: string | Blob | File;
  /**
   * The CFG (Classifier Free Guidance) scale is a measure of how close you want the model to stick to your prompt when looking for a related image to show you. Default value: `3.5`
   */
  guidance_scale?: number;
  /**
   * Number of inference steps for sampling. Default value: `30`
   */
  num_inference_steps?: number;
  /**
   * Whether to enable the safety checker for the generated image. Default value: `true`
   */
  enable_safety_checker?: boolean;
  /**
   * The scale factor for the LoRA model. Controls the strength of the LoRA effect. Default value: `0.5`
   */
  lora_scale?: number;
  /**
   * The same seed and the same prompt given to the same version of the model will output the same image every time.
   */
  seed?: number;
  /**
   * If set to true, the function will wait for the image to be generated and uploaded before returning the response. This will increase the latency of the function but it allows you to get the image directly in the response without going through the CDN.
   */
  sync_mode?: boolean;
  /**
   * The text to include in the YouTube thumbnail. Default value: `"Generate youtube thumbnails"`
   */
  prompt?: string;
};
export type YouTubeThumbnailsOutput = {
  /**
   *
   */
  images: Array<Image>;
  /**
   *
   */
  seed: number;
};
export type YueInput = {
  /**
   * The prompt to generate an image from. Must have two sections. Sections start with either [chorus] or a [verse].
   */
  lyrics: string;
  /**
   * The genres (separated by a space ' ') to guide the music generation.
   */
  genres: string;
};
export type YueOutput = {
  /**
   * Generated music file.
   */
  audio: File;
};
export type ZonosInput = {
  /**
   * The reference audio.
   */
  reference_audio_url: string | Blob | File;
  /**
   * The content generated using cloned voice.
   */
  prompt: string;
};
export type ZonosOutput = {
  /**
   * The generated audio
   */
  audio: File;
};
export type EndpointTypeMap = {
  "fal-ai/kling-video/v2.1/master/image-to-video": {
    input: KlingVideoV21MasterImageToVideoInput;
    output: KlingVideoV21MasterImageToVideoOutput;
  };
  "fal-ai/flux-pro/kontext": {
    input: FluxProKontextInput;
    output: FluxProKontextOutput;
  };
  "fal-ai/imagen4/preview": {
    input: Imagen4PreviewInput;
    output: Imagen4PreviewOutput;
  };
  "fal-ai/kling-video/v2/master/image-to-video": {
    input: KlingVideoV2MasterImageToVideoInput;
    output: KlingVideoV2MasterImageToVideoOutput;
  };
  "fal-ai/wan-effects": {
    input: WanEffectsInput;
    output: WanEffectsOutput;
  };
  "fal-ai/wan-pro/image-to-video": {
    input: WanProImageToVideoInput;
    output: WanProImageToVideoOutput;
  };
  "fal-ai/veo2/image-to-video": {
    input: Veo2ImageToVideoInput;
    output: Veo2ImageToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/pro/image-to-video": {
    input: KlingVideoV16ProImageToVideoInput;
    output: KlingVideoV16ProImageToVideoOutput;
  };
  "fal-ai/flux-lora-fast-training": {
    input: FluxLoraFastTrainingInput;
    output: FluxLoraFastTrainingOutput;
  };
  "fal-ai/playai/tts/dialog": {
    input: PlayaiTtsDialogInput;
    output: PlayaiTtsDialogOutput;
  };
  "fal-ai/flux-pro/v1.1-ultra": {
    input: FluxProV11UltraInput;
    output: FluxProV11UltraOutput;
  };
  "fal-ai/recraft/v3/text-to-image": {
    input: RecraftV3TextToImageInput;
    output: RecraftV3TextToImageOutput;
  };
  "fal-ai/minimax/video-01/image-to-video": {
    input: MinimaxVideo01ImageToVideoInput;
    output: MinimaxVideo01ImageToVideoOutput;
  };
  "fal-ai/flux-krea-trainer": {
    input: FluxKreaTrainerInput;
    output: FluxKreaTrainerOutput;
  };
  "fal-ai/veo3/fast": {
    input: Veo3FastInput;
    output: Veo3FastOutput;
  };
  "bria/video/background-removal": {
    input: VideoBackgroundRemovalInput;
    output: VideoBackgroundRemovalOutput;
  };
  "fal-ai/flux-kontext-trainer": {
    input: FluxKontextTrainerInput;
    output: FluxKontextTrainerOutput;
  };
  "fal-ai/minimax/hailuo-02/standard/image-to-video": {
    input: MinimaxHailuo02StandardImageToVideoInput;
    output: MinimaxHailuo02StandardImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/standard/text-to-video": {
    input: MinimaxHailuo02StandardTextToVideoInput;
    output: MinimaxHailuo02StandardTextToVideoOutput;
  };
  "bria/text-to-image/3.2": {
    input: TextToImage32Input;
    output: TextToImage32Output;
  };
  "fal-ai/bytedance/seedance/v1/pro/image-to-video": {
    input: BytedanceSeedanceV1ProImageToVideoInput;
    output: BytedanceSeedanceV1ProImageToVideoOutput;
  };
  "fal-ai/veo3": {
    input: Veo3Input;
    output: Veo3Output;
  };
  "fal-ai/kling-video/v2.1/standard/image-to-video": {
    input: KlingVideoV21StandardImageToVideoInput;
    output: KlingVideoV21StandardImageToVideoOutput;
  };
  "fal-ai/pixverse/v4.5/image-to-video": {
    input: PixverseV45ImageToVideoInput;
    output: PixverseV45ImageToVideoOutput;
  };
  "fal-ai/tavus/hummingbird-lipsync/v0": {
    input: TavusHummingbirdLipsyncV0Input;
    output: TavusHummingbirdLipsyncV0Output;
  };
  "fal-ai/kling-video/v2/master/text-to-video": {
    input: KlingVideoV2MasterTextToVideoInput;
    output: KlingVideoV2MasterTextToVideoOutput;
  };
  "fal-ai/hidream-i1-full": {
    input: HidreamI1FullInput;
    output: any;
  };
  "fal-ai/hidream-i1-dev": {
    input: HidreamI1DevInput;
    output: HidreamI1DevOutput;
  };
  "fal-ai/hidream-i1-fast": {
    input: HidreamI1FastInput;
    output: HidreamI1FastOutput;
  };
  "fal-ai/flux/dev": {
    input: FluxDevInput;
    output: FluxDevOutput;
  };
  "fal-ai/wan-i2v": {
    input: WanI2vInput;
    output: WanI2vOutput;
  };
  "fal-ai/mmaudio-v2": {
    input: MmaudioV2Input;
    output: MmaudioV2Output;
  };
  "fal-ai/ideogram/v2": {
    input: IdeogramV2Input;
    output: IdeogramV2Output;
  };
  "fal-ai/flux-lora-portrait-trainer": {
    input: FluxLoraPortraitTrainerInput;
    output: FluxLoraPortraitTrainerOutput;
  };
  "fal-ai/stable-diffusion-v35-large": {
    input: StableDiffusionV35LargeInput;
    output: StableDiffusionV35LargeOutput;
  };
  "fal-ai/flux-general": {
    input: FluxGeneralInput;
    output: FluxGeneralOutput;
  };
  "fal-ai/flux-lora": {
    input: FluxLoraInput;
    output: FluxLoraOutput;
  };
  "fal-ai/flux/dev/image-to-image": {
    input: FluxDevImageToImageInput;
    output: FluxDevImageToImageOutput;
  };
  "fal-ai/aura-sr": {
    input: AuraSrInput;
    output: AuraSrOutput;
  };
  "fal-ai/clarity-upscaler": {
    input: ClarityUpscalerInput;
    output: ClarityUpscalerOutput;
  };
  "fal-ai/ideogram/character/edit": {
    input: IdeogramCharacterEditInput;
    output: IdeogramCharacterEditOutput;
  };
  "fal-ai/ideogram/character": {
    input: IdeogramCharacterInput;
    output: IdeogramCharacterOutput;
  };
  "fal-ai/ideogram/character/remix": {
    input: IdeogramCharacterRemixInput;
    output: IdeogramCharacterRemixOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video/lora": {
    input: WanV22A14bTextToVideoLoraInput;
    output: WanV22A14bTextToVideoLoraOutput;
  };
  "fal-ai/wan/v2.2-a14b/image-to-video/lora": {
    input: WanV22A14bImageToVideoLoraInput;
    output: WanV22A14bImageToVideoLoraOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video/distill": {
    input: WanV225bTextToVideoDistillInput;
    output: WanV225bTextToVideoDistillOutput;
  };
  "fal-ai/minimax/hailuo-02-fast/image-to-video": {
    input: MinimaxHailuo02FastImageToVideoInput;
    output: MinimaxHailuo02FastImageToVideoOutput;
  };
  "fal-ai/bytedance/dreamina/v3.1/text-to-image": {
    input: BytedanceDreaminaV31TextToImageInput;
    output: BytedanceDreaminaV31TextToImageOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video/fast-wan": {
    input: WanV225bTextToVideoFastWanInput;
    output: WanV225bTextToVideoFastWanOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-image/lora": {
    input: WanV22A14bTextToImageLoraInput;
    output: WanV22A14bTextToImageLoraOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-image": {
    input: WanV225bTextToImageInput;
    output: WanV225bTextToImageOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-image": {
    input: WanV22A14bTextToImageInput;
    output: WanV22A14bTextToImageOutput;
  };
  "fal-ai/qwen-image": {
    input: QwenImageInput;
    output: QwenImageOutput;
  };
  "fal-ai/wan/v2.2-a14b/video-to-video": {
    input: WanV22A14bVideoToVideoInput;
    output: WanV22A14bVideoToVideoOutput;
  };
  "fal-ai/flux-krea-lora/stream": {
    input: FluxKreaLoraStreamInput;
    output: FluxKreaLoraStreamOutput;
  };
  "fal-ai/flux-krea-lora/inpainting": {
    input: FluxKreaLoraInpaintingInput;
    output: FluxKreaLoraInpaintingOutput;
  };
  "fal-ai/flux-krea-lora": {
    input: FluxKreaLoraInput;
    output: FluxKreaLoraOutput;
  };
  "fal-ai/flux-krea-lora/image-to-image": {
    input: FluxKreaLoraImageToImageInput;
    output: FluxKreaLoraImageToImageOutput;
  };
  "easel-ai/fashion-tryon": {
    input: FashionTryonInput;
    output: FashionTryonOutput;
  };
  "fal-ai/veo3/image-to-video": {
    input: Veo3ImageToVideoInput;
    output: Veo3ImageToVideoOutput;
  };
  "fal-ai/wan/v2.2-a14b/image-to-video/turbo": {
    input: WanV22A14bImageToVideoTurboInput;
    output: WanV22A14bImageToVideoTurboOutput;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video/turbo": {
    input: WanV22A14bTextToVideoTurboInput;
    output: WanV22A14bTextToVideoTurboOutput;
  };
  "fal-ai/flux/krea/image-to-image": {
    input: FluxKreaImageToImageInput;
    output: FluxKreaImageToImageOutput;
  };
  "fal-ai/flux/krea/redux": {
    input: FluxKreaReduxInput;
    output: FluxKreaReduxOutput;
  };
  "fal-ai/flux/krea": {
    input: FluxKreaInput;
    output: FluxKreaOutput;
  };
  "fal-ai/flux-1/krea/image-to-image": {
    input: Flux1KreaImageToImageInput;
    output: Flux1KreaImageToImageOutput;
  };
  "fal-ai/flux-1/krea/redux": {
    input: Flux1KreaReduxInput;
    output: Flux1KreaReduxOutput;
  };
  "fal-ai/flux-1/krea": {
    input: Flux1KreaInput;
    output: Flux1KreaOutput;
  };
  "fal-ai/wan/v2.2-5b/image-to-video": {
    input: WanV225bImageToVideoInput;
    output: WanV225bImageToVideoOutput;
  };
  "fal-ai/flux-kontext-lora/inpaint": {
    input: FluxKontextLoraInpaintInput;
    output: FluxKontextLoraInpaintOutput;
  };
  "fal-ai/wan/v2.2-5b/text-to-video": {
    input: WanV225bTextToVideoInput;
    output: WanV225bTextToVideoOutput;
  };
  "fal-ai/wan/v2.2-a14B/image-to-video": {
    input: any;
    output: any;
  };
  "fal-ai/wan/v2.2-a14b/text-to-video": {
    input: WanV22A14bTextToVideoInput;
    output: WanV22A14bTextToVideoOutput;
  };
  "fal-ai/hunyuan_world": {
    input: Hunyuan_worldInput;
    output: Hunyuan_worldOutput;
  };
  "fal-ai/hunyuan_world/image-to-world": {
    input: Hunyuan_worldImageToWorldInput;
    output: Hunyuan_worldImageToWorldOutput;
  };
  "fal-ai/x-ailab/nsfw": {
    input: XAilabNsfwInput;
    output: XAilabNsfwOutput;
  };
  "fal-ai/bytedance/omnihuman": {
    input: BytedanceOmnihumanInput;
    output: BytedanceOmnihumanOutput;
  };
  "fal-ai/sky-raccoon": {
    input: SkyRaccoonInput;
    output: SkyRaccoonOutput;
  };
  "fal-ai/image-editing/retouch": {
    input: ImageEditingRetouchInput;
    output: ImageEditingRetouchOutput;
  };
  "fal-ai/hidream-e1-1": {
    input: HidreamE11Input;
    output: HidreamE11Output;
  };
  "fal-ai/ltxv-13b-098-distilled/extend": {
    input: Ltxv13b098DistilledExtendInput;
    output: Ltxv13b098DistilledExtendOutput;
  };
  "fal-ai/rife/video": {
    input: RifeVideoInput;
    output: RifeVideoOutput;
  };
  "fal-ai/rife": {
    input: RifeInput;
    output: RifeOutput;
  };
  "fal-ai/film/video": {
    input: FilmVideoInput;
    output: FilmVideoOutput;
  };
  "fal-ai/film": {
    input: FilmInput;
    output: FilmOutput;
  };
  "fal-ai/minimax/voice-design": {
    input: MinimaxVoiceDesignInput;
    output: MinimaxVoiceDesignOutput;
  };
  "creatify/lipsync": {
    input: LipsyncInput;
    output: LipsyncOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/modify": {
    input: LumaDreamMachineRay2FlashModifyInput;
    output: LumaDreamMachineRay2FlashModifyOutput;
  };
  "fal-ai/ltxv-13b-098-distilled/image-to-video": {
    input: Ltxv13b098DistilledImageToVideoInput;
    output: Ltxv13b098DistilledImageToVideoOutput;
  };
  "fal-ai/ltxv-13b-098-distilled": {
    input: Ltxv13b098DistilledInput;
    output: Ltxv13b098DistilledOutput;
  };
  "fal-ai/ltxv-13b-098-distilled/multiconditioning": {
    input: Ltxv13b098DistilledMulticonditioningInput;
    output: Ltxv13b098DistilledMulticonditioningOutput;
  };
  "fal-ai/any-llm/enterprise": {
    input: AnyLlmEnterpriseInput;
    output: AnyLlmEnterpriseOutput;
  };
  "easel-ai/fashion-photoshoot": {
    input: FashionPhotoshootInput;
    output: FashionPhotoshootOutput;
  };
  "fal-ai/calligrapher": {
    input: CalligrapherInput;
    output: CalligrapherOutput;
  };
  "fal-ai/veo3/fast/image-to-video": {
    input: Veo3FastImageToVideoInput;
    output: Veo3FastImageToVideoOutput;
  };
  "fal-ai/ffmpeg-api/loudnorm": {
    input: FfmpegApiLoudnormInput;
    output: FfmpegApiLoudnormOutput;
  };
  "fal-ai/vidu/q1/reference-to-video": {
    input: ViduQ1ReferenceToVideoInput;
    output: ViduQ1ReferenceToVideoOutput;
  };
  "fal-ai/bria/reimagine": {
    input: BriaReimagineInput;
    output: BriaReimagineOutput;
  };
  "fal-ai/pixverse/sound-effects": {
    input: PixverseSoundEffectsInput;
    output: PixverseSoundEffectsOutput;
  };
  "fal-ai/image-editing/realism": {
    input: ImageEditingRealismInput;
    output: ImageEditingRealismOutput;
  };
  "fal-ai/thinksound/audio": {
    input: ThinksoundAudioInput;
    output: ThinksoundAudioOutput;
  };
  "fal-ai/thinksound": {
    input: ThinksoundInput;
    output: ThinksoundOutput;
  };
  "fal-ai/post-processing/vignette": {
    input: PostProcessingVignetteInput;
    output: PostProcessingVignetteOutput;
  };
  "fal-ai/post-processing/solarize": {
    input: PostProcessingSolarizeInput;
    output: PostProcessingSolarizeOutput;
  };
  "fal-ai/post-processing/sharpen": {
    input: PostProcessingSharpenInput;
    output: PostProcessingSharpenOutput;
  };
  "fal-ai/post-processing/parabolize": {
    input: PostProcessingParabolizeInput;
    output: PostProcessingParabolizeOutput;
  };
  "fal-ai/post-processing/grain": {
    input: PostProcessingGrainInput;
    output: PostProcessingGrainOutput;
  };
  "fal-ai/post-processing/dodge-burn": {
    input: PostProcessingDodgeBurnInput;
    output: PostProcessingDodgeBurnOutput;
  };
  "fal-ai/post-processing/dissolve": {
    input: PostProcessingDissolveInput;
    output: PostProcessingDissolveOutput;
  };
  "fal-ai/post-processing/desaturate": {
    input: PostProcessingDesaturateInput;
    output: PostProcessingDesaturateOutput;
  };
  "fal-ai/post-processing/color-tint": {
    input: PostProcessingColorTintInput;
    output: PostProcessingColorTintOutput;
  };
  "fal-ai/post-processing/color-correction": {
    input: PostProcessingColorCorrectionInput;
    output: PostProcessingColorCorrectionOutput;
  };
  "fal-ai/post-processing/chromatic-aberration": {
    input: PostProcessingChromaticAberrationInput;
    output: PostProcessingChromaticAberrationOutput;
  };
  "fal-ai/post-processing/blur": {
    input: PostProcessingBlurInput;
    output: PostProcessingBlurOutput;
  };
  "fal-ai/pixverse/extend/fast": {
    input: PixverseExtendFastInput;
    output: PixverseExtendFastOutput;
  };
  "fal-ai/pixverse/extend": {
    input: PixverseExtendInput;
    output: PixverseExtendOutput;
  };
  "fal-ai/pixverse/lipsync": {
    input: PixverseLipsyncInput;
    output: PixverseLipsyncOutput;
  };
  "fal-ai/image-editing/youtube-thumbnails": {
    input: ImageEditingYoutubeThumbnailsInput;
    output: ImageEditingYoutubeThumbnailsOutput;
  };
  "fal-ai/luma-dream-machine/ray-2/modify": {
    input: LumaDreamMachineRay2ModifyInput;
    output: LumaDreamMachineRay2ModifyOutput;
  };
  "fal-ai/bytedance/seededit/v3/edit-image": {
    input: BytedanceSeededitV3EditImageInput;
    output: BytedanceSeededitV3EditImageOutput;
  };
  "fal-ai/image-editing/broccoli-haircut": {
    input: ImageEditingBroccoliHaircutInput;
    output: ImageEditingBroccoliHaircutOutput;
  };
  "fal-ai/image-editing/wojak-style": {
    input: ImageEditingWojakStyleInput;
    output: ImageEditingWojakStyleOutput;
  };
  "fal-ai/image-editing/plushie-style": {
    input: ImageEditingPlushieStyleInput;
    output: ImageEditingPlushieStyleOutput;
  };
  "fal-ai/flux-kontext-lora/text-to-image": {
    input: FluxKontextLoraTextToImageInput;
    output: FluxKontextLoraTextToImageOutput;
  };
  "fal-ai/flux-kontext-lora": {
    input: FluxKontextLoraInput;
    output: FluxKontextLoraOutput;
  };
  "fal-ai/omnigen-v2": {
    input: OmnigenV2Input;
    output: OmnigenV2Output;
  };
  "fal-ai/fashn/tryon/v1.6": {
    input: FashnTryonV16Input;
    output: FashnTryonV16Output;
  };
  "fal-ai/ai-avatar/single-text": {
    input: AiAvatarSingleTextInput;
    output: AiAvatarSingleTextOutput;
  };
  "fal-ai/ai-avatar": {
    input: AiAvatarInput;
    output: AiAvatarOutput;
  };
  "fal-ai/ai-avatar/multi-text": {
    input: AiAvatarMultiTextInput;
    output: AiAvatarMultiTextOutput;
  };
  "fal-ai/ai-avatar/multi": {
    input: AiAvatarMultiInput;
    output: AiAvatarMultiOutput;
  };
  "fal-ai/video-understanding": {
    input: VideoUnderstandingInput;
    output: VideoUnderstandingOutput;
  };
  "fal-ai/wan-vace-14b/reframe": {
    input: WanVace14bReframeInput;
    output: WanVace14bReframeOutput;
  };
  "fal-ai/wan-vace-14b/outpainting": {
    input: WanVace14bOutpaintingInput;
    output: WanVace14bOutpaintingOutput;
  };
  "fal-ai/wan-vace-14b/inpainting": {
    input: WanVace14bInpaintingInput;
    output: WanVace14bInpaintingOutput;
  };
  "fal-ai/wan-vace-14b/pose": {
    input: WanVace14bPoseInput;
    output: WanVace14bPoseOutput;
  };
  "fal-ai/wan-vace-14b/depth": {
    input: WanVace14bDepthInput;
    output: WanVace14bDepthOutput;
  };
  "fal-ai/chain-of-zoom": {
    input: ChainOfZoomInput;
    output: ChainOfZoomOutput;
  };
  "tripo3d/tripo/v2.5/multiview-to-3d": {
    input: TripoV25MultiviewTo3dInput;
    output: TripoV25MultiviewTo3dOutput;
  };
  "fal-ai/minimax/hailuo-02/pro/image-to-video": {
    input: MinimaxHailuo02ProImageToVideoInput;
    output: MinimaxHailuo02ProImageToVideoOutput;
  };
  "fal-ai/minimax/hailuo-02/pro/text-to-video": {
    input: MinimaxHailuo02ProTextToVideoInput;
    output: MinimaxHailuo02ProTextToVideoOutput;
  };
  "fal-ai/pasd": {
    input: PasdInput;
    output: PasdOutput;
  };
  "fal-ai/object-removal/bbox": {
    input: ObjectRemovalBboxInput;
    output: ObjectRemovalBboxOutput;
  };
  "fal-ai/object-removal/mask": {
    input: ObjectRemovalMaskInput;
    output: ObjectRemovalMaskOutput;
  };
  "fal-ai/object-removal": {
    input: ObjectRemovalInput;
    output: ObjectRemovalOutput;
  };
  "fal-ai/bytedance/seedance/v1/pro/text-to-video": {
    input: BytedanceSeedanceV1ProTextToVideoInput;
    output: BytedanceSeedanceV1ProTextToVideoOutput;
  };
  "fal-ai/hunyuan3d-v21": {
    input: Hunyuan3dV21Input;
    output: Hunyuan3dV21Output;
  };
  "fal-ai/bytedance/seedance/v1/lite/image-to-video": {
    input: BytedanceSeedanceV1LiteImageToVideoInput;
    output: BytedanceSeedanceV1LiteImageToVideoOutput;
  };
  "fal-ai/bytedance/seedance/v1/lite/text-to-video": {
    input: BytedanceSeedanceV1LiteTextToVideoInput;
    output: BytedanceSeedanceV1LiteTextToVideoOutput;
  };
  "fal-ai/recraft/vectorize": {
    input: RecraftVectorizeInput;
    output: RecraftVectorizeOutput;
  };
  "fal-ai/wan-trainer/t2v": {
    input: WanTrainerT2vInput;
    output: WanTrainerT2vOutput;
  };
  "fal-ai/wan-trainer/t2v-14b": {
    input: WanTrainerT2v14bInput;
    output: WanTrainerT2v14bOutput;
  };
  "fal-ai/wan-trainer/i2v-720p": {
    input: WanTrainerI2v720pInput;
    output: WanTrainerI2v720pOutput;
  };
  "fal-ai/wan-trainer/flf2v-720p": {
    input: WanTrainerFlf2v720pInput;
    output: WanTrainerFlf2v720pOutput;
  };
  "fal-ai/bytedance/seedream/v3/text-to-image": {
    input: BytedanceSeedreamV3TextToImageInput;
    output: BytedanceSeedreamV3TextToImageOutput;
  };
  "fal-ai/ffmpeg-api/extract-frame": {
    input: FfmpegApiExtractFrameInput;
    output: FfmpegApiExtractFrameOutput;
  };
  "fal-ai/ffmpeg-api/merge-audio-video": {
    input: FfmpegApiMergeAudioVideoInput;
    output: FfmpegApiMergeAudioVideoOutput;
  };
  "fal-ai/luma-photon/flash/modify": {
    input: LumaPhotonFlashModifyInput;
    output: LumaPhotonFlashModifyOutput;
  };
  "fal-ai/luma-photon/modify": {
    input: LumaPhotonModifyInput;
    output: LumaPhotonModifyOutput;
  };
  "fal-ai/image-editing/reframe": {
    input: ImageEditingReframeInput;
    output: ImageEditingReframeOutput;
  };
  "fal-ai/wan-vace-1-3b": {
    input: WanVace13bInput;
    output: WanVace13bOutput;
  };
  "fal-ai/image-editing/baby-version": {
    input: ImageEditingBabyVersionInput;
    output: ImageEditingBabyVersionOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/reframe": {
    input: LumaDreamMachineRay2FlashReframeInput;
    output: LumaDreamMachineRay2FlashReframeOutput;
  };
  "fal-ai/luma-dream-machine/ray-2/reframe": {
    input: LumaDreamMachineRay2ReframeInput;
    output: LumaDreamMachineRay2ReframeOutput;
  };
  "fal-ai/luma-photon/flash/reframe": {
    input: LumaPhotonFlashReframeInput;
    output: LumaPhotonFlashReframeOutput;
  };
  "fal-ai/luma-photon/reframe": {
    input: LumaPhotonReframeInput;
    output: LumaPhotonReframeOutput;
  };
  "resemble-ai/chatterboxhd/speech-to-speech": {
    input: ChatterboxhdSpeechToSpeechInput;
    output: ChatterboxhdSpeechToSpeechOutput;
  };
  "resemble-ai/chatterboxhd/text-to-speech": {
    input: ChatterboxhdTextToSpeechInput;
    output: ChatterboxhdTextToSpeechOutput;
  };
  "fal-ai/flux-1/schnell/redux": {
    input: Flux1SchnellReduxInput;
    output: Flux1SchnellReduxOutput;
  };
  "fal-ai/flux-1/dev/redux": {
    input: Flux1DevReduxInput;
    output: Flux1DevReduxOutput;
  };
  "fal-ai/flux-1/dev/image-to-image": {
    input: Flux1DevImageToImageInput;
    output: Flux1DevImageToImageOutput;
  };
  "fal-ai/flux-1/schnell": {
    input: Flux1SchnellInput;
    output: Flux1SchnellOutput;
  };
  "fal-ai/flux-1/dev": {
    input: Flux1DevInput;
    output: Flux1DevOutput;
  };
  "fal-ai/playai/inpaint/diffusion": {
    input: PlayaiInpaintDiffusionInput;
    output: PlayaiInpaintDiffusionOutput;
  };
  "fal-ai/image-editing/text-removal": {
    input: ImageEditingTextRemovalInput;
    output: ImageEditingTextRemovalOutput;
  };
  "fal-ai/image-editing/photo-restoration": {
    input: ImageEditingPhotoRestorationInput;
    output: ImageEditingPhotoRestorationOutput;
  };
  "fal-ai/chatterbox/speech-to-speech": {
    input: ChatterboxSpeechToSpeechInput;
    output: ChatterboxSpeechToSpeechOutput;
  };
  "fal-ai/chatterbox/text-to-speech": {
    input: ChatterboxTextToSpeechInput;
    output: any;
  };
  "fal-ai/image-editing/weather-effect": {
    input: ImageEditingWeatherEffectInput;
    output: ImageEditingWeatherEffectOutput;
  };
  "fal-ai/image-editing/time-of-day": {
    input: ImageEditingTimeOfDayInput;
    output: ImageEditingTimeOfDayOutput;
  };
  "fal-ai/image-editing/style-transfer": {
    input: ImageEditingStyleTransferInput;
    output: ImageEditingStyleTransferOutput;
  };
  "fal-ai/image-editing/scene-composition": {
    input: ImageEditingSceneCompositionInput;
    output: ImageEditingSceneCompositionOutput;
  };
  "fal-ai/image-editing/professional-photo": {
    input: ImageEditingProfessionalPhotoInput;
    output: ImageEditingProfessionalPhotoOutput;
  };
  "fal-ai/image-editing/object-removal": {
    input: ImageEditingObjectRemovalInput;
    output: ImageEditingObjectRemovalOutput;
  };
  "fal-ai/image-editing/hair-change": {
    input: ImageEditingHairChangeInput;
    output: ImageEditingHairChangeOutput;
  };
  "fal-ai/image-editing/face-enhancement": {
    input: ImageEditingFaceEnhancementInput;
    output: ImageEditingFaceEnhancementOutput;
  };
  "fal-ai/image-editing/expression-change": {
    input: ImageEditingExpressionChangeInput;
    output: ImageEditingExpressionChangeOutput;
  };
  "fal-ai/image-editing/color-correction": {
    input: ImageEditingColorCorrectionInput;
    output: ImageEditingColorCorrectionOutput;
  };
  "fal-ai/image-editing/cartoonify": {
    input: ImageEditingCartoonifyInput;
    output: ImageEditingCartoonifyOutput;
  };
  "fal-ai/image-editing/background-change": {
    input: ImageEditingBackgroundChangeInput;
    output: ImageEditingBackgroundChangeOutput;
  };
  "fal-ai/image-editing/age-progression": {
    input: ImageEditingAgeProgressionInput;
    output: ImageEditingAgeProgressionOutput;
  };
  "fal-ai/flux-pro/kontext/max/multi": {
    input: FluxProKontextMaxMultiInput;
    output: FluxProKontextMaxMultiOutput;
  };
  "fal-ai/flux-pro/kontext/multi": {
    input: FluxProKontextMultiInput;
    output: FluxProKontextMultiOutput;
  };
  "fal-ai/hunyuan-avatar": {
    input: HunyuanAvatarInput;
    output: HunyuanAvatarOutput;
  };
  "fal-ai/flux-pro/kontext/max": {
    input: FluxProKontextMaxInput;
    output: FluxProKontextMaxOutput;
  };
  "fal-ai/flux-pro/kontext/max/text-to-image": {
    input: FluxProKontextMaxTextToImageInput;
    output: FluxProKontextMaxTextToImageOutput;
  };
  "fal-ai/kling-video/v2.1/master/text-to-video": {
    input: KlingVideoV21MasterTextToVideoInput;
    output: KlingVideoV21MasterTextToVideoOutput;
  };
  "fal-ai/kling-video/v2.1/pro/image-to-video": {
    input: KlingVideoV21ProImageToVideoInput;
    output: KlingVideoV21ProImageToVideoOutput;
  };
  "fal-ai/flux-pro/kontext/text-to-image": {
    input: FluxProKontextTextToImageInput;
    output: FluxProKontextTextToImageOutput;
  };
  "fal-ai/flux-kontext/dev": {
    input: FluxKontextDevInput;
    output: FluxKontextDevOutput;
  };
  "veed/lipsync": {
    input: LipsyncInput;
    output: LipsyncOutput;
  };
  "veed/avatars/text-to-video": {
    input: AvatarsTextToVideoInput;
    output: AvatarsTextToVideoOutput;
  };
  "veed/avatars/audio-to-video": {
    input: AvatarsAudioToVideoInput;
    output: AvatarsAudioToVideoOutput;
  };
  "fal-ai/hunyuan-portrait": {
    input: HunyuanPortraitInput;
    output: HunyuanPortraitOutput;
  };
  "fal-ai/wan-vace-14b": {
    input: WanVace14bInput;
    output: WanVace14bOutput;
  };
  "fal-ai/bagel/understand": {
    input: BagelUnderstandInput;
    output: BagelUnderstandOutput;
  };
  "fal-ai/bagel/edit": {
    input: BagelEditInput;
    output: BagelEditOutput;
  };
  "fal-ai/bagel": {
    input: BagelInput;
    output: BagelOutput;
  };
  "fal-ai/lyria2": {
    input: Lyria2Input;
    output: Lyria2Output;
  };
  "fal-ai/imagen4/preview/ultra": {
    input: Imagen4PreviewUltraInput;
    output: Imagen4PreviewUltraOutput;
  };
  "fal-ai/kling-video/v1.6/standard/elements": {
    input: KlingVideoV16StandardElementsInput;
    output: KlingVideoV16StandardElementsOutput;
  };
  "fal-ai/kling-video/v1.6/pro/elements": {
    input: KlingVideoV16ProElementsInput;
    output: KlingVideoV16ProElementsOutput;
  };
  "fal-ai/dreamo": {
    input: DreamoInput;
    output: DreamoOutput;
  };
  "fal-ai/ltx-video-13b-distilled/extend": {
    input: LtxVideo13bDistilledExtendInput;
    output: LtxVideo13bDistilledExtendOutput;
  };
  "fal-ai/ltx-video-13b-distilled/multiconditioning": {
    input: LtxVideo13bDistilledMulticonditioningInput;
    output: LtxVideo13bDistilledMulticonditioningOutput;
  };
  "fal-ai/ltx-video-13b-distilled/image-to-video": {
    input: LtxVideo13bDistilledImageToVideoInput;
    output: LtxVideo13bDistilledImageToVideoOutput;
  };
  "fal-ai/ltx-video-13b-dev/multiconditioning": {
    input: LtxVideo13bDevMulticonditioningInput;
    output: LtxVideo13bDevMulticonditioningOutput;
  };
  "fal-ai/ltx-video-13b-dev/extend": {
    input: LtxVideo13bDevExtendInput;
    output: LtxVideo13bDevExtendOutput;
  };
  "fal-ai/ltx-video-13b-dev/image-to-video": {
    input: LtxVideo13bDevImageToVideoInput;
    output: LtxVideo13bDevImageToVideoOutput;
  };
  "fal-ai/ltx-video-13b-dev": {
    input: LtxVideo13bDevInput;
    output: LtxVideo13bDevOutput;
  };
  "fal-ai/ltx-video-13b-distilled": {
    input: LtxVideo13bDistilledInput;
    output: LtxVideo13bDistilledOutput;
  };
  "easel-ai/easel-gifswap": {
    input: EaselGifswapInput;
    output: EaselGifswapOutput;
  };
  "fal-ai/flux-lora/stream": {
    input: FluxLoraStreamInput;
    output: FluxLoraStreamOutput;
  };
  "fal-ai/ltx-video-lora/multiconditioning": {
    input: LtxVideoLoraMulticonditioningInput;
    output: LtxVideoLoraMulticonditioningOutput;
  };
  "fal-ai/ltx-video-lora/image-to-video": {
    input: LtxVideoLoraImageToVideoInput;
    output: LtxVideoLoraImageToVideoOutput;
  };
  "fal-ai/ltx-video-lora": {
    input: LtxVideoLoraInput;
    output: LtxVideoLoraOutput;
  };
  "fal-ai/pixverse/v4.5/transition": {
    input: PixverseV45TransitionInput;
    output: PixverseV45TransitionOutput;
  };
  "fal-ai/pixverse/v4.5/image-to-video/fast": {
    input: PixverseV45ImageToVideoFastInput;
    output: PixverseV45ImageToVideoFastOutput;
  };
  "fal-ai/pixverse/v4.5/text-to-video/fast": {
    input: PixverseV45TextToVideoFastInput;
    output: PixverseV45TextToVideoFastOutput;
  };
  "fal-ai/pixverse/v4.5/text-to-video": {
    input: PixverseV45TextToVideoInput;
    output: PixverseV45TextToVideoOutput;
  };
  "fal-ai/pixverse/v4.5/effects": {
    input: PixverseV45EffectsInput;
    output: PixverseV45EffectsOutput;
  };
  "fal-ai/hunyuan-custom": {
    input: HunyuanCustomInput;
    output: HunyuanCustomOutput;
  };
  "fal-ai/framepack/f1": {
    input: FramepackF1Input;
    output: FramepackF1Output;
  };
  "fal-ai/ace-step/audio-outpaint": {
    input: AceStepAudioOutpaintInput;
    output: AceStepAudioOutpaintOutput;
  };
  "fal-ai/ace-step/audio-inpaint": {
    input: AceStepAudioInpaintInput;
    output: AceStepAudioInpaintOutput;
  };
  "fal-ai/ace-step/audio-to-audio": {
    input: AceStepAudioToAudioInput;
    output: AceStepAudioToAudioOutput;
  };
  "fal-ai/ace-step/prompt-to-audio": {
    input: AceStepPromptToAudioInput;
    output: AceStepPromptToAudioOutput;
  };
  "smoretalk-ai/rembg-enhance": {
    input: RembgEnhanceInput;
    output: RembgEnhanceOutput;
  };
  "fal-ai/vidu/q1/start-end-to-video": {
    input: ViduQ1StartEndToVideoInput;
    output: ViduQ1StartEndToVideoOutput;
  };
  "fal-ai/vidu/q1/text-to-video": {
    input: ViduQ1TextToVideoInput;
    output: ViduQ1TextToVideoOutput;
  };
  "fal-ai/vidu/q1/image-to-video": {
    input: ViduQ1ImageToVideoInput;
    output: ViduQ1ImageToVideoOutput;
  };
  "fal-ai/ace-step": {
    input: AceStepInput;
    output: AceStepOutput;
  };
  "fal-ai/ltx-video-trainer": {
    input: LtxVideoTrainerInput;
    output: LtxVideoTrainerOutput;
  };
  "fal-ai/recraft/upscale/creative": {
    input: RecraftUpscaleCreativeInput;
    output: RecraftUpscaleCreativeOutput;
  };
  "fal-ai/recraft/upscale/crisp": {
    input: RecraftUpscaleCrispInput;
    output: RecraftUpscaleCrispOutput;
  };
  "fal-ai/recraft/v3/create-style": {
    input: RecraftV3CreateStyleInput;
    output: RecraftV3CreateStyleOutput;
  };
  "fal-ai/recraft/v3/image-to-image": {
    input: RecraftV3ImageToImageInput;
    output: RecraftV3ImageToImageOutput;
  };
  "fal-ai/ltx-video-v097/extend": {
    input: LtxVideoV097ExtendInput;
    output: LtxVideoV097ExtendOutput;
  };
  "easel-ai/easel-avatar": {
    input: EaselAvatarInput;
    output: EaselAvatarOutput;
  };
  "fal-ai/minimax/voice-clone": {
    input: MinimaxVoiceCloneInput;
    output: MinimaxVoiceCloneOutput;
  };
  "fal-ai/minimax/speech-02-turbo": {
    input: MinimaxSpeech02TurboInput;
    output: MinimaxSpeech02TurboOutput;
  };
  "fal-ai/ltx-video-v097/multiconditioning": {
    input: LtxVideoV097MulticonditioningInput;
    output: LtxVideoV097MulticonditioningOutput;
  };
  "fal-ai/minimax/speech-02-hd": {
    input: MinimaxSpeech02HdInput;
    output: MinimaxSpeech02HdOutput;
  };
  "fal-ai/ltx-video-v097": {
    input: LtxVideoV097Input;
    output: LtxVideoV097Output;
  };
  "fal-ai/ltx-video-v097/image-to-video": {
    input: LtxVideoV097ImageToVideoInput;
    output: LtxVideoV097ImageToVideoOutput;
  };
  "fal-ai/minimax/image-01/subject-reference": {
    input: MinimaxImage01SubjectReferenceInput;
    output: MinimaxImage01SubjectReferenceOutput;
  };
  "fal-ai/minimax/image-01": {
    input: MinimaxImage01Input;
    output: MinimaxImage01Output;
  };
  "fal-ai/hidream-i1-full/image-to-image": {
    input: HidreamI1FullImageToImageInput;
    output: HidreamI1FullImageToImageOutput;
  };
  "fal-ai/trellis/multi": {
    input: TrellisMultiInput;
    output: TrellisMultiOutput;
  };
  "fal-ai/ideogram/v3/reframe": {
    input: IdeogramV3ReframeInput;
    output: IdeogramV3ReframeOutput;
  };
  "fal-ai/ideogram/v3": {
    input: IdeogramV3Input;
    output: IdeogramV3Output;
  };
  "fal-ai/ideogram/v3/replace-background": {
    input: IdeogramV3ReplaceBackgroundInput;
    output: IdeogramV3ReplaceBackgroundOutput;
  };
  "fal-ai/ideogram/v3/remix": {
    input: IdeogramV3RemixInput;
    output: IdeogramV3RemixOutput;
  };
  "fal-ai/ideogram/v3/edit": {
    input: IdeogramV3EditInput;
    output: IdeogramV3EditOutput;
  };
  "fal-ai/hidream-e1-full": {
    input: HidreamE1FullInput;
    output: HidreamE1FullOutput;
  };
  "fal-ai/f-lite/standard": {
    input: FLiteStandardInput;
    output: FLiteStandardOutput;
  };
  "fal-ai/f-lite/texture": {
    input: FLiteTextureInput;
    output: FLiteTextureOutput;
  };
  "fal-ai/moondream2/visual-query": {
    input: Moondream2VisualQueryInput;
    output: Moondream2VisualQueryOutput;
  };
  "fal-ai/moondream2": {
    input: Moondream2Input;
    output: Moondream2Output;
  };
  "fal-ai/moondream2/point-object-detection": {
    input: Moondream2PointObjectDetectionInput;
    output: Moondream2PointObjectDetectionOutput;
  };
  "fal-ai/moondream2/object-detection": {
    input: Moondream2ObjectDetectionInput;
    output: Moondream2ObjectDetectionOutput;
  };
  "fal-ai/step1x-edit": {
    input: Step1xEditInput;
    output: Step1xEditOutput;
  };
  "tripo3d/tripo/v2.5/image-to-3d": {
    input: TripoV25ImageTo3dInput;
    output: TripoV25ImageTo3dOutput;
  };
  "fal-ai/image2svg": {
    input: Image2svgInput;
    output: Image2svgOutput;
  };
  "fal-ai/uno": {
    input: UnoInput;
    output: UnoOutput;
  };
  "fal-ai/gpt-image-1/edit-image/byok": {
    input: GptImage1EditImageByokInput;
    output: GptImage1EditImageByokOutput;
  };
  "fal-ai/gpt-image-1/text-to-image/byok": {
    input: GptImage1TextToImageByokInput;
    output: GptImage1TextToImageByokOutput;
  };
  "fal-ai/magi/extend-video": {
    input: MagiExtendVideoInput;
    output: MagiExtendVideoOutput;
  };
  "fal-ai/magi": {
    input: MagiInput;
    output: MagiOutput;
  };
  "fal-ai/magi/image-to-video": {
    input: MagiImageToVideoInput;
    output: MagiImageToVideoOutput;
  };
  "fal-ai/pixverse/v4/effects": {
    input: PixverseV4EffectsInput;
    output: PixverseV4EffectsOutput;
  };
  "fal-ai/magi-distilled/extend-video": {
    input: MagiDistilledExtendVideoInput;
    output: MagiDistilledExtendVideoOutput;
  };
  "fal-ai/magi-distilled/image-to-video": {
    input: MagiDistilledImageToVideoInput;
    output: MagiDistilledImageToVideoOutput;
  };
  "fal-ai/dia-tts/voice-clone": {
    input: DiaTtsVoiceCloneInput;
    output: DiaTtsVoiceCloneOutput;
  };
  "fal-ai/framepack/flf2v": {
    input: FramepackFlf2vInput;
    output: FramepackFlf2vOutput;
  };
  "fal-ai/dia-tts": {
    input: DiaTtsInput;
    output: DiaTtsOutput;
  };
  "fal-ai/magi-distilled": {
    input: MagiDistilledInput;
    output: MagiDistilledOutput;
  };
  "fal-ai/smart-turn": {
    input: SmartTurnInput;
    output: SmartTurnOutput;
  };
  "rundiffusion-fal/juggernaut-flux-lora/inpainting": {
    input: JuggernautFluxLoraInpaintingInput;
    output: JuggernautFluxLoraInpaintingOutput;
  };
  "fal-ai/fashn/tryon/v1.5": {
    input: FashnTryonV15Input;
    output: FashnTryonV15Output;
  };
  "fal-ai/plushify": {
    input: PlushifyInput;
    output: PlushifyOutput;
  };
  "fal-ai/instant-character": {
    input: InstantCharacterInput;
    output: InstantCharacterOutput;
  };
  "fal-ai/wan-flf2v": {
    input: WanFlf2vInput;
    output: WanFlf2vOutput;
  };
  "fal-ai/turbo-flux-trainer": {
    input: TurboFluxTrainerInput;
    output: TurboFluxTrainerOutput;
  };
  "fal-ai/framepack": {
    input: FramepackInput;
    output: FramepackOutput;
  };
  "fal-ai/cartoonify": {
    input: CartoonifyInput;
    output: CartoonifyOutput;
  };
  "fal-ai/wan-vace": {
    input: WanVaceInput;
    output: WanVaceOutput;
  };
  "fal-ai/finegrain-eraser/mask": {
    input: FinegrainEraserMaskInput;
    output: FinegrainEraserMaskOutput;
  };
  "fal-ai/finegrain-eraser/bbox": {
    input: FinegrainEraserBboxInput;
    output: FinegrainEraserBboxOutput;
  };
  "fal-ai/finegrain-eraser": {
    input: FinegrainEraserInput;
    output: FinegrainEraserOutput;
  };
  "cassetteai/video-sound-effects-generator": {
    input: VideoSoundEffectsGeneratorInput;
    output: VideoSoundEffectsGeneratorOutput;
  };
  "fal-ai/speech-to-text/turbo": {
    input: SpeechToTextTurboInput;
    output: SpeechToTextTurboOutput;
  };
  "fal-ai/speech-to-text/turbo/stream": {
    input: SpeechToTextTurboStreamInput;
    output: any;
  };
  "fal-ai/speech-to-text/stream": {
    input: SpeechToTextStreamInput;
    output: any;
  };
  "fal-ai/speech-to-text": {
    input: SpeechToTextInput;
    output: SpeechToTextOutput;
  };
  "cassetteai/sound-effects-generator": {
    input: SoundEffectsGeneratorInput;
    output: SoundEffectsGeneratorOutput;
  };
  "fal-ai/sync-lipsync/v2": {
    input: SyncLipsyncV2Input;
    output: SyncLipsyncV2Output;
  };
  "fal-ai/star-vector": {
    input: StarVectorInput;
    output: StarVectorOutput;
  };
  "fal-ai/pixverse/v4/image-to-video/fast": {
    input: PixverseV4ImageToVideoFastInput;
    output: PixverseV4ImageToVideoFastOutput;
  };
  "fal-ai/pixverse/v4/image-to-video": {
    input: PixverseV4ImageToVideoInput;
    output: PixverseV4ImageToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/effects": {
    input: PixverseV35EffectsInput;
    output: PixverseV35EffectsOutput;
  };
  "fal-ai/pixverse/v4/text-to-video": {
    input: PixverseV4TextToVideoInput;
    output: PixverseV4TextToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/transition": {
    input: PixverseV35TransitionInput;
    output: PixverseV35TransitionOutput;
  };
  "fal-ai/pixverse/v4/text-to-video/fast": {
    input: PixverseV4TextToVideoFastInput;
    output: PixverseV4TextToVideoFastOutput;
  };
  "fal-ai/ghiblify": {
    input: GhiblifyInput;
    output: GhiblifyOutput;
  };
  "fal-ai/orpheus-tts": {
    input: OrpheusTtsInput;
    output: OrpheusTtsOutput;
  };
  "fal-ai/sana/v1.5/1.6b": {
    input: SanaV1516bInput;
    output: SanaV1516bOutput;
  };
  "fal-ai/sana/v1.5/4.8b": {
    input: SanaV1548bInput;
    output: SanaV1548bOutput;
  };
  "fal-ai/sana/sprint": {
    input: SanaSprintInput;
    output: SanaSprintOutput;
  };
  "CassetteAI/music-generator": {
    input: MusicGeneratorInput;
    output: MusicGeneratorOutput;
  };
  "fal-ai/kling-video/lipsync/text-to-video": {
    input: KlingVideoLipsyncTextToVideoInput;
    output: KlingVideoLipsyncTextToVideoOutput;
  };
  "fal-ai/kling-video/lipsync/audio-to-video": {
    input: KlingVideoLipsyncAudioToVideoInput;
    output: KlingVideoLipsyncAudioToVideoOutput;
  };
  "fal-ai/latentsync": {
    input: LatentsyncInput;
    output: LatentsyncOutput;
  };
  "fal-ai/wan-t2v-lora": {
    input: WanT2vLoraInput;
    output: WanT2vLoraOutput;
  };
  "fal-ai/wan-trainer": {
    input: WanTrainerInput;
    output: WanTrainerOutput;
  };
  "fal-ai/thera": {
    input: TheraInput;
    output: TheraOutput;
  };
  "fal-ai/mix-dehaze-net": {
    input: MixDehazeNetInput;
    output: MixDehazeNetOutput;
  };
  "fal-ai/hunyuan3d/v2/multi-view/turbo": {
    input: Hunyuan3dV2MultiViewTurboInput;
    output: Hunyuan3dV2MultiViewTurboOutput;
  };
  "fal-ai/gemini-flash-edit": {
    input: GeminiFlashEditInput;
    output: GeminiFlashEditOutput;
  };
  "fal-ai/hunyuan3d/v2": {
    input: Hunyuan3dV2Input;
    output: Hunyuan3dV2Output;
  };
  "fal-ai/hunyuan3d/v2/multi-view": {
    input: Hunyuan3dV2MultiViewInput;
    output: Hunyuan3dV2MultiViewOutput;
  };
  "fal-ai/hunyuan3d/v2/mini": {
    input: Hunyuan3dV2MiniInput;
    output: Hunyuan3dV2MiniOutput;
  };
  "fal-ai/hunyuan3d/v2/turbo": {
    input: Hunyuan3dV2TurboInput;
    output: Hunyuan3dV2TurboOutput;
  };
  "fal-ai/hunyuan3d/v2/mini/turbo": {
    input: Hunyuan3dV2MiniTurboInput;
    output: Hunyuan3dV2MiniTurboOutput;
  };
  "fal-ai/gemini-flash-edit/multi": {
    input: GeminiFlashEditMultiInput;
    output: GeminiFlashEditMultiOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash/image-to-video": {
    input: LumaDreamMachineRay2FlashImageToVideoInput;
    output: LumaDreamMachineRay2FlashImageToVideoOutput;
  };
  "fal-ai/luma-dream-machine/ray-2-flash": {
    input: LumaDreamMachineRay2FlashInput;
    output: LumaDreamMachineRay2FlashOutput;
  };
  "fal-ai/pika/v1.5/pikaffects": {
    input: PikaV15PikaffectsInput;
    output: PikaV15PikaffectsOutput;
  };
  "fal-ai/pika/v2/turbo/image-to-video": {
    input: PikaV2TurboImageToVideoInput;
    output: PikaV2TurboImageToVideoOutput;
  };
  "fal-ai/invisible-watermark": {
    input: InvisibleWatermarkInput;
    output: InvisibleWatermarkOutput;
  };
  "fal-ai/pika/v2.1/text-to-video": {
    input: PikaV21TextToVideoInput;
    output: PikaV21TextToVideoOutput;
  };
  "fal-ai/pika/v2/turbo/text-to-video": {
    input: PikaV2TurboTextToVideoInput;
    output: PikaV2TurboTextToVideoOutput;
  };
  "fal-ai/pika/v2.2/image-to-video": {
    input: PikaV22ImageToVideoInput;
    output: PikaV22ImageToVideoOutput;
  };
  "fal-ai/pika/v2.2/text-to-video": {
    input: PikaV22TextToVideoInput;
    output: PikaV22TextToVideoOutput;
  };
  "fal-ai/pika/v2.2/pikascenes": {
    input: PikaV22PikascenesInput;
    output: PikaV22PikascenesOutput;
  };
  "fal-ai/pika/v2.1/image-to-video": {
    input: PikaV21ImageToVideoInput;
    output: PikaV21ImageToVideoOutput;
  };
  "fal-ai/csm-1b": {
    input: Csm1bInput;
    output: Csm1bOutput;
  };
  "fal-ai/vidu/image-to-video": {
    input: ViduImageToVideoInput;
    output: ViduImageToVideoOutput;
  };
  "fal-ai/vidu/start-end-to-video": {
    input: ViduStartEndToVideoInput;
    output: ViduStartEndToVideoOutput;
  };
  "fal-ai/vidu/reference-to-video": {
    input: ViduReferenceToVideoInput;
    output: ViduReferenceToVideoOutput;
  };
  "fal-ai/vidu/template-to-video": {
    input: ViduTemplateToVideoInput;
    output: ViduTemplateToVideoOutput;
  };
  "fal-ai/wan-pro/text-to-video": {
    input: WanProTextToVideoInput;
    output: WanProTextToVideoOutput;
  };
  "easel-ai/advanced-face-swap": {
    input: AdvancedFaceSwapInput;
    output: AdvancedFaceSwapOutput;
  };
  "fal-ai/wan-i2v-lora": {
    input: WanI2vLoraInput;
    output: WanI2vLoraOutput;
  };
  "fal-ai/kling-video/v1/standard/effects": {
    input: KlingVideoV1StandardEffectsInput;
    output: KlingVideoV1StandardEffectsOutput;
  };
  "fal-ai/kling-video/v1.6/pro/effects": {
    input: KlingVideoV16ProEffectsInput;
    output: KlingVideoV16ProEffectsOutput;
  };
  "fal-ai/kling-video/v1.5/pro/effects": {
    input: KlingVideoV15ProEffectsInput;
    output: KlingVideoV15ProEffectsOutput;
  };
  "fal-ai/kling-video/v1.6/standard/effects": {
    input: KlingVideoV16StandardEffectsInput;
    output: KlingVideoV16StandardEffectsOutput;
  };
  "fal-ai/hunyuan-video-image-to-video": {
    input: HunyuanVideoImageToVideoInput;
    output: HunyuanVideoImageToVideoOutput;
  };
  "rundiffusion-fal/juggernaut-flux/lightning": {
    input: JuggernautFluxLightningInput;
    output: JuggernautFluxLightningOutput;
  };
  "rundiffusion-fal/rundiffusion-photo-flux": {
    input: RundiffusionPhotoFluxInput;
    output: RundiffusionPhotoFluxOutput;
  };
  "rundiffusion-fal/juggernaut-flux/pro/image-to-image": {
    input: JuggernautFluxProImageToImageInput;
    output: JuggernautFluxProImageToImageOutput;
  };
  "rundiffusion-fal/juggernaut-flux-lora": {
    input: JuggernautFluxLoraInput;
    output: JuggernautFluxLoraOutput;
  };
  "fal-ai/ltx-video-v095/image-to-video": {
    input: LtxVideoV095ImageToVideoInput;
    output: LtxVideoV095ImageToVideoOutput;
  };
  "rundiffusion-fal/juggernaut-flux/base/image-to-image": {
    input: JuggernautFluxBaseImageToImageInput;
    output: JuggernautFluxBaseImageToImageOutput;
  };
  "fal-ai/ltx-video-v095/extend": {
    input: LtxVideoV095ExtendInput;
    output: LtxVideoV095ExtendOutput;
  };
  "fal-ai/ltx-video-v095/multiconditioning": {
    input: LtxVideoV095MulticonditioningInput;
    output: LtxVideoV095MulticonditioningOutput;
  };
  "fal-ai/ltx-video-v095": {
    input: LtxVideoV095Input;
    output: LtxVideoV095Output;
  };
  "rundiffusion-fal/juggernaut-flux/pro": {
    input: JuggernautFluxProInput;
    output: JuggernautFluxProOutput;
  };
  "rundiffusion-fal/juggernaut-flux/base": {
    input: JuggernautFluxBaseInput;
    output: JuggernautFluxBaseOutput;
  };
  "fal-ai/cogview4": {
    input: Cogview4Input;
    output: Cogview4Output;
  };
  "fal-ai/diffrhythm": {
    input: DiffrhythmInput;
    output: DiffrhythmOutput;
  };
  "fal-ai/topaz/upscale/video": {
    input: TopazUpscaleVideoInput;
    output: TopazUpscaleVideoOutput;
  };
  "fal-ai/docres/dewarp": {
    input: DocresDewarpInput;
    output: DocresDewarpOutput;
  };
  "fal-ai/docres": {
    input: DocresInput;
    output: DocresOutput;
  };
  "fal-ai/swin2sr": {
    input: Swin2srInput;
    output: Swin2srOutput;
  };
  "fal-ai/ideogram/v2a/remix": {
    input: IdeogramV2aRemixInput;
    output: IdeogramV2aRemixOutput;
  };
  "fal-ai/kling-video/v1.6/pro/text-to-video": {
    input: KlingVideoV16ProTextToVideoInput;
    output: KlingVideoV16ProTextToVideoOutput;
  };
  "fal-ai/ideogram/v2a/turbo/remix": {
    input: IdeogramV2aTurboRemixInput;
    output: IdeogramV2aTurboRemixOutput;
  };
  "fal-ai/elevenlabs/tts/multilingual-v2": {
    input: ElevenlabsTtsMultilingualV2Input;
    output: ElevenlabsTtsMultilingualV2Output;
  };
  "fal-ai/ideogram/v2a/turbo": {
    input: IdeogramV2aTurboInput;
    output: IdeogramV2aTurboOutput;
  };
  "fal-ai/elevenlabs/audio-isolation": {
    input: ElevenlabsAudioIsolationInput;
    output: ElevenlabsAudioIsolationOutput;
  };
  "fal-ai/elevenlabs/tts/turbo-v2.5": {
    input: ElevenlabsTtsTurboV25Input;
    output: ElevenlabsTtsTurboV25Output;
  };
  "fal-ai/elevenlabs/speech-to-text": {
    input: ElevenlabsSpeechToTextInput;
    output: ElevenlabsSpeechToTextOutput;
  };
  "fal-ai/elevenlabs/sound-effects": {
    input: ElevenlabsSoundEffectsInput;
    output: ElevenlabsSoundEffectsOutput;
  };
  "fal-ai/ideogram/v2a": {
    input: IdeogramV2aInput;
    output: IdeogramV2aOutput;
  };
  "fal-ai/evf-sam": {
    input: EvfSamInput;
    output: EvfSamOutput;
  };
  "fal-ai/ddcolor": {
    input: DdcolorInput;
    output: DdcolorOutput;
  };
  "fal-ai/wan-t2v": {
    input: WanT2vInput;
    output: WanT2vOutput;
  };
  "fal-ai/video-prompt-generator": {
    input: VideoPromptGeneratorInput;
    output: VideoPromptGeneratorOutput;
  };
  "fal-ai/sam2/auto-segment": {
    input: Sam2AutoSegmentInput;
    output: Sam2AutoSegmentOutput;
  };
  "fal-ai/minimax/video-01-director/image-to-video": {
    input: MinimaxVideo01DirectorImageToVideoInput;
    output: MinimaxVideo01DirectorImageToVideoOutput;
  };
  "fal-ai/drct-super-resolution": {
    input: DrctSuperResolutionInput;
    output: DrctSuperResolutionOutput;
  };
  "fal-ai/veo2": {
    input: Veo2Input;
    output: Veo2Output;
  };
  "fal-ai/nafnet/deblur": {
    input: NafnetDeblurInput;
    output: NafnetDeblurOutput;
  };
  "fal-ai/nafnet/denoise": {
    input: NafnetDenoiseInput;
    output: NafnetDenoiseOutput;
  };
  "fal-ai/post-processing": {
    input: PostProcessingInput;
    output: PostProcessingOutput;
  };
  "fal-ai/skyreels-i2v": {
    input: SkyreelsI2vInput;
    output: SkyreelsI2vOutput;
  };
  "fal-ai/flowedit": {
    input: FloweditInput;
    output: FloweditOutput;
  };
  "fal-ai/kokoro/hindi": {
    input: KokoroHindiInput;
    output: KokoroHindiOutput;
  };
  "fal-ai/kokoro/mandarin-chinese": {
    input: KokoroMandarinChineseInput;
    output: KokoroMandarinChineseOutput;
  };
  "fal-ai/kokoro/brazilian-portuguese": {
    input: KokoroBrazilianPortugueseInput;
    output: KokoroBrazilianPortugueseOutput;
  };
  "fal-ai/kokoro/spanish": {
    input: KokoroSpanishInput;
    output: KokoroSpanishOutput;
  };
  "fal-ai/kokoro/french": {
    input: KokoroFrenchInput;
    output: KokoroFrenchOutput;
  };
  "fal-ai/kokoro/japanese": {
    input: KokoroJapaneseInput;
    output: KokoroJapaneseOutput;
  };
  "fal-ai/kokoro/british-english": {
    input: KokoroBritishEnglishInput;
    output: KokoroBritishEnglishOutput;
  };
  "fal-ai/kokoro/american-english": {
    input: KokoroAmericanEnglishInput;
    output: KokoroAmericanEnglishOutput;
  };
  "fal-ai/zonos": {
    input: ZonosInput;
    output: ZonosOutput;
  };
  "fal-ai/kokoro/italian": {
    input: KokoroItalianInput;
    output: KokoroItalianOutput;
  };
  "fal-ai/luma-dream-machine/ray-2/image-to-video": {
    input: LumaDreamMachineRay2ImageToVideoInput;
    output: LumaDreamMachineRay2ImageToVideoOutput;
  };
  "fal-ai/got-ocr/v2": {
    input: GotOcrV2Input;
    output: GotOcrV2Output;
  };
  "fal-ai/flux-control-lora-canny/image-to-image": {
    input: FluxControlLoraCannyImageToImageInput;
    output: FluxControlLoraCannyImageToImageOutput;
  };
  "fal-ai/flux-control-lora-depth/image-to-image": {
    input: FluxControlLoraDepthImageToImageInput;
    output: FluxControlLoraDepthImageToImageOutput;
  };
  "fal-ai/ben/v2/image": {
    input: BenV2ImageInput;
    output: BenV2ImageOutput;
  };
  "fal-ai/flux-control-lora-canny": {
    input: FluxControlLoraCannyInput;
    output: FluxControlLoraCannyOutput;
  };
  "fal-ai/flux-control-lora-depth": {
    input: FluxControlLoraDepthInput;
    output: FluxControlLoraDepthOutput;
  };
  "fal-ai/ben/v2/video": {
    input: BenV2VideoInput;
    output: BenV2VideoOutput;
  };
  "fal-ai/minimax/video-01-director": {
    input: MinimaxVideo01DirectorInput;
    output: MinimaxVideo01DirectorOutput;
  };
  "fal-ai/imagen3": {
    input: Imagen3Input;
    output: Imagen3Output;
  };
  "fal-ai/imagen3/fast": {
    input: Imagen3FastInput;
    output: Imagen3FastOutput;
  };
  "fal-ai/ideogram/upscale": {
    input: IdeogramUpscaleInput;
    output: IdeogramUpscaleOutput;
  };
  "fal-ai/hunyuan-video-img2vid-lora": {
    input: HunyuanVideoImg2vidLoraInput;
    output: HunyuanVideoImg2vidLoraOutput;
  };
  "fal-ai/codeformer": {
    input: CodeformerInput;
    output: CodeformerOutput;
  };
  "fal-ai/lumina-image/v2": {
    input: LuminaImageV2Input;
    output: LuminaImageV2Output;
  };
  "fal-ai/hunyuan-video/video-to-video": {
    input: HunyuanVideoVideoToVideoInput;
    output: HunyuanVideoVideoToVideoOutput;
  };
  "fal-ai/hunyuan-video-lora/video-to-video": {
    input: HunyuanVideoLoraVideoToVideoInput;
    output: HunyuanVideoLoraVideoToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/text-to-video": {
    input: PixverseV35TextToVideoInput;
    output: PixverseV35TextToVideoOutput;
  };
  "fal-ai/pixverse/v3.5/image-to-video/fast": {
    input: PixverseV35ImageToVideoFastInput;
    output: PixverseV35ImageToVideoFastOutput;
  };
  "fal-ai/pixverse/v3.5/text-to-video/fast": {
    input: PixverseV35TextToVideoFastInput;
    output: PixverseV35TextToVideoFastOutput;
  };
  "fal-ai/pixverse/v3.5/image-to-video": {
    input: PixverseV35ImageToVideoInput;
    output: PixverseV35ImageToVideoOutput;
  };
  "fal-ai/janus": {
    input: JanusInput;
    output: JanusOutput;
  };
  "fal-ai/yue": {
    input: YueInput;
    output: YueOutput;
  };
  "fal-ai/luma-dream-machine/ray-2": {
    input: LumaDreamMachineRay2Input;
    output: LumaDreamMachineRay2Output;
  };
  "fal-ai/kling/v1-5/kolors-virtual-try-on": {
    input: KlingV15KolorsVirtualTryOnInput;
    output: KlingV15KolorsVirtualTryOnOutput;
  };
  "fal-ai/ffmpeg-api/metadata": {
    input: FfmpegApiMetadataInput;
    output: FfmpegApiMetadataOutput;
  };
  "fal-ai/ffmpeg-api/waveform": {
    input: FfmpegApiWaveformInput;
    output: FfmpegApiWaveformOutput;
  };
  "fal-ai/ffmpeg-api/compose": {
    input: FfmpegApiComposeInput;
    output: FfmpegApiComposeOutput;
  };
  "fal-ai/minimax/video-01-subject-reference": {
    input: MinimaxVideo01SubjectReferenceInput;
    output: MinimaxVideo01SubjectReferenceOutput;
  };
  "fal-ai/moondream-next/batch": {
    input: MoondreamNextBatchInput;
    output: MoondreamNextBatchOutput;
  };
  "fal-ai/flux-pro/v1/canny": {
    input: FluxProV1CannyInput;
    output: FluxProV1CannyOutput;
  };
  "fal-ai/flux-pro/v1/depth": {
    input: FluxProV1DepthInput;
    output: FluxProV1DepthOutput;
  };
  "fal-ai/flux-pro/v1/canny-finetuned": {
    input: FluxProV1CannyFinetunedInput;
    output: FluxProV1CannyFinetunedOutput;
  };
  "fal-ai/flux-lora-canny": {
    input: FluxLoraCannyInput;
    output: FluxLoraCannyOutput;
  };
  "fal-ai/flux-pro-trainer": {
    input: FluxProTrainerInput;
    output: FluxProTrainerOutput;
  };
  "fal-ai/flux-pro/v1.1": {
    input: FluxProV11Input;
    output: FluxProV11Output;
  };
  "fal-ai/flux-pro/v1.1-ultra-finetuned": {
    input: FluxProV11UltraFinetunedInput;
    output: FluxProV11UltraFinetunedOutput;
  };
  "fal-ai/flux-pro/v1/fill-finetuned": {
    input: FluxProV1FillFinetunedInput;
    output: FluxProV1FillFinetunedOutput;
  };
  "fal-ai/flux-pro/v1/depth-finetuned": {
    input: FluxProV1DepthFinetunedInput;
    output: FluxProV1DepthFinetunedOutput;
  };
  "fal-ai/hunyuan-video-lora": {
    input: HunyuanVideoLoraInput;
    output: HunyuanVideoLoraOutput;
  };
  "fal-ai/cogvideox-5b": {
    input: Cogvideox5bInput;
    output: Cogvideox5bOutput;
  };
  "fal-ai/transpixar": {
    input: TranspixarInput;
    output: TranspixarOutput;
  };
  "fal-ai/hunyuan-video-lora-training": {
    input: HunyuanVideoLoraTrainingInput;
    output: HunyuanVideoLoraTrainingOutput;
  };
  "fal-ai/sync-lipsync": {
    input: SyncLipsyncInput;
    output: SyncLipsyncOutput;
  };
  "fal-ai/sa2va/8b/video": {
    input: Sa2va8bVideoInput;
    output: Sa2va8bVideoOutput;
  };
  "fal-ai/sa2va/4b/video": {
    input: Sa2va4bVideoInput;
    output: Sa2va4bVideoOutput;
  };
  "fal-ai/sa2va/4b/image": {
    input: Sa2va4bImageInput;
    output: Sa2va4bImageOutput;
  };
  "fal-ai/sa2va/8b/image": {
    input: Sa2va8bImageInput;
    output: Sa2va8bImageOutput;
  };
  "fal-ai/moondream-next": {
    input: MoondreamNextInput;
    output: MoondreamNextOutput;
  };
  "fal-ai/moondream-next/detection": {
    input: MoondreamNextDetectionInput;
    output: MoondreamNextDetectionOutput;
  };
  "fal-ai/kling-video/v1.6/standard/image-to-video": {
    input: KlingVideoV16StandardImageToVideoInput;
    output: KlingVideoV16StandardImageToVideoOutput;
  };
  "fal-ai/kling-video/v1.6/standard/text-to-video": {
    input: KlingVideoV16StandardTextToVideoInput;
    output: KlingVideoV16StandardTextToVideoOutput;
  };
  "fal-ai/auto-caption": {
    input: AutoCaptionInput;
    output: AutoCaptionOutput;
  };
  "fal-ai/switti": {
    input: SwittiInput;
    output: SwittiOutput;
  };
  "fal-ai/switti/512": {
    input: Switti512Input;
    output: Switti512Output;
  };
  "fal-ai/mmaudio-v2/text-to-audio": {
    input: MmaudioV2TextToAudioInput;
    output: MmaudioV2TextToAudioOutput;
  };
  "fal-ai/sadtalker/reference": {
    input: SadtalkerReferenceInput;
    output: SadtalkerReferenceOutput;
  };
  "fal-ai/dubbing": {
    input: DubbingInput;
    output: DubbingOutput;
  };
  "fal-ai/bria/text-to-image/fast": {
    input: BriaTextToImageFastInput;
    output: BriaTextToImageFastOutput;
  };
  "fal-ai/bria/expand": {
    input: BriaExpandInput;
    output: BriaExpandOutput;
  };
  "fal-ai/bria/text-to-image/base": {
    input: BriaTextToImageBaseInput;
    output: BriaTextToImageBaseOutput;
  };
  "fal-ai/bria/genfill": {
    input: BriaGenfillInput;
    output: BriaGenfillOutput;
  };
  "fal-ai/playai/tts/v3": {
    input: PlayaiTtsV3Input;
    output: PlayaiTtsV3Output;
  };
  "fal-ai/flux-lora-fill": {
    input: FluxLoraFillInput;
    output: FluxLoraFillOutput;
  };
  "fal-ai/bria/background/replace": {
    input: BriaBackgroundReplaceInput;
    output: BriaBackgroundReplaceOutput;
  };
  "fal-ai/bria/eraser": {
    input: BriaEraserInput;
    output: BriaEraserOutput;
  };
  "fal-ai/bria/text-to-image/hd": {
    input: BriaTextToImageHdInput;
    output: BriaTextToImageHdOutput;
  };
  "fal-ai/bria/product-shot": {
    input: BriaProductShotInput;
    output: BriaProductShotOutput;
  };
  "fal-ai/bria/background/remove": {
    input: BriaBackgroundRemoveInput;
    output: BriaBackgroundRemoveOutput;
  };
  "fal-ai/cat-vton": {
    input: CatVtonInput;
    output: CatVtonOutput;
  };
  "fal-ai/leffa/pose-transfer": {
    input: LeffaPoseTransferInput;
    output: LeffaPoseTransferOutput;
  };
  "fal-ai/minimax-music": {
    input: MinimaxMusicInput;
    output: MinimaxMusicOutput;
  };
  "fal-ai/leffa/virtual-tryon": {
    input: LeffaVirtualTryonInput;
    output: LeffaVirtualTryonOutput;
  };
  "fal-ai/recraft-20b": {
    input: Recraft20bInput;
    output: Recraft20bOutput;
  };
  "fal-ai/minimax/video-01-live/image-to-video": {
    input: MinimaxVideo01LiveImageToVideoInput;
    output: MinimaxVideo01LiveImageToVideoOutput;
  };
  "fal-ai/hyper3d/rodin": {
    input: Hyper3dRodinInput;
    output: Hyper3dRodinOutput;
  };
  "fal-ai/minimax/video-01-live": {
    input: MinimaxVideo01LiveInput;
    output: MinimaxVideo01LiveOutput;
  };
  "fal-ai/ideogram/v2/edit": {
    input: IdeogramV2EditInput;
    output: IdeogramV2EditOutput;
  };
  "fal-ai/trellis": {
    input: TrellisInput;
    output: TrellisOutput;
  };
  "fal-ai/luma-dream-machine": {
    input: LumaDreamMachineInput;
    output: LumaDreamMachineOutput;
  };
  "fal-ai/ideogram/v2/turbo/edit": {
    input: IdeogramV2TurboEditInput;
    output: IdeogramV2TurboEditOutput;
  };
  "fal-ai/ideogram/v2/turbo/remix": {
    input: IdeogramV2TurboRemixInput;
    output: IdeogramV2TurboRemixOutput;
  };
  "fal-ai/ideogram/v2/turbo": {
    input: IdeogramV2TurboInput;
    output: IdeogramV2TurboOutput;
  };
  "fal-ai/video-upscaler": {
    input: VideoUpscalerInput;
    output: VideoUpscalerOutput;
  };
  "fal-ai/ideogram/v2/remix": {
    input: IdeogramV2RemixInput;
    output: IdeogramV2RemixOutput;
  };
  "fal-ai/kling-video/v1/standard/text-to-video": {
    input: KlingVideoV1StandardTextToVideoInput;
    output: KlingVideoV1StandardTextToVideoOutput;
  };
  "fal-ai/luma-photon/flash": {
    input: LumaPhotonFlashInput;
    output: LumaPhotonFlashOutput;
  };
  "fal-ai/aura-flow": {
    input: AuraFlowInput;
    output: AuraFlowOutput;
  };
  "fal-ai/omnigen-v1": {
    input: OmnigenV1Input;
    output: OmnigenV1Output;
  };
  "fal-ai/flux/schnell/redux": {
    input: FluxSchnellReduxInput;
    output: FluxSchnellReduxOutput;
  };
};
